import { DUMMY_DSL, DUMMY_CRYSTALIZED } from "./VizDslRenderer";

// ---------------------------------------------------------------------------
// Example 1: Financial posture (full slide) — included in VizDslRenderer as DUMMY
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Example 2: Simple text block only
// ---------------------------------------------------------------------------

const EXAMPLE_2_DSL = {
  version: 1,
  kind: "slide",
  title: "Executive Summary",
  flow: [
    {
      type: "text_block",
      id: "summary",
      label: "Key takeaway",
      slots: [
        { role: "llm_gen", key: "headline", max_words: 12, style: "bold" },
        { role: "llm_gen", key: "body", max_words: 80, style: "italic" }
      ]
    }
  ]
};

const EXAMPLE_2_DATA = {
  summary: {
    headline: "Revenue accelerated while margins held steady through Q4.",
    body: "Diageo reported organic net sales growth of 10.7% for fiscal 2025, driven by premiumisation in tequila and scotch portfolios. Operating margin expanded 40bps to 33.2%, reflecting mix shift and pricing power. Free cash flow reached £2.8B, enabling a £500M share buyback programme announced in January."
  }
};

// ---------------------------------------------------------------------------
// Example 3: Chart-only — sparkline with extract-style data
// ---------------------------------------------------------------------------

const EXAMPLE_3_DSL = {
  version: 1,
  kind: "slide",
  title: "Quarterly revenue trend",
  flow: [
    {
      type: "chart",
      id: "quarterly_revenue",
      kind: "bar",
      proportion: 1,
      data_binding: { limit: 8, label_field: "quarter" },
      series: [
        { key: "revenue", label: "Revenue", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" }
      ],
      y_axis: { gridlines: { dashed: true, steps: [0.25, 0.50, 0.75, 1.0] }}
    }
  ]
};

const EXAMPLE_3_DATA = {
  quarterly_revenue: {
    x_labels: ["Q1 FY24", "Q2 FY24", "Q3 FY24", "Q4 FY24", "Q1 FY25", "Q2 FY25", "Q3 FY25", "Q4 FY25"],
    series: {
      revenue: [4800, 5200, 5600, 5900, 5100, 5500, 5800, 6200]
    }
  }
};

// ---------------------------------------------------------------------------
// Example 4: Conditional — listed vs private
// ---------------------------------------------------------------------------

const EXAMPLE_4_DSL = {
  version: 1,
  kind: "slide",
  title: "Stock & Capital",
  flow: [
    {
      type: "conditional",
      id: "listing_status",
      branches: [
        {
          when: { field: "is_listed", op: "eq", value: true },
          then: {
            type: "flow",
            direction: "vertical",
            children: [
              {
                type: "chart",
                id: "stock_price",
                kind: "bar",
                proportion: 1.5,
                data_binding: { limit_years: 3, label_field: "date" },
                series: [{ key: "adj_close", label: "Adjusted close", color: "#1F3A2E", label_position: "above_bar" }]
              },
              {
                type: "tile_row",
                id: "stock_stats",
                tiles: [
                  { value: { extract: "market_cap",        format: "currency" }, label: { text: "Market cap" } },
                  { value: { extract: "pe_ratio",          format: "num_1dp" },  label: { text: "P/E ratio" } },
                  { value: { extract: "price_change_3yr",  format: "pct_1dp" },  label: { text: "3yr change" } }
                ]
              }
            ]
          }
        },
        {
          when: { field: "is_listed", op: "eq", value: false },
          then: {
            type: "text_block",
            id: "private_notice",
            label: "Private company",
            slots: [
              { role: "literal", value: "Stock data not applicable", style: "italic_serif_center" },
              { role: "literal", value: "Private company. Capital structure summarized via recent recap and credit actions." },
              { role: "llm_gen", key: "actions", max_words: 60 }
            ]
          }
        }
      ]
    }
  ]
};

// Listed version
const EXAMPLE_4_DATA_LISTED = {
  listing_status: {
    is_listed: true,
    stock_price: {
      x_labels: ["Q1 2023", "Q2 2023", "Q3 2023", "Q4 2023", "Q1 2024", "Q2 2024", "Q3 2024"],
      series: { adj_close: [145, 152, 148, 160, 158, 170, 175] }
    },
    stock_stats: {
      market_cap: 12800,
      pe_ratio: 18.4,
      price_change_3yr: 28.5
    }
  }
};

// Private version — switch the condition
const EXAMPLE_4_DATA_PRIVATE = {
  listing_status: {
    is_listed: false,
    private_notice: {
      actions: "Completed $500M recapitalization in Q3 2024. Extended credit facility to 2028. Net debt reduced from 3.2x to 1.8x EBITDA."
    }
  }
};

// ---------------------------------------------------------------------------
// Example 5: Tile row with per-tile null_fallback
// ---------------------------------------------------------------------------

const EXAMPLE_5_DSL = {
  version: 1,
  kind: "slide",
  title: "Key Financial Ratios",
  flow: [
    {
      type: "tile_row",
      id: "ratios",
      null_fallback: { display: "N/A", color: "#999" },
      tiles: [
        { value: { formula: "np / rev * 100",  format: "pct_1dp" },                                                                  label: { text: "Net margin" } },
        { value: { formula: "gp / rev * 100",  format: "pct_1dp" },                                                                  label: { text: "Gross margin" } },
        { value: { formula: "nd / ebitda",     format: "num_1dp", suffix: "x" },                                                     label: { text: "Net debt/EBITDA" } },
        { value: { formula: "ca / cl",         format: "num_1dp", suffix: "x", null_fallback: { display: "—", color: "#999" } },        label: { text: "Current ratio" } },
        { value: { formula: "np / eq * 100",   format: "pct_1dp" },                                                                  label: { text: "Return on equity" } },
        { value: { extract: "fcf",             format: "currency", null_fallback: { display: "Not disclosed", color: "#999" } },        label: { text: "Free cash flow" } }
      ]
    }
  ]
};

const EXAMPLE_5_DATA = {
  ratios: {
    np: 380, rev: 4200, gp: 1776, nd: 684, ebitda: 380,
    ca: null, cl: null, eq: 2100, fcf: 215
  }
};

// ---------------------------------------------------------------------------
// Example 6: Horizontal flow — sidebar + chart
// ---------------------------------------------------------------------------

const EXAMPLE_6_DSL = {
  version: 1,
  kind: "slide",
  title: "Revenue analysis",
  flow: [
    {
      type: "flow",
      direction: "horizontal",
      children: [
        {
          type: "text_block",
          id: "sidebar",
          proportion: 0.35,
          label: "Revenue overview",
          slots: [
            { role: "llm_gen", key: "takeaway", max_words: 10, style: "italic" },
            { role: "llm_gen", key: "detail", max_words: 40 }
          ]
        },
        {
          type: "chart",
          id: "revenue_bars",
          kind: "bar",
          proportion: 0.65,
          data_binding: { limit: 6, label_field: "year" },
          series: [
            { key: "revenue", label: "Revenue", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" }
          ]
        }
      ]
    }
  ]
};

const EXAMPLE_6_DATA = {
  sidebar: {
    takeaway: "Consistent double-digit growth over five fiscal years.",
    detail: "Revenue grew from £12.9B to £20.3B over the period, with the most significant acceleration in FY24-FY25 driven by premium tequila and scotch categories."
  },
  revenue_bars: {
    x_labels: ["FY21", "FY22", "FY23", "FY24", "FY25", "FY26E"],
    series: { revenue: [12900, 15500, 17100, 18700, 20300, 21800] }
  }
};

// ---------------------------------------------------------------------------
// Example 7: Full Slide 2 — Financial Health & Business Activity
// From the supplier intelligence spec
// ---------------------------------------------------------------------------

const EXAMPLE_7_DSL = {
  version: 1,
  kind: "slide",
  title: "Slide 2 · Financial Health & Business Activity",
  flow: [
    {
      type: "flow",
      direction: "horizontal",
      proportion: 0.4,
      children: [
        {
          type: "text_block",
          id: "sidebar_framing",
          proportion: 0.3,
          label: "Financial posture",
          accent_bar: { side: "left", color: "#1F3A2E", width: 3 },
          background: "#F5F1E8",
          slots: [
            { role: "llm_gen", key: "takeaway", max_words: 8, style: "italic" },
            { role: "llm_gen", key: "evidence", max_words: 50, min_words: 35 }
          ]
        },
        {
          type: "chart",
          id: "revenue_chart",
          kind: "bar",
          proportion: 0.7,
          data_binding: { limit: 4, label_field: "fiscal_year", label_format: "FY{value}" },
          series: [
            { key: "revenue", label: "Revenue", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" },
            { key: "net_profit", label: "Net profit",
              color: { op: "cond", branches: [
                { when: { field: "net_profit_yoy", op: "gt", value: 0 }, then: "#7FA690" },
                { when: { field: "net_profit_yoy", op: "lt", value: -0.15 }, then: "#BA7517" }
              ], default: "#7FA690" },
              label_position: "above_bar", label_format: "currency" }
          ],
          y_axis: { gridlines: { dashed: true, steps: [0.25, 0.50, 0.75, 1.0] }}
        }
      ]
    },
    {
      type: "tile_row",
      id: "ratios",
      proportion: 0.08,
      null_fallback: { display: "N/A", color: "#999" },
      background: "#F5F1E8",
      tiles: [
        { value: { formula: "np / rev * 100",  format: "pct_1dp" },                                                                  label: { text: "Net margin" } },
        { value: { formula: "gp / rev * 100",  format: "pct_1dp" },                                                                  label: { text: "Gross margin" } },
        { value: { formula: "nd / ebitda",     format: "num_1dp", suffix: "x" },                                                     label: { text: "Net debt/EBITDA" } },
        { value: { formula: "ca / cl",         format: "num_1dp", suffix: "x", null_fallback: { display: "—", color: "#999" } },        label: { text: "Current ratio" } },
        { value: { formula: "np / eq * 100",   format: "pct_1dp" },                                                                  label: { text: "Return on equity" } },
        { value: { extract: "fcf",             format: "currency", null_fallback: { display: "Not disclosed", color: "#999" } },        label: { text: "Free cash flow" } }
      ]
    },
    {
      type: "flow",
      direction: "horizontal",
      proportion: 0.52,
      children: [
        {
          type: "conditional",
          id: "stock_or_private",
          proportion: 0.5,
          branches: [
            {
              when: { field: "is_listed", op: "eq", value: true },
              then: {
                type: "flow",
                direction: "vertical",
                children: [
                  {
                    type: "chart",
                    id: "stock_price_chart",
                    proportion: 3,
                    kind: "line",
                    data_binding: { limit_years: 3, label_field: "date" },
                    series: [
                      { key: "adj_close", label: "Price", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" },
                      { key: "volume", label: "Volume", color: "#E8792B", dash: [8, 4], label_position: "above_bar", label_format: "num_1dp" }
                    ],
                    last_point: { dot: true,
                      dot_color: { op: "cond", branches: [
                        { when: { field: "adj_close_change", op: "lt", value: 0 }, then: "#BA7517" }
                      ], default: "#2D8659" }}
                  },
                  {
                    type: "tile_row",
                    id: "stock_callouts",
                    proportion: 1,
                    tiles: [
                      { value: { extract: "price_change_3yr", format: "pct_1dp" }, label: { text: "3-year change" } },
                      { value: { extract: "market_cap",       format: "currency" }, label: { text: "Market cap" } },
                      { value: { extract: "pe_ratio",         format: "num_1dp" },  label: { text: "P/E (ttm)" } }
                    ]
                  }
                ]
              }
            },
            {
              when: { field: "is_listed", op: "eq", value: false },
              then: {
                type: "text_block",
                id: "private_capital",
                slots: [
                  { role: "literal", value: "Stock data not applicable", style: "italic_serif_center" },
                  { role: "literal", value: "Private company. Capital structure summarized via recent recap and credit actions.", style: "small" },
                  { role: "llm_gen", key: "capital_actions", max_words: 60 }
                ]
              }
            }
          ]
        },
        {
          type: "bullet_list",
          id: "business_activity",
          proportion: 0.5,
          tag_styles: {
            "LEAD":  { bg: "#DCE7DF", text: "#1F3A2E" },
            "M&A":   { bg: "#EFE0D8", text: "#8A4A2A" },
            "CAP":   { bg: "#EFE0D8", text: "#8A4A2A" },
            "OPS":   { bg: "#D8E2EF", text: "#2C4A6E" },
            "STRAT": { bg: "#E8E2D0", text: "#5A6F62" }
          }
        }
      ]
    }
  ]
};

const EXAMPLE_7_DATA = {
  sidebar_framing: {
    takeaway: "Strong franchise, weakening near-term performance.",
    evidence: "Organic net sales grew 10.7% to £20.3B in FY25, driven by super-premium tequila (+24% YoY) and scotch (+12%). However, gross margin compressed 180bps to 42.3% on input cost inflation and mix shift toward lower-margin RTD categories. Operating profit of £6.1B included a £390M one-off gain from the EABL divestiture — underlying operating profit growth was 5.2%."
  },
  revenue_chart: {
    x_labels: ["FY22", "FY23", "FY24", "FY25"],
    series: {
      revenue: [15500, 17100, 18700, 20300],
      net_profit: [3200, 3800, 4100, 4500]
    },
    net_profit_yoy: [0.12, 0.19, 0.08, 0.10]
  },
  ratios: {
    np: 4500, rev: 20300, gp: 8580, nd: 8200,
    ebitda: 7200, ca: 9800, cl: 7100, eq: 14200, fcf: 2800000000
  },
  stock_or_private: {
    is_listed: true,
    stock_price_chart: {
      x_labels: ["May 23", "Sep 23", "Jan 24", "May 24", "Sep 24", "Jan 25"],
      series: {
        adj_close: [2850, 3020, 2940, 3180, 3350, 3520],
        volume: [1800, 1650, 2200, 2050, 1700, 2450]
      },
      adj_close_change: 0.235
    },
    stock_callouts: {
      price_change_3yr: 28.5,
      market_cap: 12800000000,
      pe_ratio: 18.4
    }
  },
  business_activity: {
    items: [
      { tag: "LEAD", headline: "Jane Smith appointed CFO, January 2025",
        evidence: "Former Pfizer SVP Finance replaces Mark Jones after 12-year tenure. Announced group-wide opex review targeting £200M annual savings by FY27." },
      { tag: "CAP", headline: "£500M equity placement at £52/share",
        evidence: "Q3 2024 institutional placement oversubscribed 2.4x. Proceeds retired 2026 senior notes, reducing annual interest cost by £18M and leverage to 1.8x." },
      { tag: "M&A", headline: "EABL 15% stake divested for £820M",
        evidence: "Completed sale of East African Breweries stake to strategic partner Heineken. Net proceeds allocated to debt reduction; transaction closed 28 Feb 2025." },
      { tag: "OPS", headline: "Jalisco distillery reaches full capacity",
        evidence: "£145M tequila facility commissioned Q4 2024. Adds 2.3M cases annual output. 85% renewable energy powered; water recycling rate of 92% achieved in first quarter." },
      { tag: "STRAT", headline: "Premium+ portfolio now 38% of net sales",
        evidence: "Super-premium tequila grew 24% YoY; single malt scotch up 18%. £420M in bolt-on acquisitions closed: Don Fulano (tequila) and Loch Lomond minority stake." }
    ]
  }
};

// ---------------------------------------------------------------------------
// Example 8: Full Slide 1 — At-a-glance dashboard
// 3-band layout: header / synthesis / evidence, three consistent columns
// ---------------------------------------------------------------------------

const EXAMPLE_8_DSL = {
  version: 1, kind: "slide",
  chrome: {
    header: {
      style: "dark_green_band",
      label: "SUPPLIER INTELLIGENCE REPORT",
      supplier_name: { role: "llm_gen", key: "supplier_name" },
      chips: [
        { role: "llm_gen", key: "header_sector",      label: "Sector" },
        { role: "llm_gen", key: "header_hq_listing",  label: "HQ · listing" },
        { role: "llm_gen", key: "header_ownership",   label: "Ownership" },
        { role: "llm_gen", key: "header_report_date", label: "Report" }
      ]
    },
    footer: {
      style: "dark_green_band",
      left: "Sense Platform · AI-assisted research · Supplement to, not sole basis for, decision-making · Gaps marked N/A",
      right: "Diageo Supplier Intelligence · May 2025"
    }
  },
  bands: [
    { id: "synthesis", proportion: 0.4, columns: [
      { id: "signals_col", proportion: 0.22, header: "Key signals", blocks: [
        { type: "bullet_list", id: "key_signals", item_style: "dot_qualifier",
          items: [
            { label: { role: "literal", value: "Financial stability" }, status: { role: "llm_gen", key: "sig_fin" }, qualifier: { role: "llm_gen", cite: true, key: "sig_fin_q", max_words: 5 } },
            { label: { role: "literal", value: "Recent material change" }, status: { role: "llm_gen", key: "sig_change" }, qualifier: { role: "llm_gen", cite: true, key: "sig_change_q", max_words: 5 } },
            { label: { role: "literal", value: "SBTi status" }, status: { role: "llm_gen", key: "sig_sbti" }, qualifier: { role: "llm_gen", cite: true, key: "sig_sbti_q", max_words: 5 } },
            { label: { role: "literal", value: "Disclosure depth" }, status: { role: "llm_gen", key: "sig_disclosure" }, qualifier: { role: "llm_gen", cite: true, key: "sig_disclosure_q", max_words: 5 } },
            { label: { role: "literal", value: "Recent ESG activity" }, status: { role: "llm_gen", key: "sig_esg" }, qualifier: { role: "llm_gen", cite: true, key: "sig_esg_q", max_words: 5 } },
            { label: { role: "literal", value: "Operational change" }, status: { role: "llm_gen", key: "sig_ops" }, qualifier: { role: "llm_gen", cite: true, key: "sig_ops_q", max_words: 5 } }
          ],
          dot_color_map: { green: "#2D8659", amber: "#BA7517", red: "#C4463A" }
        }
      ]},
      { id: "observations_col", proportion: 0.44, header: "Headline observations", blocks: [
        { type: "text_block", id: "headline_observations",
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: [
            { role: "llm_gen", key: "obs_fin", max_words: 35, style: "italic_serif" },
            { role: "llm_gen", key: "obs_esg", max_words: 35, style: "italic_serif" },
            { role: "llm_gen", key: "obs_ops", max_words: 35, style: "italic_serif" }
          ]
        }
      ]},
      { id: "progress_col", proportion: 0.34, header: "Sustainability progress", pins: ["sustainability_overview", "sustainability_details"], blocks: [
        { type: "flow", id: "progress_grid", direction: "vertical", gap: "spread", children: [
          { type: "chart", kind: "progress", id: "prog_1", bar_height: 5, label: "Renewable electricity", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }},
          { type: "chart", kind: "progress", id: "prog_2", bar_height: 5, label: "Scope 1+2 reduction", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }},
          { type: "chart", kind: "progress", id: "prog_3", bar_height: 5, label: "Scope 3 target progress", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }},
          { type: "chart", kind: "progress", id: "prog_4", bar_height: 5, label: "Recycled / circular content", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }},
          { type: "chart", kind: "progress", id: "prog_5", bar_height: 5, label: "Water efficiency", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }},
          { type: "chart", kind: "progress", id: "prog_6", bar_height: 5, label: "Waste to landfill reduction", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }}
        ]}
      ]}
    ]},
    { id: "evidence", proportion: 0.6, columns: [
      { id: "snapshot_col", proportion: 0.22, header: "Company snapshot", pins: ["supplier"], blocks: [
        { type: "tile_row", id: "company_snapshot", cols: 2,
          null_fallback: { display: "N/A", color: "#999" },
          resetGroups: {
            tile_value: { fit: "width" },
            tile_label: { fit: "width" }
          },
          tiles: [
            { value: { extract: "sites",      scaleGroup: "tile_value" }, label: { text: "Manufacturing sites",   scaleGroup: "tile_label" } },
            { value: { extract: "countries",  scaleGroup: "tile_value" }, label: { text: "Countries of operation", scaleGroup: "tile_label" } },
            { value: { extract: "ghg",        scaleGroup: "tile_value" }, label: { text: "Scope 1+2 CO₂e FY25",    scaleGroup: "tile_label" } },
            { value: { extract: "net_zero",   scaleGroup: "tile_value" }, label: { text: "Net-zero target",         scaleGroup: "tile_label" } },
            { value: { extract: "cdp",        scaleGroup: "tile_value" }, label: { text: "CDP Water · 7 yrs",       scaleGroup: "tile_label" } },
            { value: { extract: "esg_report", scaleGroup: "tile_value" }, label: { text: "Latest ESG report",       scaleGroup: "tile_label" } }
          ]
        }
      ]},
      { id: "financial_col", proportion: 0.44, header: "Financial activity", pins: ["financials"], blocks: [
        { type: "conditional", id: "financial_activity", branches: [
          { when: { field: "is_listed", op: "eq", value: true }, then: {
            type: "flow", direction: "vertical", children: [
              { type: "chart", id: "revenue_chart", kind: "bar", proportion: 1.5,
                data_binding: { limit: 4, label_field: "fiscal_year", label_format: "FY{value}" },
                series: [
                  { key: "revenue", label: "Revenue", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" },
                  { key: "net_profit", label: "Net profit", label_position: "above_bar", label_format: "currency",
                    color: { op: "cond", branches: [
                      { when: { field: "net_profit_yoy", op: "lt", value: -0.15 }, then: "#BA7517" }
                    ], default: "#7FA690" }}
                ],
                y_axis: { gridlines: { dashed: true, steps: [0.25, 0.50, 0.75, 1.0] }}
              },
              { type: "section_header", text: "Key ratios · latest FY", proportion: 0.15 },
              { type: "tile_row", id: "ratio_callout", data_pin: "financials", proportion: 0.6, null_fallback: { display: "N/A", color: "#999" },
                tiles: [
                  { value: { formula: "np / rev * 100",  format: "pct_1dp" },               label: { text: "Net margin" } },
                  { value: { formula: "nd / ebitda",     format: "num_1dp", suffix: "x" },  label: { text: "Net debt/EBITDA" } },
                  { value: { formula: "np / eq * 100",   format: "pct_1dp" },               label: { text: "Return on equity" } }
                ]
              }
            ]
          }},
          { when: { field: "is_listed", op: "eq", value: false }, then: {
            type: "text_block", id: "private_financials",
            slots: [
              { role: "literal", value: "Private · financials not disclosed", style: "italic_serif_center" },
              { role: "llm_gen", key: "last_known", max_words: 15 },
              { role: "llm_gen", key: "capital_actions", max_words: 50 }
            ]
          }}
        ]}
      ]},
      { id: "events_col", proportion: 0.34, header: "Recent events · past 12 months", pins: ["supplier"], blocks: [
        { type: "bullet_list", id: "recent_events", item_limit: 5,
          tag_styles: {
            BIZ: { bg: "#DCE7DF", text: "#1F3A2E" },
            FIN: { bg: "#EFE0D8", text: "#8A4A2A" },
            ESG: { bg: "#D8E2EF", text: "#2C4A6E" },
            OPS: { bg: "#E8E2D0", text: "#5A6F62" }
          }
        }
      ]}
    ]}
  ]
};

const EXAMPLE_8_DATA = {
  supplier_name: "Diageo",
  header_chips: {
    header_sector: "Spirits & beverage",
    header_hq_listing: "London, UK · LSE: DGE",
    header_ownership: "Public",
    header_report_date: "22 May 2025"
  },
  key_signals: {
    items: [
      { label: "Financial stability", status: "green", qualifier: "Profitable · strong cash flow" },
      { label: "Recent material change", status: "amber", qualifier: "New CFO · EABL sale" },
      { label: "SBTi status", status: "green", qualifier: "Validated · near-term + net-zero" },
      { label: "Disclosure depth", status: "green", qualifier: "CDP A-List · full S1-3" },
      { label: "Recent ESG activity", status: "green", qualifier: "Jalisco renewable · Mar 2025" },
      { label: "Operational change", status: "amber", qualifier: "Mexico expansion · Q4 2024" }
    ]
  },
  headline_observations: {
    obs_fin: "**Strategic reset underway.** The appointment of Jane Smith as CFO in January and the £820M EABL divestiture signal a portfolio reshaping. Organic net sales grew 10.7% to £20.3B, though gross margins compressed 180bps on input cost inflation.",
    obs_esg: "**Sustainability leadership maintained.** Diageo holds validated SBTi near-term and net-zero targets, CDP A-List status for water, and achieved 68% renewable electricity. The Jalisco distillery reached 92% water recycling in its first quarter of operation.",
    obs_ops: "**Footprint expanding selectively.** The new £145M Jalisco facility adds 2.3M cases capacity while 31 Scotch distilleries anchor the legacy base. Mexico and India represent the primary growth geographies for the coming fiscal year."
  },
  prog_1: { value: 68, target: 100, display_value: "68% / 100%" },
  prog_2: { value: 42, target: 50,  display_value: "42% / 50%" },
  prog_3: { value: 18, display_value: "Set · −25% by 2030" },
  prog_4: { value: 55, target: 50,  display_value: "55% / 50%" },
  prog_5: { value: 34, display_value: "+34% in stressed basins" },
  prog_6: { value: 35.5, display_value: "−35.5%" },
  company_snapshot: {
    sites: 132, countries: 34, ghg: "1.8 Mt CO₂e",
    net_zero: 2050, cdp: "A-List · 7 yrs", esg_report: "Sep 2024"
  },
  financial_activity: {
    is_listed: true,
    revenue_chart: {
      x_labels: ["FY22", "FY23", "FY24", "FY25"],
      series: { revenue: [15500, 17100, 18700, 20300], net_profit: [3200, 3800, 4100, 4500] },
      net_profit_yoy: [0.12, 0.19, 0.08, 0.10]
    },
    ratio_callout: { np: 4500, rev: 20300, nd: 8200, ebitda: 7200, eq: 14200 }
  },
  recent_events: {
    items: [
      { tag: "BIZ", date: "Jan 2025", headline: "Jane Smith appointed CFO", evidence: "Former Pfizer SVP Finance replaces Mark Jones. Opex review targets £200M savings." },
      { tag: "FIN", date: "Sep 2024", headline: "£500M equity placement", evidence: "Institutional placement oversubscribed 2.4x. Proceeds retired 2026 senior notes." },
      { tag: "ESG", date: "Mar 2025", headline: "Jalisco 92% water recycling achieved", evidence: "New £145M tequila facility reaches full capacity, powered 85% by renewables." },
      { tag: "OPS", date: "Nov 2024", headline: "Jalisco distillery commissioned", evidence: "Adds 2.3M cases annual output. 200+ jobs created in Jalisco region." },
      { tag: "FIN", date: "Feb 2025", headline: "EABL 15% stake sold for £820M", evidence: "Completed sale to Heineken. Net proceeds allocated to debt reduction." }
    ]
  }
};

// ---------------------------------------------------------------------------
// Example 9: World map with footprint shading and city dots
// ---------------------------------------------------------------------------

const EXAMPLE_9_DSL = {
  version: 1, kind: "slide",
  title: "Manufacturing footprint",
  flow: [
    { type: "text_block", id: "map_title", proportion: 0.08,
      slots: [{ role: "llm_gen", key: "title", max_words: 10, style: "bold" }]
    },
    { type: "flow", direction: "horizontal", proportion: 0.92, children: [
      { type: "map", id: "world_map", proportion: 0.7,
        shading: { active: "#1F3A2E", inactive: "#E5E0D2" }
      },
      { type: "text_block", id: "map_legend", proportion: 0.3,
        slots: [
          { role: "llm_gen", key: "description", max_words: 50 },
          { role: "llm_gen", key: "sites_summary", max_words: 80 }
        ]
      }
    ]}
  ]
};

const EXAMPLE_9_DATA = {
  map_title: { title: "Global manufacturing presence" },
  world_map: {
    active_countries: ["united kingdom", "united states of america", "mexico", "brazil",
      "nigeria", "kenya", "south africa", "india", "singapore", "australia",
      "ireland", "france", "germany", "netherlands"],
    cities: [
      { name: "london", label: "London HQ", color: "#2D8659" },
      { name: "edinburgh", label: "Edinburgh" },
      { name: "glasgow", label: "Glasgow" },
      { name: "dublin", label: "Dublin" },
      { name: "mexico city", label: "Jalisco" },
      { name: "sao paulo", label: "São Paulo" },
      { name: "nairobi", label: "Nairobi" },
      { name: "lagos", label: "Lagos" },
      { name: "mumbai", label: "Mumbai" },
      { name: "singapore", label: "Singapore" },
      { name: "sydney", label: "Sydney" }
    ],
    country_count: 14,
    site_count: 132
  },
  map_legend: {
    description: "Diageo operates 132 manufacturing sites across 34 countries, with production concentrated in Scotland, Ireland, Mexico, and emerging markets.",
    sites_summary: "Key production hubs: Scotland (31 distilleries), Ireland (St. James's Gate), Mexico (Jalisco), East Africa (Nairobi, Lagos). Sustainability investments are concentrated at Jalisco (85% renewable, 92% water recycling) and Scottish distilleries (biomass transition programme)."
  }
};

// ---------------------------------------------------------------------------
// Example 10: text sizing test — three observation columns, varying min_font_scale
// All three columns receive the same observation cards (same data). Only the
// text_block's min_font_scale changes, so you can compare the shrink behaviour
// side-by-side. The middle card in each column has been padded ~50% longer so
// overflow is more dramatic.
// ---------------------------------------------------------------------------

const OBS_SHARED = {
  obs_fin: "**Strategic reset underway.** The appointment of Jane Smith as CFO in January and the £820M EABL divestiture signal a portfolio reshaping. Organic net sales grew 10.7% to £20.3B, though gross margins compressed 180bps on input cost inflation.",
  obs_esg: "**Sustainability leadership maintained.** Diageo holds validated SBTi near-term and net-zero targets, CDP A-List status for water, and achieved 68% renewable electricity. The Jalisco distillery reached 92% water recycling in its first quarter of operation, with plans to extend the closed-loop water system to all Mexico facilities by FY27 and replicate the model across the Scotland and Caribbean estates alongside a Scope 3 supplier engagement programme.",
  obs_ops: "**Footprint expanding selectively.** The new £145M Jalisco facility adds 2.3M cases capacity while 31 Scotch distilleries anchor the legacy base. Mexico and India represent the primary growth geographies for the coming fiscal year."
};

const OBS_SLOTS = [
  { role: "llm_gen", key: "obs_fin", style: "italic_serif" },
  { role: "llm_gen", key: "obs_esg", style: "italic_serif" },
  { role: "llm_gen", key: "obs_ops", style: "italic_serif" }
];

const EXAMPLE_10_DSL = {
  version: 1, kind: "slide",
  title: "Text sizing test",
  chrome: {
    header: {
      style: "dark_green_band",
      label: "TEXT SIZING TEST",
      supplier_name: { role: "literal", value: "Observations · min_font_scale" },
      chips: []
    },
    footer: {
      style: "dark_green_band",
      left: "Same content in every column · only min_font_scale differs",
      right: "Text sizing test"
    }
  },
  bands: [
    { id: "test", proportion: 0.4, columns: [
      { id: "col_default", proportion: 0.34, header: "Default · shrink to fit", blocks: [
        { type: "text_block", id: "obs_default",
          min_font_scale: 0,
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: OBS_SLOTS
        }
      ]},
      { id: "col_no_shrink", proportion: 0.33, header: "No shrink (min = 1.0)", blocks: [
        { type: "text_block", id: "obs_no_shrink",
          min_font_scale: 1.0,
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: OBS_SLOTS
        }
      ]},
      { id: "col_large_min", proportion: 0.33, header: "Shrink, large min (0.8)", blocks: [
        { type: "text_block", id: "obs_large_min",
          min_font_scale: 0.8,
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: OBS_SLOTS
        }
      ]}
    ]},
    // Spacer band — single full-width column, intentionally empty.
    // Claims 60% of the slide so the test band above must overflow and
    // exercise min_font_scale on each test column's text_block.
    { id: "spacer", proportion: 0.6, background: "#EBE5D6", columns: [
      { id: "spacer_col", proportion: 1, blocks: [] }
    ]}
  ]
};

const EXAMPLE_10_DATA = {
  obs_default:   OBS_SHARED,
  obs_no_shrink: OBS_SHARED,
  obs_large_min: OBS_SHARED
};

// ---------------------------------------------------------------------------
// Example 11: Ardagh Group — source material for LLM crystallization
// ---------------------------------------------------------------------------

const EXAMPLE_11_DSL = {
  version: 1, kind: "slide",
  pins: [
    { name: "supplier",                type: "entity",     label: "Supplier",                          required: true },
    { name: "financials",              type: "financial",  label: "Financial data",                    required: false },
    { name: "sustainability_overview", type: "any",        label: "Sustainability report overview",    required: false },
    { name: "sustainability_details",  type: "any",        label: "Sustainability deep-dive details",  required: false },
    { name: "facilities",              type: "any",        label: "Manufacturing facilities",          required: false },
    { name: "risk",                   type: "any",        label: "Risk assessment data",              required: false }
  ],
  chrome: {
    header: {
      style: "dark_green_band",
      label: "SUPPLIER INTELLIGENCE REPORT",
      supplier_name: { role: "llm_gen", key: "supplier_name", pins: ["supplier"], prompt: "Company name as it appears in the source." },
      chips: [
        { role: "llm_gen", key: "header_sector",      label: "Sector",       pins: ["supplier"], prompt: "2-3 word industry category (e.g. 'Glass packaging')." },
        { role: "llm_gen", key: "header_hq_listing",  label: "HQ · listing", pins: ["supplier"], prompt: "HQ city, country. If publicly listed include 'exchange:ticker'; otherwise 'Private'." },
        { role: "llm_gen", key: "header_ownership",   label: "Ownership",    pins: ["supplier"], prompt: "Most specific recent ownership status: Public, Private, PE-backed, State-owned, Subsidiary — or 'Recapitalized' if a recent debt-for-equity / creditor takeover occurred." },
        { role: "today",   key: "header_report_date", label: "Report",       format: "D Mon YYYY" }
      ]
    },
    footer: {
      style: "dark_green_band",
      left: "Sense Platform · AI-assisted research · Supplement to, not sole basis for, decision-making · Gaps marked N/A",
      right: "Diageo Supplier Intelligence · May 2026"
    }
  },
  bands: [
    { id: "synthesis", proportion: 0.4, columns: [
      { id: "signals_col", proportion: 0.22, header: "Key signals", blocks: [
        { type: "bullet_list", id: "key_signals", item_style: "dot_qualifier",
          status_legend: {
            green: "positive / on-track / low concern",
            amber: "caution / mixed / recovering from past trouble",
            red: "active, unresolved distress or clearly negative / high concern"
          },
          items: [
            { label: { role: "literal", value: "Financial stability" },
              status:    { role: "llm_gen", key: "sig_fin",      pins: ["supplier", "financials"], prompt: "Rate the CURRENT state from the most recent data. red = active/unresolved distress (ongoing or worsening losses, looming maturities, covenant breach, default, distressed recap that wiped shareholders). amber = recently distressed but stabilising (completed recap, debt cut, ratings upgraded) OR high leverage with positive momentum. green = consistently profitable with healthy coverage." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_fin_q",    max_words: 6, pins: ["supplier", "financials"], prompt: "≤6-word qualifier that justifies the COLOUR. For amber, convey the caution/recovery (e.g. 'Stabilising post-recap', 'Recovering after losses') — not just the positive headline. For red, name the distress (e.g. 'Worsening net losses'). Append year to any old figure." } },
            { label: { role: "literal", value: "Recent material change" },
              status:    { role: "llm_gen", key: "sig_change",   pins: ["supplier", "financials"], prompt: "Judge the NATURE of the change. green = clearly value-accretive (healthy M&A, growth equity). amber = a recapitalization / change-of-control / refinancing — even if the outcome is positive, it signals the company needed rescuing. red = distressed restructuring / debt-for-equity that wipes out existing shareholders, default, or ratings downgrade." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_change_q", max_words: 5, pins: ["supplier", "financials"], prompt: "Name the change in ≤5 words (e.g. 'Debt-for-equity recap Nov '25')." } },
            { label: { role: "literal", value: "SBTi status" },
              status:    { role: "llm_gen", key: "sig_sbti",     pins: ["sustainability_overview", "sustainability_details"], prompt: "green = SBTi-validated targets; amber = committed but unvalidated; red = none disclosed." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_sbti_q",   max_words: 5, pins: ["sustainability_overview", "sustainability_details"], prompt: "State target year and scope (e.g. '42% Scope 1+2 by 2030')." } },
            { label: { role: "literal", value: "Disclosure depth" },
              status:    { role: "llm_gen", key: "sig_disclosure",   pins: ["sustainability_overview", "sustainability_details"], prompt: "green = comprehensive (report + CDP + Scope 3 + net-zero target); amber = partial (e.g. report exists but Scope 3 or net-zero target is missing); red = little/none." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_disclosure_q", max_words: 5, pins: ["sustainability_overview", "sustainability_details"], prompt: "Note depth and gaps (e.g. 'Partial · no S3, no net-zero')." } },
            { label: { role: "literal", value: "Recent ESG activity" },
              status:    { role: "llm_gen", key: "sig_esg",   pins: ["sustainability_overview", "sustainability_details"], prompt: "green = multiple substantive initiatives in past 12 months; amber = limited; red = none." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_esg_q", max_words: 5, pins: ["sustainability_overview", "sustainability_details"], prompt: "Headline ESG action (e.g. 'Solar plant, recycling JV')." } },
            { label: { role: "literal", value: "Operational change" },
              status:    { role: "llm_gen", key: "sig_ops",   pins: ["supplier"], prompt: "green = expansion / new products; amber = mixed (closures alongside launches); red = significant downsizing." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_ops_q", max_words: 5, pins: ["supplier"], prompt: "Headline operational change (e.g. 'Plant closures, new bottle lines')." } },
            { label: { role: "literal", value: "Credit / default risk" },
              status:    { role: "llm_gen", key: "sig_credit",   pins: ["risk"], prompt: "Assess creditworthiness from risk data. green = investment-grade or strong score; amber = elevated risk or recent downgrade; red = distressed or near-default. Use the providerScore, rating, and riskDescription fields." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_credit_q", max_words: 6, pins: ["risk"], prompt: "≤6-word qualifier: rating + score + key metric (e.g. 'B rating, score 64/100')." } },
            { label: { role: "literal", value: "Regulatory / compliance risk" },
              status:    { role: "llm_gen", key: "sig_reg",   pins: ["risk"], prompt: "Assess regulatory exposure from risk data. green = no CCJs, profit warnings, or enforcement actions; amber = some CCJs or past profit warnings; red = significant outstanding CCJs, active enforcement, or multiple profit warnings." },
              qualifier: { role: "llm_gen", cite: true, key: "sig_reg_q", max_words: 6, pins: ["risk"], prompt: "≤6-word label: cite CCJ count, profit warnings, or 'No issues' (e.g. 'No CCJs, 0 profit warnings')." } }
          ],
          dot_color_map: { green: "#2D8659", amber: "#BA7517", red: "#C4463A" }
        }
      ]},
      { id: "observations_col", proportion: 0.44, header: "Headline observations", blocks: [
        { type: "text_block", id: "headline_observations",
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: [
            { role: "llm_gen", key: "obs_fin", max_words: 45, cite: true, pins: ["supplier", "financials"], prompt: "Start with a 3-5 word bold markdown lead-in, then assess financial health led by the MOST RECENT material development (recap, refinancing, ratings action, current leverage). Cite figures, but tag any non-current ones with their year (e.g. '2004 net loss $86M'). Don't call it 'strong' while loss-making." },
            { role: "llm_gen", key: "obs_esg", max_words: 45, cite: true, pins: ["sustainability_overview", "sustainability_details"], prompt: "Start with a 3-5 word bold markdown lead-in (e.g. '**Climate progress without full SBTi coverage.**'), then assess ESG posture: disclosures, SBTi scope/gaps, renewables, recent initiatives." },
            { role: "llm_gen", key: "obs_ops", max_words: 45, cite: true, pins: ["supplier"], prompt: "Start with a 3-5 word bold markdown lead-in (e.g. '**Aggressive operational reset.**'), then summarise operational change: M&A, plant closures/openings, product launches, leadership." }
          ]
        }
      ]},
      { id: "progress_col", proportion: 0.34, header: "Sustainability progress", pins: ["sustainability_overview", "sustainability_details"], blocks: [
        // Superset of candidate metrics. The flow renders only the populated
        // ones (data-first), capping at 6 — so each report shows its own KPIs,
        // padding with N/A only when fewer than 6 are disclosed.
        { type: "flow", id: "progress_grid", direction: "vertical", max_visible: 6, children: [
          { type: "chart", kind: "progress", id: "prog_renewable", bar_height: 5, label: "Renewable electricity", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "renewable_pct", prompt: "% renewable electricity currently achieved. 0-100 or null." },
            display_value: { role: "llm_gen", key: "renewable_display", prompt: "Short label ≤6 words, e.g. '25% renewable' or '30% global · 46% Europe'. null if not disclosed." } },
          { type: "chart", kind: "progress", id: "prog_ghg", bar_height: 5, label: "GHG emissions reduction", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "ghg_reduction_pct", prompt: "% TOTAL GHG emissions reduction ACHIEVED (absolute, year-over-year), e.g. 19.6. null if not disclosed." },
            display_value: { role: "llm_gen", key: "ghg_reduction_display", prompt: "Short label ≤6 words, e.g. '−19.6% total GHG'. null if not disclosed." } },
          { type: "chart", kind: "progress", id: "prog_scope12", bar_height: 5, label: "Scope 1+2 target", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "s12_reduction", prompt: "% Scope 1+2 reduction achieved toward target, or null if only a target (no current progress) is disclosed." },
            display_value: { role: "llm_gen", key: "s12_display", prompt: "Short label ≤6 words. If only a target exists: 'Target −42% by 2030'. null if no target." } },
          { type: "chart", kind: "progress", id: "prog_recycled", bar_height: 5, label: "Recycled / circular content", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "recycled_pct", prompt: "Any numeric circularity metric: recycled/cullet content %, OR a recycling-rate change (e.g. 19 for '+19% recycling'). null only if there's no number at all." },
            display_value: { role: "llm_gen", key: "recycled_display", prompt: "Short label ≤6 words for the circularity story, e.g. '+19% recycling YoY', '30% recycled', or 'Cullet via SVA + CAP Glass'. null if none." } },
          { type: "chart", kind: "progress", id: "prog_water", bar_height: 5, label: "Water efficiency", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "water_efficiency", prompt: "% water intensity/withdrawal improvement achieved, e.g. 8.7. null if only a target or not disclosed." },
            display_value: { role: "llm_gen", key: "water_display", prompt: "Short label ≤6 words, e.g. '−8.7% withdrawal' or 'Target −26% by 2030'. null if none." } },
          { type: "chart", kind: "progress", id: "prog_waste", bar_height: 5, label: "Waste reduction", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "waste_pct", prompt: "% waste reduction achieved OR % of facilities at zero waste to landfill, e.g. 9 or 83. null if not disclosed." },
            display_value: { role: "llm_gen", key: "waste_display", prompt: "Short label ≤6 words, e.g. '−9% waste' or '83% zero-WtL'. null if none." } },
          { type: "chart", kind: "progress", id: "prog_intensity", bar_height: 5, label: "Emissions intensity", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "intensity_pct", prompt: "% improvement in EMISSIONS INTENSITY (per unit output), e.g. 26.9. null if not disclosed." },
            display_value: { role: "llm_gen", key: "intensity_display", prompt: "Short label ≤6 words, e.g. '−26.9% intensity'. null if not disclosed." } },
          { type: "chart", kind: "progress", id: "prog_scope3", bar_height: 5, label: "Scope 3 progress", color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
            value: { role: "llm_gen", key: "s3_progress", prompt: "% Scope 3 progress, or null if no Scope 3 target." },
            display_value: { role: "llm_gen", key: "s3_display", prompt: "Short label ≤6 words, e.g. 'Inventory complete' or 'Target −X% by YYYY'. null if no Scope 3 work." } }
        ]}
      ]}
    ]},
    { id: "evidence", proportion: 0.6, columns: [
      { id: "snapshot_col", proportion: 0.22, header: "Company snapshot", blocks: [
        { type: "tile_row", id: "company_snapshot", cols: 2, null_fallback: { display: "N/A", color: "#999" },
          tiles: [
            { value: { extract: "sites",      cite: true, prompt: "Total number of individual manufacturing facilities. Sum across all rows, expanding aggregate counts where stated (e.g. a row that says '31 distilleries' counts as 31, not 1). A row mentioning a single named site counts as 1. If exact totals are unclear, append '+' to indicate a lower bound (e.g. '50+'). Integer or null." }, label: { text: "Manufacturing sites" },      pins: ["facilities"] },
            { value: { extract: "countries",  cite: true, prompt: "Number of distinct countries the facilities span. Count each country once even if it appears on multiple rows. If exact totals are unclear, append '+' (e.g. '15+'). Integer or null." },                label: { text: "Countries of operation" },   pins: ["facilities"] },
            { value: { extract: "ghg",        cite: true, prompt: "Latest reported Scope 1+2 (or total) GHG emissions, normalized to clean units: megatonnes for values ≥100,000 tonnes — e.g. 749,643 tonnes → '0.75 Mt CO₂e' — otherwise 't CO₂e'. NEVER use the ambiguous 'MT' (the source's 'MT' means metric tonnes; convert it). null if not disclosed." }, label: { text: "Scope 1+2 CO₂e · latest" },  pins: ["sustainability_overview", "sustainability_details"] },
            { value: { extract: "net_zero",   cite: true, prompt: "Net-zero target year or commitment statement (e.g. '2050' or 'SBTi 2030')." }, label: { text: "Net-zero target" },          pins: ["sustainability_overview", "sustainability_details"] },
            { value: { extract: "cdp",        cite: true, prompt: "CDP score or disclosure rating (e.g. 'A-' or 'Discloser')." },              label: { text: "CDP / disclosure" },         pins: ["sustainability_overview", "sustainability_details"] },
            { value: { extract: "esg_report", cite: true, prompt: "Year of the most recent sustainability/ESG report, e.g. '2024'. Year only — the tile label already says 'Latest ESG report'." }, label: { text: "Latest ESG report" }, pins: ["sustainability_overview", "sustainability_details"] }
          ]
        }
      ]},
      { id: "financial_col", proportion: 0.44, header: "Financial activity", pins: ["financials"], blocks: [
        { type: "conditional", id: "financial_activity", pins: ["supplier", "financials"],
          discriminator: { role: "llm_gen", key: "has_financials", pins: ["supplier", "financials"], prompt: "true if the source provides multi-year quantitative financial figures (e.g. a revenue series, profit/EBITDA, net debt) — typical of listed issuers or bond filers. false if the company is private with no reported figures, or only stray dollar amounts appear in narrative." },
          branches: [
          { when: { field: "has_financials", op: "eq", value: true }, then: {
            type: "flow", direction: "vertical", children: [
              { type: "chart", id: "revenue_chart", kind: "bar", proportion: 1.5, pins: ["financials"],
                data_binding: { limit: 4, label_field: "fiscal_year", label_format: "FY{value}" },
                series: [
                  { key: "revenue", label: "Revenue", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" },
                  { key: "net_profit", label: "Net profit", label_position: "above_bar", label_format: "currency",
                    color: { op: "cond", branches: [
                      { when: { field: "net_profit_yoy", op: "lt", value: -0.15 }, then: "#BA7517" }
                    ], default: "#7FA690" }}
                ],
                y_axis: { unit: "$bn", value_divisor: 1000, value_decimals: 2, gridlines: { dashed: true, steps: [0.25, 0.50, 0.75, 1.0] }}
              },
              { type: "section_header", text: "Key ratios · latest FY", proportion: 0.15 },
              { type: "tile_row", id: "ratio_callout", data_pin: "financials", proportion: 0.6, pins: ["financials"], null_fallback: { display: "N/A", color: "#999" },
                tiles: [
                  { value: { formula: "net_profit / revenue * 100", format: "pct_1dp" },           label: { text: "Net margin" } },
                  { value: { formula: "net_debt / ebitda",          format: "num_1dp", suffix: "x" }, label: { text: "Net debt/EBITDA" } },
                  { value: { formula: "net_profit / assets * 100",  format: "pct_1dp" },           label: { text: "Return on assets" } }
                ]
              }
            ]
          }},
          { when: { field: "has_financials", op: "eq", value: false }, then: {
            type: "flow", direction: "vertical", children: [
              { type: "text_block", id: "private_financials", pins: ["supplier"], proportion: 1,
                slots: [
                  { role: "literal", value: "Private · financials not disclosed", style: "italic_serif_center" },
                  { role: "llm_gen", key: "last_known",      max_words: 15, cite: true, prompt: "Last known revenue and one or two key headline metrics. Cite the year." },
                  { role: "llm_gen", key: "capital_actions", max_words: 45, cite: true, prompt: "Summarise recent capital actions: debt issuance, refinancing, recapitalization, equity raises, M&A. Include dates and amounts." }
                ]
              },
              { type: "section_header", text: "Key financial actions", proportion: 0.12 },
              { type: "tile_row", id: "capital_callout", cols: 3, proportion: 0.55, pins: ["supplier"], null_fallback: { display: "—", color: "#999" },
                tiles: [
                  { value: { extract: "debt_change",      cite: true, prompt: "Headline debt change from a recent recap/refinancing, e.g. '−60%'. null if none." }, label: { text: "Debt reduction" } },
                  { value: { extract: "new_capital",      cite: true, prompt: "New capital injected, e.g. '+$100M'. null if none." },                              label: { text: "New capital" } },
                  { value: { extract: "credit_extension", cite: true, prompt: "Maturity/credit extension, e.g. '+2.5yr'. null if none." },                          label: { text: "Credit extended" } }
                ]
              }
            ]
          }}
        ]}
      ]},
      { id: "events_col", proportion: 0.34, header: "Recent events · past 12 months", pins: ["supplier"], blocks: [
        { type: "bullet_list", id: "recent_events", item_limit: 5, pins: ["supplier"], evidence_in_tooltip: true,
          priority_hint: "Return 5 distinct material events whenever the source supports at least 5 (it usually does) — only return fewer if there genuinely aren't 5. A plant closure, a financing/recap, a ratings action, a report release, and an inventory/target are SEPARATE events — include each; don't drop a clear event just because it also appears elsewhere in the report. APPROXIMATE dates are fine and must NOT cause you to drop an event — 'mid-2025' → 'Mid 2025', 'early 2025' → 'Early 2025', undated-but-yearly → '2024'. The ONLY thing to merge is the SAME underlying event repeated with different dates/wording (e.g. a recapitalization described as both September and October 2025 is one event — pick the most precise date). Most recent first; balance BIZ/FIN/ESG/OPS.",
          item_template: {
            tag:      { role: "llm_gen", key: "tag",      prompt: "One of: BIZ (business/product/partnerships), FIN (financing, M&A, ratings), ESG (sustainability), OPS (sites, capacity, leadership)." },
            date:     { role: "llm_gen", key: "date",     format: "Mon YYYY", prompt: "Event date: 'Mon YYYY' when known, else an approximate label like 'Early 2025', 'Mid 2025', 'H1 2025', or just '2024'. Only omit the date (not the event) if there is no time reference at all." },
            headline: { role: "llm_gen", key: "headline", max_words: 12, prompt: "Action-led headline naming the actor and what happened. It can be a little fuller now that supporting detail moves to a hover citation." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30, prompt: "Verbatim sentence(s) from the source supporting the headline — quote exactly, do not paraphrase. Shown only on hover as the citation." }
          },
          tag_styles: {
            BIZ: { bg: "#DCE7DF", text: "#1F3A2E" },
            FIN: { bg: "#EFE0D8", text: "#8A4A2A" },
            ESG: { bg: "#D8E2EF", text: "#2C4A6E" },
            OPS: { bg: "#E8E2D0", text: "#5A6F62" }
          }
        }
      ]}
    ]}
  ]
};

const EXAMPLE_11_SOURCE_PINS = {
  supplier: `OverviewLate 2025 AIHS secured new senior debt; Ardagh completed debt-for-equity recap, shifted ownership, agreed Swedish recycler purchase, AGP-North America partnership.  DetailsIn January 2024, AGP-Europe launched a 300g lightweight glass wine bottle combining premium design and sustainability. In mAIHS executed definitive documentation for a new senior secured facility to support its capital structure. In July 2025, Ardagh agreed a comprehensive recapitalization transaction, culminating on November 12 with financial creditors holding over 99% of SSNs and 80% of toggle notes; former SUN holders received 92.5% equity via a debt-for-equity swap, transferring ownership principally to Ardagh Holdings S.A.'s major financial institutions and investment funds. Concurrently, Ardagh announced acquisition of a world-leading Swedish glass recycler and extinguished a $3 million related-party loan receivable. It confirmed participation in Drinktec 2025 and, in February 2025, disclosed permanent closures of its Drebkau and Dolton glass production facilities with demand now serviced by remaining networks. On December 1, 2025, AMPSA issued €570 million 5.000% and $620 million 6.250% Senior Secured Green Notes due 2031. Through its "Clearly Ardagh" Transformation Initiative launched in May 2026, Ardagh Glass Packaging launched a new 275ml beverage bottle in collaboration with Fitz's Bottling Company, added 8oz ring-neck bottles for U.S. markets, and empowered teams under Executive Chairman Mark Porto and CCO Timur Colak to drive competitive agility.  PerformanceArdagh reported $9.1 billion in 2024 sales and $9.598 billion in 2025 revenue. Group Adjusted EBITDA for Q4 2025 increased 24% to $342 million from $275 million in Q4 2024. The company projects Glass Packaging EBITDA at approximately $660 million in FY 25E, rising to $700 million and $760 million in FY 26E and FY 27E, respectively. At December 31 2025, net debt was $8.468 billion, supported by $1.877 billion in available liquidity.`,

  financials: JSON.stringify({
    revenue_details: { description: "Revenue", source: "merged_financials", currency: "USD", values: [
      { value: 9030000000, point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 9402000000, point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 9140000000, point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 9598000000, point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    operating_income_details: { description: "Operating income", values: [
      { value: 347000000, point_in_time_raw: "2022" },
      { value: 451000000, point_in_time_raw: "2023" }
    ]},
    net_profit_details: { description: "Net profit", values: [
      { value: -290000000, point_in_time_raw: "2022" },
      { value: -495000000, point_in_time_raw: "2023" },
      { value: -597000000, point_in_time_raw: "2024" },
      { value: -952000000, point_in_time_raw: "2025" }
    ]},
    assets_details: { description: "Assets", currency: "USD", values: [
      { value: 11821000000, point_in_time_raw: "2022" },
      { value: 11514000000, point_in_time_raw: "2023" },
      { value: 10722000000, point_in_time_raw: "2024" },
      { value: 11162000000, point_in_time_raw: "2025" }
    ]},
    employee_count_details: { description: "Employees", values: [
      { value: 20000, point_in_time_raw: "2024" },
      { value: 19000, point_in_time_raw: "2025" }
    ]},
    price_details: { description: "Stock price (adj. close)", currency: "USD", values: [
      { value: 23.23, point_in_time_iso: "2021-05-28" },
      { value: 23.19, point_in_time_iso: "2021-06-30" },
      { value: 22.08, point_in_time_iso: "2021-07-30" },
      { value: 24.84, point_in_time_iso: "2021-08-31" },
      { value: 24.25, point_in_time_iso: "2021-09-30" },
      { value: 24.75, point_in_time_iso: "2021-10-29" },
      { value: 24.75, point_in_time_iso: "2021-11-30" },
      { value: 24.75, point_in_time_iso: "2021-12-31" },
      { value: 24.75, point_in_time_iso: "2022-01-31" },
      { value: 24.75, point_in_time_iso: "2022-02-28" },
      { value: 24.75, point_in_time_iso: "2025-07-31" },
      { value: 24.75, point_in_time_iso: "2025-08-29" },
      { value: 24.75, point_in_time_iso: "2025-09-30" },
      { value: 24.75, point_in_time_iso: "2026-05-20" }
    ]},
    net_profit_percentage_details: { description: "Net profit percentage", values: [
      { value: -3.21, point_in_time_raw: "2022" },
      { value: -5.26, point_in_time_raw: "2023" },
      { value: -6.53, point_in_time_raw: "2024" },
      { value: -9.92, point_in_time_raw: "2025" }
    ]}
  }),

  sustainability_overview: `Overview2024 Sustainability Report released, renewable PPA covering 40% load, biofuel trial, CAP Glass recycling partnership, new solar plant in Netherlands.  DetailsArdagh's 2024 Sustainability Report outlines a suite of environmental initiatives. A power purchase agreement supplies 100% of Vitberget's capacity, covering roughly 40% of AGP-Europe's electricity demand and offsetting Scope 2 emissions. The company's NextGen hybrid furnace consistently cuts CO2 emissions by 64%, potentially saving up to 45,000 tonnes of carbon each year. An industrial fuel-switching trial at the Knottingley site demonstrated successful glass production using biofuel, further lowering the carbon footprint. Ardagh announced a long-term partnership with CAP Glass to expand glass-recycling services, reinforcing circular-economy goals. A new solar power plant in Dongen, the Netherlands, now provides on-site renewable electricity, complementing the broader aim of 100% renewable electricity by 2030. The 2024 Sustainability Roadmap, published alongside the report, charts progress toward science-based targets approved by the SBTi, including a 42% absolute Scope 1+2 reduction by 2030 and zero waste to landfill ambitions. The California facility's tenth consecutive ENERGY STAR certification underscores ongoing energy-efficiency efforts across the group.  PerformanceThe renewable PPA offsets about 40% of AGP-Europe's power load, reducing Scope 2 emissions. The NextGen furnace achieves a 64% CO2 cut, equating to roughly 45,000 tonnes saved annually. Renewable electricity now represents 30% of global consumption, with Europe at 46%. Water withdrawal intensity improved by 30%, and 83% of facilities have achieved zero waste to landfill. Targets include 100% renewable electricity and a 42% Scope 1+2 reduction by 2030.`,

  sustainability_details: `ClimateAGP-Europe's recent PPAs and on-site solar illustrate a shift toward long-term renewable procurement, covering >50 % of its future electricity demand and delivering measurable emissions reductions. The 10-year PPA with RPC's 161 MW Vitberget wind farm secures about 40 % of AGP-Europe's load, while the Sunnic Lighthouse agreement fixes 130 GWh annually and promises up to 45 000 t CO2 savings and a 42 % cut in scope 1-2 emissions by 2030.Complementary solar projects at Moerdijk and Nigel add 10 MW and 725 t CO2 avoided, reinforcing the 100 % renewable electricity target and SBTi alignment. The hybrid NextGen furnace delivers a 64 % CO2 reduction, saving 18 000 t since 2024, underscoring a broader strategy that blends off-site PPAs, on-site generation and fuel-switching trials to secure additionality and stable cash flows without subsidies.  Nature / BiodiversityArdagh Glass Packaging has expanded its circularity agenda through a partnership with CAP Glass, establishing dedicated glass recycling services that extend the value chain. The acquisition of Swedish collector SVA further strengthens resource efficiency, while internal initiatives such as large-scale tree planting by three Polish facilities demonstrate a community-driven approach to biodiversity restoration. These actions are framed within the Emissions and Ecology pillars, which guide the company's strategy to mitigate negative environmental impacts across operations and supply chains.Beyond recycling, Ardagh leverages its education programme to promote STEM learning and biodiversity awareness, encouraging schools to develop green classrooms and conservation projects. The company highlights the critical role of biodiversity for human well-being and positions its permanent, infinitely recyclable glass material as a catalyst for a circular economy that reduces energy use and supports ecosystem health.  WaterThe N2 and N3 projects in Africa embed significant water-efficiency measures as part of AGP-Africa's broader decarbonisation pathway, with the Nigel facility also hosting a 10 MW solar PV array that supports the 100 % renewable energy ambition for 2030. These initiatives align with the company's internal water targets, which aim for a 26 % reduction in water intensity by 2030, building on a reported 30 % progress toward earlier goals.AGP's water stewardship is further reflected in its CDP submission and a corporate commitment to cut water withdrawal intensity by 20 % across operations, while maintaining quality standards.  Regional / Country FocusAGP-Africa's recent capacity investments, highlighted by the N2 and N3 projects, combine energy and water efficiency with a 10 MW solar PV array at the Nigel facility, positioning the region to respond quickly to sustainable demand. The CEO emphasised that the modern asset base and spare capacity enable rapid restarts when market conditions support additional production. Complementary initiatives extend the regional ecosystem: the introduction of five Smart Trucks enhances logistics efficiency, while the partnership with CAP Glass and the acquisition of SVA reinforce circularity across the continent. In Brazil, long-term contracts will deliver sustainable metal and glass packaging to Europe, North America and Brazil, reflecting a broader commitment to ESG goals.`,

  risk: JSON.stringify({
    rating: "B",
    riskDescription: "Low Risk",
    providerScore: 64,
    providerScoreMax: null,
    pod: null,
    creditLimit: 1000000,
    creditLimitCurrency: "USD",
    contractLimit: null,
    previousScore: 68,
    ratingDate: "2026-06-14T10:53:01Z",
    oddsOfFailure: null,
    peerRanking: null,
    dbt: { dbt: 13, industryDBT: 1 },
    weightedDbt: null,
    industryDbt: null,
    paymentTrend: null,
    ccjExactCount: null,
    ccjExactValue: null,
    ccjCurrency: null,
    ccjPossibleCount: null,
    ccjSatisfiedCount: null,
    mortgagesOutstanding: null,
    mortgagesSatisfied: null,
    profitWarningCount: 0,
    latestProfitWarning: null,
    connectId: "US001-X-US80026763",
    fetchedAt: "2026-06-16T09:29:15.303Z"
  }),

  facilities: `Consolidated Manufacturing Facilities Table
Country | Location | Focus | Sustainability
USA | Bridgeton, New Jersey | Glass packaging | FSSC 22000 certified
USA | Dunkirk, Indiana | Glass packaging | ENERGY STAR certified
USA | Madera, California | Glass packaging | ENERGY STAR certified
USA | Fairfield, California | Beverage can manufacturing | ISO achievements
USA | Fremont, Ohio | Beverage can manufacturing | ISO achievements
USA | Reno, Nevada | Food can manufacturing | ISO achievements
UK | Wrexham, UK | Metal packaging plant | -`
};

// ---------------------------------------------------------------------------
// Example 11 — alternate dataset: Anchor Glass (same DSL, different source).
// Note: no `financials` pin ("finances: none") — exercises the private-company
// branch of the financial_activity conditional.
// ---------------------------------------------------------------------------

const ANCHOR_GLASS_SOURCE_PINS = {
  supplier: `OverviewAnchor Glass completed its comprehensive recapitalization and extended its term loan and revolving credit facilities to strengthen its balance sheet.  DetailsIn October 2025, Anchor Glass completed its previously announced comprehensive recapitalization with backing from new majority owners Canyon Capital Advisors, Millstreet Capital, and UBS, reducing debt by over 60% and providing $100 million of new capital. Anchor Glass, the third largest manufacturer of glass containers in the United States, operates eight strategically located facilities producing a diverse line of flint (clear), amber, green and other colored glass containers for the beer, beverage, food, liquor and flavored alcoholic beverage markets. Containers that do not meet quality standards are crushed and recycled as cullet, and the company maintains laboratories at each plant to test physical characteristics. Under its "One Anchor" rallying cry to "execute as One Anchor," the company is advancing safety, delivering a flawless customer experience, and reducing industrial cost at its five glass manufacturing facilities in New York, Oklahoma, Indiana, Minnesota, and Georgia. Earlier, in August 2023, Anchor Glass amended and extended its term loan credit agreements and asset-based revolving credit facility. CEO Nipesh Shah said the successful extension underscores strong relationships with key lenders and equity owners and reflects confidence in Anchor Glass's continuing success.  PerformanceAnchor Glass's comprehensive recapitalization reduced debt by more than 60% and provided $100 million of new capital. The maturity dates of all three credit agreements were extended by approximately two and a half years in August 2023. For the year ended December 31 2004, net sales were $746.9 million, cost of products sold was $712.3 million, and net loss was $86.1 million.  Recent Newsworthy EventsAnchor Glass Container Corporation completed a comprehensive recapitalization agreement in September 2025, reducing its total outstanding debt by more than 60% and securing about $100 million in new capital to fund ongoing investment and growth initiatives. Following this transaction, credit analysts upgraded Anchor Glass's ratings in mid-2025, citing the strengthened balance sheet and expectations for continued capital spending on furnace rebuilds and modernization. In early 2025, Anchor Glass announced plans to permanently close its longstanding Jacksonville, Florida plant, with layoffs affecting roughly 140-150 employees, as part of a footprint rationalization. In its 2024 Corporate Sustainability and Responsibility Report released in September 2025, Anchor Glass highlighted progress on environmental and social initiatives, including energy-efficiency projects at furnaces and expanded recycling and cullet usage. By 2024, Anchor Glass's Environmental, Health and Safety (EHS) team emphasized updated 2024 goals focused on safety performance and environmental compliance across plants.`,

  sustainability_overview: `OverviewJoined Glass Recycling Coalition, earned Jacksonville Environmental Stewardship Award, increased renewable electricity to 25%, cut GHG 19.6%, boosted recycling 19%.  DetailsAnchor Glass expanded its sustainability agenda by joining the Glass Recycling Coalition, reinforcing circular-economy goals. The Jacksonville plant earned the Jacksonville Electric Authority's Environmental Stewardship Award. Renewable electricity in the supplying grids rose to 25%, a two-percent gain, while total energy use reached 2,089,358,006 kWh. A Gold Medal placed Anchor in the top five percent of global glass manufacturers for environmental and social performance. Greenhouse-gas emissions fell 10.8% to 932,628 MT CO2e, with Scope 1 down 9.5% and Scope 2 down 14.2%. The company reported four consecutive years of reductions in sulfur dioxide, nitrogen oxides and particulate matter, alongside a 9% waste decline and a 19% increase in recycling. Anchor produces infinitely recyclable glass bottles and aligns its reporting with GRI Universal Standards and the United Nations Sustainable Development Goals. Renewable energy content reached 24% based on EPA eGRID data. In 2024, total emissions dropped to 749,643 MT CO2e, a 19.6% reduction, while recycling efforts rose 19%. Partnerships with the Glass Recycling Foundation support circular-economy initiatives across North America. The company completed 98% of CSR questionnaires for Category 3 suppliers.  PerformanceIn 2023 Anchor used 2,089,358,006 kWh of energy, with purchased electricity representing 20% of total. Renewable electricity share rose to 25%, a 2% increase. GHG emissions fell 10.8% to 932,628 MT CO2e (Scope 1 down 9.5%, Scope 2 down 14.2%). In 2024 emissions dropped to 749,643 MT CO2e, a 19.6% reduction, renewable energy content reached 24%, and recycling increased 19%.`,

  sustainability_details: `ClimateAnchor Glass frames its climate strategy around sustainable development principles, targeting GHG reductions and renewable energy integration. The company reports a 19.6% reduction in total GHG emissions with a 26.9% improvement in intensity, while noting a rise in Scope 2 emissions linked to higher electricity use. It has completed its first full Scope 3 inventory and leverages continuous air emission monitoring across all facilities since 2021. Renewable energy is pursued through partnerships and virtual power purchase agreements, with renewable electricity share rising and plans to explore further procurement in 2025. Technological advances such as ceramic catalytic filters have cut SO2 intensity from 0.72 kg/MT to 0.66 kg/MT and NOx intensity down 4.5%.  Nature / BiodiversityAnchor Glass signals a deepening commitment to circularity by joining the Glass Recycling Coalition and collaborating with industry groups such as the Glass Packaging Institute and the Glass Recycling Foundation. These partnerships support higher recycling rates and embed sustainable procurement and waste-management priorities. The company tracks reuse and recycling metrics to guide future reduction targets.  WaterAnchor Glass places water stewardship within its sustainability agenda, citing Goal 6 on clean water and sanitation. All manufacturing sites comply with wastewater and stormwater permitting, and the company reports an 8.72% reduction in water withdrawals from 2022 to 2023 - the third consecutive year of decline, with withdrawal intensity also falling. Despite a 3% increase in total water usage in 2024 driven by higher operational demand, intensity factors continue to improve. The report notes rising aquifer stress and insufficient replenishment.  Regional / Country FocusAnchor Glass highlights regional economic development through partnerships with nearby businesses and infrastructure investments. Through the Anchor in Action employee resource group, the firm pursues value creation for all stakeholders.  SBTi Target DashboardAnchor Glass Container Corporation (SBTi ID 40002791), Containers and Packaging sector, United States. Near-term status: Commitment removed. Net-zero status: Commitment removed. Two commitments were published 2022-09-15 with no validated targets. Temperature alignment: not available for scope 1+2.`,

  facilities: `Consolidated Manufacturing Facilities Table
Country | Location | Focus | Sustainability
United States | Shakopee, MN | Glass containers | Draws water from two low-risk wells; part of company-wide 19.6% GHG reduction, 26.9% intensity improvement, 9% waste reduction, 19% recycling increase, 25% renewable electricity
United States | Henryetta, OK | Glass containers | Contributes to company-wide 19.6% GHG reduction and 26.9% intensity improvement
United States | Lawrenceburg, IN | Glass containers | Contributes to company-wide GHG and intensity improvements
United States | Elmira, NY | Glass containers | Contributes to company-wide GHG and intensity improvements
United States | Warner Robins, GA | Glass containers | Contributes to company-wide GHG and intensity improvements
United States | Jacksonville, FL (closed Nov 2024) | Glass containers (closed) | Closed November 2024, reducing water use by ~0.7%`
};

// ---------------------------------------------------------------------------
// Example 12 — Diageo Slide 02 · Financial health & business activity.
// Three-band layout: posture callout + revenue chart · economic ratios row ·
// stock-price chart with stat tiles + business activity bullets. Introduces
// a new `stock_price` pin for the time-series chart; everything else flows
// from the existing supplier + financials pins.
// ---------------------------------------------------------------------------

const EXAMPLE_12_DSL = {
  version: 1, kind: "slide",
  pins: [
    { name: "supplier",    type: "entity",    label: "Supplier",         required: true  },
    { name: "financials",  type: "financial", label: "Financial data",   required: false },
    { name: "stock_price", type: "financial", label: "Stock price data", required: false }
  ],
  chrome: {
    header: {
      style: "dark_green_band",
      label: "02 · FINANCIAL HEALTH & BUSINESS ACTIVITY",
      supplier_name: { role: "llm_gen", key: "supplier_name", pins: ["supplier"], prompt: "Company name as it appears in the source." },
      chips: [
        { role: "llm_gen", key: "header_sector",      label: "Sector",       pins: ["supplier"], prompt: "2-3 word industry category (e.g. 'Food & beverage')." },
        { role: "llm_gen", key: "header_hq_listing",  label: "HQ · listing", pins: ["supplier"], prompt: "HQ city, country · exchange:ticker (e.g. 'London, UK · LSE'). 'Private' if unlisted." },
        { role: "llm_gen", key: "header_ownership",   label: "Ownership",    pins: ["supplier"], prompt: "Most specific recent ownership status: Public, Private, PE-backed, State-owned, Subsidiary." }
      ]
    },
    footer: {
      style: "dark_green_band",
      left: "Sense Platform · AI-assisted research · Supplement to, not sole basis for, decision-making",
      right: "Supplier Intelligence · May 2026"
    }
  },
  bands: [
    // Band 1: financial posture callout · revenue & net profit chart
    { id: "posture_band", proportion: 0.32, columns: [
      { id: "posture_col", proportion: 0.3, header: "Financial posture", blocks: [
        { type: "text_block", id: "financial_posture",
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: [
            { role: "llm_gen", key: "posture_headline", max_words: 8, cite: true, pins: ["supplier", "financials"], style: "italic_serif",
              prompt: "5-8 word italic-serif headline summarising overall financial posture (e.g. 'Strong franchise, weakening near-term performance.')." },
            { role: "llm_gen", key: "posture_body", max_words: 55, cite: true, pins: ["supplier", "financials"],
              prompt: "2-3 sentence assessment. Cite headline revenue, profit trajectory (with YoY direction), debt level, and any leadership-driven strategic shift." }
          ]
        }
      ]},
      { id: "rev_chart_col", proportion: 0.7, header: "Revenue & net profit · FY22-FY25 ($bn)", blocks: [
        { type: "chart", id: "revenue_chart", kind: "bar", pins: ["financials"],
          data_binding: { limit: 4, label_field: "fiscal_year", label_format: "FY{value}" },
          series: [
            { key: "revenue",    label: "Revenue",    color: "#1F3A2E", label_position: "above_bar", label_format: "currency" },
            { key: "net_profit", label: "Net profit", label_position: "above_bar", label_format: "currency",
              // YoY drop > 15% gets the "Loss / decline" amber.
              color: { op: "cond", branches: [
                { when: { field: "net_profit_yoy", op: "lt", value: -0.15 }, then: "#BA7517" }
              ], default: "#7FA690" }}
          ],
          y_axis: { unit: "$bn", value_divisor: 1000, value_decimals: 2,
            gridlines: { dashed: true, steps: [0.25, 0.50, 0.75, 1.0] }}
        }
      ]}
    ]},

    // Band 2: key economic ratios — 6 tiles across the full width
    { id: "ratios_band", proportion: 0.18, columns: [
      { id: "ratios_col", proportion: 1, header: "Key economic ratios · most recent disclosed", blocks: [
        { type: "tile_row", id: "economic_ratios", cols: 6, data_pin: "financials",
          pins: ["financials", "supplier"],
          null_fallback: { display: "N/A", color: "#999" },
          tiles: [
            { value: { formula: "net_profit / revenue * 100", format: "pct_1dp" },
              label: { text: "Net margin" } },
            { value: { extract: "gross_margin", cite: true,
                       prompt: "Latest reported gross margin (gross profit / revenue × 100) as a percentage. null if not disclosed.",
                       format: "pct_1dp" },
              label: { text: "Gross margin" } },
            // Reported leverage ratio ("Adjusted net borrowings to adjusted
            // EBITDA" in the annual report) — extracted directly rather than
            // computed, since net debt isn't a clean line in the finance pin
            // and the reported ratio already reconciles currency/adjustments.
            { value: { extract: "net_debt_to_ebitda", cite: true,
                       prompt: "Reported leverage ratio — 'Adjusted net borrowings to adjusted EBITDA' (or 'Net debt / EBITDA'). Latest period. Number only (e.g. 3.4). null if not disclosed.",
                       format: "num_1dp", suffix: "x" },
              label: { text: "Net debt / EBITDA" } },
            { value: { formula: "operating_income / revenue * 100", format: "pct_1dp" },
              label: { text: "Operating margin" } },
            { value: { formula: "net_profit / (assets - liabilities) * 100", format: "pct_1dp" },
              label: { text: "Return on equity" } },
            { value: { extract: "free_cash_flow", cite: true,
                       prompt: "Most recent reported free cash flow. null if not disclosed.",
                       format: "currency_m" },
              label: { text: "Free cash flow" } }
          ]
        }
      ]}
    ]},

    // Band 3: stock chart + stat tiles (left half) · business activity (right half)
    { id: "stock_activity_band", proportion: 0.5, columns: [
      { id: "stock_col", proportion: 0.5, header: "Stock price · 3-year", blocks: [
        { type: "flow", id: "stock_inner", direction: "vertical", children: [
          { type: "chart", id: "stock_chart", kind: "line", proportion: 0.7,
            pins: ["stock_price"],
            data_binding: { limit_years: 3, label_field: "month_label", label_format: "MMM 'YY" },
            series: [
              { key: "price", label: "Stock price", color: "#1F3A2E", show_latest_label: true, label_format: "currency" }
            ],
            y_axis: { unit: "$", value_decimals: 0,
              gridlines: { dashed: true, steps: [0.25, 0.50, 0.75, 1.0] }}
          },
          { type: "tile_row", id: "stock_stats", cols: 3, proportion: 0.3,
            pins: ["stock_price", "financials", "supplier"],
            null_fallback: { display: "N/A", color: "#999" },
            tiles: [
              { value: { extract: "stock_3y_change", cite: true,
                         prompt: "Total stock-price return over the past 3 years as a percentage (e.g. -37 for a 37% decline). Compute from earliest vs latest available price within the 3-year window. Negative values render as the loss colour automatically.",
                         format: "pct_1dp" },
                label: { text: "3-year change" } },
              { value: { extract: "market_cap", cite: true,
                         prompt: "Latest market capitalisation. Currency with bn/B suffix. null if not derivable." },
                label: { text: "Market cap" } },
              { value: { extract: "pe_trailing", cite: true,
                         prompt: "Trailing twelve-month price-to-earnings ratio. null if not disclosed.",
                         format: "num_1dp", suffix: "x" },
                label: { text: "P/E (trailing)" } }
            ]
          }
        ]}
      ]},
      { id: "activity_col", proportion: 0.5, header: "Business activity · past 12 months", blocks: [
        { type: "bullet_list", id: "business_activity", item_limit: 5,
          pins: ["supplier"], evidence_in_tooltip: true,
          priority_hint: "Return 5 distinct material business events from the past ~12 months. Mix categories where the source supports it: leadership (LEAD), M&A / divestiture / partnership (M&A), strategic programme or transformation (STRAT), capacity / plant action (CAP), financing or capital action (FIN). Most recent first. Drop overlapping items only if literally the same event.",
          item_template: {
            tag:      { role: "llm_gen", key: "tag",      prompt: "One of: LEAD, M&A, STRAT, CAP, FIN. Choose by event type (see priority_hint)." },
            date:     { role: "llm_gen", key: "date",     format: "Mon YYYY",
                        prompt: "Event date as 'Mon YYYY' (e.g. 'Apr 2025') when an explicit month-year is stated in the source. Use just 'YYYY' if only a year is stated. Return the literal string 'Unknown' when the source has no date or only an ambiguous reference (e.g. a fiscal-quarter label like 'Q3 F25' without an accompanying calendar month). NEVER infer or invent a date." },
            headline: { role: "llm_gen", key: "headline", max_words: 28,
                        prompt: "Format: '**<2-4 word bold lead label>.** <one-sentence description naming actor, action, and key detail.>' Don't restate the tag in the lead label. Don't include the date in the headline — the date column already shows it." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "Verbatim sentence(s) from the source supporting the headline — quote exactly. Shown only on hover." }
          },
          tag_styles: {
            LEAD:  { bg: "#D8E2EF", text: "#2C4A6E" },
            "M&A": { bg: "#EFE0D8", text: "#8A4A2A" },
            STRAT: { bg: "#E8E2D0", text: "#5A6F62" },
            CAP:   { bg: "#DCE7DF", text: "#1F3A2E" },
            FIN:   { bg: "#F2D7CE", text: "#B85450" }
          }
        }
      ]}
    ]}
  ]
};

// Example 12 source data — Diageo. Reuses supplier + financials from the
// existing test fixtures and adds a stock_price JSON pin (monthly adj-close +
// trading-volume from the data feed format used in the live flow).
const DIAGEO_SOURCE_PINS = {
  // Verbatim from the live flow's last_inputs payload — intentionally NOT
  // edited to match the target slide. Lets us see which fields the
  // crystallizer can actually extract from the real source vs. which would
  // need source enrichment upstream.
  supplier: `# Overview
Diageo recently issued its Fiscal '26 Q3 trading statement, released interim results, published Q3/H1 aide memoires and completed Sheridan's sale.
# Details
Diageo today announces that Debra Crew has stepped down as Chief Executive Officer and as a Board Director with immediate effect, marking a key leadership transition. On 7 April 2025, the group entered into a strategic partnership with Main Street Advisors to strengthen advisory capabilities. As part of that agreement, Diageo transferred its majority ownership interest in Cîroc in North America in exchange for a global interest in Lobos 1707 Tequila, diversifying its premium spirits portfolio. Earlier, on 16 January 2024, Diageo agreed with Combs Wine and Spirits LLC to purchase the remaining 50% share capital of DeLeon Holdco LLC for a total consideration of $223 million, including transaction costs, securing full control of its Tequila venture. In its Q3 F25 trading statement, Diageo outlined the first phase of Accelerate, setting clear cash delivery targets alongside a disciplined approach to operational excellence and cost efficiency, underlining its strategic focus on driving margin improvement. These measures underscore proactive governance and portfolio realignment, supporting disciplined growth and operational excellence across the organisation. It reflects Diageo's emphasis on sharpening brand portfolio and financial discipline as part of its Accelerate programme. Leadership transitions, partnership deals and brand exchanges combine to drive global market positioning and operational resilience.
# Performance
In the six months to 31 December 2025, Diageo reported net sales of $10,460 million, down 4.0%. Free cash flow decreased by $164 million to $1.5 billion, although management reiterated full-year guidance of $3.0 billion. Net debt stood at $21.7 billion as at 31 December 2025. The reiterated guidance underscores commitment to liquidity and disciplined capital allocation in H2.`,
  financials: JSON.stringify({
    revenue_details: { description: "Revenue", source: "merged_financials", currency: "GBP", values: [
      { value: 12733000000,    currency: "GBP", point_in_time_raw: "2021", point_in_time_iso: "2021-01-01" },
      { value: 15246601010.76, currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 20555000000,    currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 20269000000,    currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 20245000000,    currency: "GBP", point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    net_profit_details: { description: "Net profit", source: "merged_financials", currency: "GBP", values: [
      { value: 3277320650.1,   currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 4479000000,     currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 4166000000,     currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 2538000000,     currency: "GBP", point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    assets_details: { description: "Assets", source: "merged_financials", currency: "GBP", values: [
      { value: 32834888499.63, currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 33355098126.63, currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 45474000000,    currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 49322000000,    currency: "GBP", point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    liabilities_details: { description: "Liabilities", source: "merged_financials", currency: "GBP", values: [
      { value: 24280412761.92, currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 24653477380.14, currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 33404000000,    currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 36144000000,    currency: "GBP", point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    operating_income_details: { description: "Operating income", source: "merged_financials", currency: "GBP", values: [
      { value: 3731000000,    currency: "GBP", point_in_time_raw: "2021", point_in_time_iso: "2021-01-01" },
      { value: 4382394529.17, currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 5547000000,    currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 6001000000,    currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 4335000000,    currency: "GBP", point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    free_cash_flow_details: { description: "Free cash flow", source: "merged_financials", currency: "GBP", values: [
      { value: 3028000000,    currency: "GBP", point_in_time_raw: "2021", point_in_time_iso: "2021-01-01" },
      { value: 2783000000,    currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 1660955023.35, currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 1938895595.49, currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 2195892752.28, currency: "GBP", point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    ebitda_details: { description: "EBITDA", source: "merged_financials", currency: "GBP", values: [
      { value: 5446394529.17, currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 6844000000,    currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 6494000000,    currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" }
    ]},
    net_debt_to_ebitda_details: { description: "Adjusted net borrowings to adjusted EBITDA", source: "reported", values: [
      { value: 3.0, point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 3.4, point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    gross_margin_details: { description: "Gross margin", source: "merged_financials", values: [
      { value: 60.43, point_in_time_raw: "2021", point_in_time_iso: "2021-01-01" },
      { value: 61.38, point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: 59.67, point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: 60.18, point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" },
      { value: 60.13, point_in_time_raw: "2025", point_in_time_iso: "2025-01-01" }
    ]},
    capital_expenditure_details: { description: "Capital expenditure", source: "merged_financials", currency: "GBP", values: [
      { value: -626000000,    currency: "GBP", point_in_time_raw: "2021", point_in_time_iso: "2021-01-01" },
      { value: -1097000000,   currency: "GBP", point_in_time_raw: "2022", point_in_time_iso: "2022-01-01" },
      { value: -1053052916.37, currency: "GBP", point_in_time_raw: "2023", point_in_time_iso: "2023-01-01" },
      { value: -1122166481.1, currency: "GBP", point_in_time_raw: "2024", point_in_time_iso: "2024-01-01" }
    ]}
  }),
  // Sustainability content — verbatim from the same live-flow last_inputs
  // payload as the supplier pin above. Lets the new "Sustainability
  // commitments & progress" example draw on the same source the live flow
  // would actually see.
  sustainability_overview: `# Overview
Diageo advances climate resilience, cuts scope 1‑2 emissions, boosts recycled PET, improves water efficiency, and expands renewable energy targets 2025.
# Details
Diageo reports that it has achieved its 2025 recycled‑content target for PET bottles, reaching 43% versus the 35% goal. An update on its sustainability goals emphasizes building climate resilience while committing to cut scope 1 and 2 emissions by 50% by 2030 and to reach net‑zero in operations. The company also pledges a 26% reduction in scope 3 emissions by 2030. In 2025 Diageo launched a peatland restoration programme, investing up to £5 million over five years. It is developing circular packaging propositions to reduce the volume and type of packaging materials used, partnering with suppliers to improve the granularity of its scope 3 carbon footprint data while driving reductions together. Diageo works with suppliers to help them access low‑cost, reliable renewable energy, contributing to a renewable electricity share of 86.7% this year and supporting emissions reduction. Twenty‑three percent of the green gas used by its Scottish sites now derives from its own feedstocks. Water stewardship earned a place on CDP Water Security 'A List' for the seventh consecutive year, and 75% of production facilities hold ISO 9001 certification. As a signatory to RE100, Diageo aims to source 100% of its electricity from renewable sources by 2030, aligning with global grain‑to‑glass sustainability priority to preserve water.
# Performance
Diageo's 2025 recycled‑content goal for PET bottles was exceeded, reaching 43%. Renewable electricity use rose to 86.7%, surpassing the 50% target. Scope 1 and 2 emissions are set to fall 50% by 2030, with a 5.4% reduction this year driven by renewable energy. Water efficiency improved 40% in stressed areas, and waste to landfill fell 35.5%. overall performance shows continued progress globally.`,
  sustainability_details: `# Climate
Diageo's recent climate roadmap is anchored by a CAD$45.8 million public‑private contribution from Quebec, Canada and Hydro‑Québec, enabling the Valleyfield plant to switch from natural‑gas and fuel‑oil to electricity and become its first North‑American carbon‑neutral distillery. The partnership underpins four energy‑efficiency upgrades and two electrification measures, while the company reports a 5.4 % emissions dip in fiscal 23 and a 33 % Scope 1‑2 reduction driven by new biomass facilities in Kenya and Uganda. These actions reinforce Diageo's RE100 progress—86.7 % renewable electricity— and signal a market shift toward government‑backed electrification incentives.

Beyond plant‑level upgrades, Diageo leverages its Sustainable Solutions unit to co‑create low‑carbon innovations with suppliers, technology start‑ups and research partners. A landmark collaboration with glass maker Encirc aims to deliver net‑zero bottles at scale by 2030, while broader supplier engagements target renewable‑energy procurement, circular packaging and regenerative agriculture. Together with a 50 % supply‑chain emissions reduction target approved by the Science Based Targets initiative, these alliances illustrate a strategic pivot toward ecosystem‑wide decarbonisation.
# Nature / Biodiversity
Diageo's biodiversity agenda is scaling through multi‑regional nature‑based projects, beginning with a £5 million peatland restoration programme in Scotland and a 2.5 million‑tree planting pledge with American Forests for white oak regeneration. Parallel regenerative‑agriculture pilots in barley, wheat, agave and Mexico's agave sector extend climate‑smart practices to key supply‑chain inputs, while partnerships with The Nature Conservancy and IUCN launch watershed initiatives in Uganda's Victoria Nile and India's Godavari basins. Collectively these programmes embed soil‑health metrics and biodiversity outcomes into supplier contracts, signalling a shift toward data‑driven nature finance.

Diageo's participation in the One Planet for Business Biodiversity (OP2B) coalition deepens its commitment to landscape‑scale transformation, with OP2B targeting regenerative agriculture and habitat‑conversion safeguards across five sourcing regions. The company's net‑zero habitat conversion pledge—no deforestation by 2025 for direct suppliers and 2030 for indirect—underpins a strategic blend of tree‑nursery collaborations in Jalisco, Mexico, and on‑farm monitoring of biodiversity, carbon and water indicators.
# Water
Diageo's water stewardship is now anchored by a portfolio of 23 WASH projects across nine countries, delivering safe water and sanitation to 128,000 people while exceeding its replenishment target—more water returned than consumed in water‑stressed basins by 2026. The company reports a 20.6 % efficiency gain and a 40 % improvement in water‑use efficiency in stressed regions, driven by over 150 replenishment projects and the Lagos Aqua Initiative partnership with WaterAid to upgrade sanitation services in Nigeria's Ogun basin.

Strategic alignment with the Water Resilience Coalition, Alliance for Water Stewardship and the WASH4WORK framework standardises impact accounting, while collective‑action plans now cover 100 % of identified water‑stressed sites. Fiscal 24 saw 890,476 m³ of water recovered—representing 17 % of total use in stressed areas—and a 2030 commitment to extend efficiency measures to all such sites.

---

# SBTi Overview
**Location:** United Kingdom, Europe
**Near-term status:** Targets set
**Net-zero status:** Targets set
**Organization type:** Corporate
**Sector:** Food and Beverage Processing
**SBTi ID:** 40006920
**Temperature alignment:** 1.5°C (scope 1+2)
# Target Language
**Overall net-zero target:** Diageo Plc commits to reach net-zero greenhouse gas emissions across the value chain by FY2050.

**Near-term targets:** Energy & Industry: Diageo Plc commits to reduce absolute scope 1 and 2 GHG emissions 50% by FY2030 from a FY2022 base year. Diageo Plc also commits to reduce absolute Scope 3 GHG emissions 25% by FY2030 from a FY2022 base year.

FLAG: Diageo Plc commits to reduce absolute scope 1 and 3 FLAG GHG emissions 30.3% by FY2030 from a FY2022 base year. Diageo Plc commits to no deforestation across its primary deforestation-linked commodities, with a target date of December 31st, 2025.

**Long-term targets:** Energy & Industry: Diageo Plc commits to reduce absolute scope 1 and 2 GHG emissions 90% by FY2040 from a FY2022 base year. Diageo Plc also commits to maintain a minimum of 90% absolute scope 1 and 2 GHG emissions from FY2040 through FY2050. Diageo Plc further commits to reduce absolute scope 3 GHG emissions 90% by FY2050 from a FY2022 base year.

FLAG: Diageo Plc commits to reduce absolute scope 1 and 3 FLAG GHG emissions 75% by FY2050 from a FY2022 base year.`,
  stock_price: JSON.stringify({
    price_details: { description: "Stock price (adj. close)", source: "alphavantage", currency: "USD", values: [
      { value: 168.5396, point_in_time_iso: "2021-05-28", point_in_time_raw: "2021-05-28" },
      { value: 167.1790, point_in_time_iso: "2021-06-30", point_in_time_raw: "2021-06-30" },
      { value: 172.8653, point_in_time_iso: "2021-07-30", point_in_time_raw: "2021-07-30" },
      { value: 169.7277, point_in_time_iso: "2021-08-31", point_in_time_raw: "2021-08-31" },
      { value: 170.5140, point_in_time_iso: "2021-09-30", point_in_time_raw: "2021-09-30" },
      { value: 176.4069, point_in_time_iso: "2021-10-29", point_in_time_raw: "2021-10-29" },
      { value: 179.4991, point_in_time_iso: "2021-11-30", point_in_time_raw: "2021-11-30" },
      { value: 194.4919, point_in_time_iso: "2021-12-31", point_in_time_raw: "2021-12-31" },
      { value: 180.1882, point_in_time_iso: "2022-01-31", point_in_time_raw: "2022-01-31" },
      { value: 177.8608, point_in_time_iso: "2022-02-28", point_in_time_raw: "2022-02-28" },
      { value: 180.9336, point_in_time_iso: "2022-03-31", point_in_time_raw: "2022-03-31" },
      { value: 176.9612, point_in_time_iso: "2022-04-29", point_in_time_raw: "2022-04-29" },
      { value: 166.5313, point_in_time_iso: "2022-05-31", point_in_time_raw: "2022-05-31" },
      { value: 155.0860, point_in_time_iso: "2022-06-30", point_in_time_raw: "2022-06-30" },
      { value: 170.4770, point_in_time_iso: "2022-07-29", point_in_time_raw: "2022-07-29" },
      { value: 159.0536, point_in_time_iso: "2022-08-31", point_in_time_raw: "2022-08-31" },
      { value: 153.1203, point_in_time_iso: "2022-09-30", point_in_time_raw: "2022-09-30" },
      { value: 150.5595, point_in_time_iso: "2022-10-31", point_in_time_raw: "2022-10-31" },
      { value: 168.2151, point_in_time_iso: "2022-11-30", point_in_time_raw: "2022-11-30" },
      { value: 160.6767, point_in_time_iso: "2022-12-30", point_in_time_raw: "2022-12-30" },
      { value: 159.4684, point_in_time_iso: "2023-01-31", point_in_time_raw: "2023-01-31" },
      { value: 156.0329, point_in_time_iso: "2023-02-28", point_in_time_raw: "2023-02-28" },
      { value: 164.7924, point_in_time_iso: "2023-03-31", point_in_time_raw: "2023-03-31" },
      { value: 168.7217, point_in_time_iso: "2023-04-28", point_in_time_raw: "2023-04-28" },
      { value: 152.8227, point_in_time_iso: "2023-05-31", point_in_time_raw: "2023-05-31" },
      { value: 157.7889, point_in_time_iso: "2023-06-30", point_in_time_raw: "2023-06-30" },
      { value: 160.0264, point_in_time_iso: "2023-07-31", point_in_time_raw: "2023-07-31" },
      { value: 152.9533, point_in_time_iso: "2023-08-31", point_in_time_raw: "2023-08-31" },
      { value: 137.7457, point_in_time_iso: "2023-09-29", point_in_time_raw: "2023-09-29" },
      { value: 141.7346, point_in_time_iso: "2023-10-31", point_in_time_raw: "2023-10-31" },
      { value: 129.7956, point_in_time_iso: "2023-11-30", point_in_time_raw: "2023-11-30" },
      { value: 134.4955, point_in_time_iso: "2023-12-29", point_in_time_raw: "2023-12-29" },
      { value: 133.2674, point_in_time_iso: "2024-01-31", point_in_time_raw: "2024-01-31" },
      { value: 140.7374, point_in_time_iso: "2024-02-29", point_in_time_raw: "2024-02-29" },
      { value: 138.8148, point_in_time_iso: "2024-03-28", point_in_time_raw: "2024-03-28" },
      { value: 128.9408, point_in_time_iso: "2024-04-30", point_in_time_raw: "2024-04-30" },
      { value: 126.0757, point_in_time_iso: "2024-05-31", point_in_time_raw: "2024-05-31" },
      { value: 117.6669, point_in_time_iso: "2024-06-28", point_in_time_raw: "2024-06-28" },
      { value: 116.4256, point_in_time_iso: "2024-07-31", point_in_time_raw: "2024-07-31" },
      { value: 124.3857, point_in_time_iso: "2024-08-30", point_in_time_raw: "2024-08-30" },
      { value: 133.4987, point_in_time_iso: "2024-09-30", point_in_time_raw: "2024-09-30" },
      { value: 118.1074, point_in_time_iso: "2024-10-31", point_in_time_raw: "2024-10-31" },
      { value: 113.5414, point_in_time_iso: "2024-11-29", point_in_time_raw: "2024-11-29" },
      { value: 120.9326, point_in_time_iso: "2024-12-31", point_in_time_raw: "2024-12-31" },
      { value: 114.1217, point_in_time_iso: "2025-01-31", point_in_time_raw: "2025-01-31" },
      { value: 105.0562, point_in_time_iso: "2025-02-28", point_in_time_raw: "2025-02-28" },
      { value: 101.1656, point_in_time_iso: "2025-03-31", point_in_time_raw: "2025-03-31" },
      { value: 108.1456, point_in_time_iso: "2025-04-30", point_in_time_raw: "2025-04-30" },
      { value: 105.2493, point_in_time_iso: "2025-05-30", point_in_time_raw: "2025-05-30" },
      { value: 97.3523,  point_in_time_iso: "2025-06-30", point_in_time_raw: "2025-06-30" },
      { value: 94.5622,  point_in_time_iso: "2025-07-31", point_in_time_raw: "2025-07-31" },
      { value: 107.9621, point_in_time_iso: "2025-08-29", point_in_time_raw: "2025-08-29" },
      { value: 92.1294,  point_in_time_iso: "2025-09-30", point_in_time_raw: "2025-09-30" },
      { value: 91.1990,  point_in_time_iso: "2025-10-31", point_in_time_raw: "2025-10-31" },
      { value: 90.7236,  point_in_time_iso: "2025-11-28", point_in_time_raw: "2025-11-28" },
      { value: 85.4353,  point_in_time_iso: "2025-12-31", point_in_time_raw: "2025-12-31" },
      { value: 91.9021,  point_in_time_iso: "2026-01-30", point_in_time_raw: "2026-01-30" },
      { value: 88.6340,  point_in_time_iso: "2026-02-27", point_in_time_raw: "2026-02-27" },
      { value: 73.7296,  point_in_time_iso: "2026-03-31", point_in_time_raw: "2026-03-31" },
      { value: 80.6500,  point_in_time_iso: "2026-04-30", point_in_time_raw: "2026-04-30" },
      { value: 83.7800,  point_in_time_iso: "2026-05-18", point_in_time_raw: "2026-05-18" }
    ]}
  }),
  // Manufacturing facilities table — used by the operations/footprint slide.
  // Verbatim from the live-flow facilities pin; structured as a pipe-delimited
  // table so the LLM can read region/location/focus/sustainability columns.
  facilities: `# Consolidated Manufacturing Facilities Table
| Country | Details | Focus | Sustainability |
|---|---|---|---|
| United States | Montgomery, Alabama (360,000 sq ft manufacturing & warehousing facility) | Multi-brand case production, bottling | State-of-the-art water & energy efficiency; site location reduces road travel and carbon emissions |
| Brazil | Ceará State (new consolidated facility) | Spirits (cachaça, rum) and other beverages | Solar power, water-treatment & reuse cutting water use up to 40% |
| Scotland | 31 Scotch whisky distilleries (nationwide) | Scotch whisky distillation | Increased use of liquid biofuel, energy-efficiency upgrades, company-wide water efficiency up 15.8% |
| Scotland | Cameronbridge (gin distillery) | Gin | — |
| Canada | Valleyfield, Quebec (gin distillery) | Gin (and whisky) | — |
| Ireland | Dublin – Roe & Co distillery | Irish whiskey | — |
| Republic of Ireland / Northern Ireland | Baileys production sites | Cream liqueur | — |
| United States (US Virgin Islands) | Rum distillation site | Rum | — |
| Australia | Bundaberg (rum distillery) | Bundaberg Rum | — |
| Australia | Bottling plant (ready-to-drink) | Ready-to-drink beverages | — |
| Thailand | Bottling plant (ready-to-drink) | Ready-to-drink beverages | — |
| Indonesia | Bali (manufacturing plant) | Spirits for Indonesian market | — |
| China | Chengdu (distilleries) | Baijiu / Chinese white spirits | — |
| Mexico | Tequila production sites | Tequila | — |
| Türkiye | Raki production sites | Raki | — |
| Canada | Gimli, Manitoba (bottling & distillation) | Whisky & other spirits | — |
| Canada | Valleyfield, Quebec (bottling & distillation) | Whisky & other spirits | — |
| Canada | Amherstburg, Ontario (bottling facility – slated to close) | Bottling | — |
| India | USL (55.94% share) – multiple plants | Indian whisky, rum, brandy, other spirits | — |`
};

// ---------------------------------------------------------------------------
// Example 13 — Diageo Slide 03 · Sustainability commitments & progress.
// Three bands: posture callout + SBTi-validated targets row · 3-column
// progress-bar grid · 3-column themed bullet detail (Climate / Nature /
// Water). All pins are existing Diageo content reused from DIAGEO_SOURCE_PINS.
// ---------------------------------------------------------------------------

const EXAMPLE_13_DSL = {
  version: 1, kind: "slide",
  pins: [
    { name: "supplier",                type: "entity", label: "Supplier",                          required: true  },
    { name: "sustainability_overview", type: "any",    label: "Sustainability report overview",    required: false },
    { name: "sustainability_details",  type: "any",    label: "Sustainability deep-dive details",  required: false }
  ],
  chrome: {
    header: {
      style: "dark_green_band",
      label: "03 · SUSTAINABILITY COMMITMENTS & PROGRESS",
      supplier_name: { role: "llm_gen", key: "supplier_name", pins: ["supplier"], prompt: "Company name as it appears in the source (e.g. 'Diageo'). Strip suffixes like 'Plc', 'Inc' unless they are inseparable from the brand." },
      chips: [
        { role: "llm_gen", key: "header_sector",      label: "Sector",       pins: ["supplier", "sustainability_details"], prompt: "2-3 word industry category. Prefer a SBTi-style sector string if disclosed (e.g. 'Food and Beverage Processing' → 'Food & beverage'). Otherwise infer from supplier narrative. null if not derivable." },
        { role: "llm_gen", key: "header_hq_listing",  label: "HQ · listing", pins: ["supplier", "sustainability_details"], prompt: "Format: '<HQ city>, <country> · <exchange>:<ticker>'. Use 'United Kingdom, Europe' as London/UK when only a country is given. Use 'Private' as the listing portion when there is no public ticker. null if no HQ at all." },
        { role: "llm_gen", key: "header_ownership",   label: "Ownership",    pins: ["supplier"], prompt: "One word from: Public, Private, PE-backed, State-owned, Subsidiary, Recapitalized. 'Public' is appropriate when the supplier text references shareholders, trading statements, or a listed entity even if no exchange is named." }
      ]
    },
    footer: {
      style: "dark_green_band",
      left: "Sense Platform · AI-assisted research · Supplement to, not sole basis for, decision-making",
      right: "Supplier Intelligence · May 2026"
    }
  },
  bands: [
    // Band 1: posture callout · SBTi-validated targets row (6 tiles)
    { id: "posture_band", proportion: 0.3, columns: [
      { id: "posture_col", proportion: 0.28, header: "Sustainability posture", blocks: [
        { type: "text_block", id: "sustainability_posture",
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: [
            { role: "llm_gen", key: "posture_headline", max_words: 8, cite: true, pins: ["sustainability_overview", "sustainability_details"], style: "italic_serif",
              prompt: "5-8 word italic-serif posture headline. Anchor on the maturity scale: 'Mature, validated, broadly on track.' (SBTi-validated S1+2+3, multi-year CDP A-List, meeting interim targets); 'Validated targets, mixed execution.' (validated but pace lagging on one+ scope); 'Targets set, early progress.' (committed but no validated SBTi yet); 'Early-stage commitments, partial disclosure.' (no SBTi, sparse data). End with a period." },
            { role: "llm_gen", key: "posture_body", max_words: 55, cite: true, pins: ["sustainability_overview", "sustainability_details"],
              prompt: "2-3 sentence assessment that names (i) SBTi validation status with the latest target year disclosed, (ii) any disclosure recognition (CDP grade, A-List streak, ISO coverage), (iii) one stand-out achievement (e.g. exceeded a 2025 target, hit a renewable-electricity %), and (iv) one explicit gap or area trailing pace. Skip an element only if the source genuinely lacks it. Don't invent metrics — if a number isn't in the source, characterize qualitatively." }
          ]
        }
      ]},
      { id: "sbti_col", proportion: 0.72, header: "SBTi-validated targets", blocks: [
        { type: "tile_row", id: "sbti_targets", cols: 6,
          pins: ["sustainability_overview", "sustainability_details"],
          null_fallback: { display: "N/A", color: "#999" },
          top_border: { color: "#2D8659", width: 4 },
          tiles: [
            // The `label.extract` prompts ask for a single string that the
            // tile renders across two lines. The first line is a fixed scope
            // tag (bolded via markdown); the second is the company-specific
            // target value. Use a real linefeed character between the two —
            // not the two-character sequence backslash+n.
            { value: { extract: "sbti_s12_year", cite: true,
                       prompt: "Year of the near-term Scope 1+2 1.5°C-aligned absolute-reduction commitment (e.g. 2030). Integer year only. null if no such SBTi commitment is disclosed." },
              label: { extract: "sbti_s12_target", cite: true,
                       prompt: "Two-line label. Line 1: literal '**S1+2 · 1.5°C**'. Line 2: the absolute target value as disclosed (e.g. '-50% absolute'). Separate the two lines with an actual newline character (LF). Example output: '**S1+2 · 1.5°C**' followed by newline followed by '-50% absolute'." } },
            { value: { extract: "sbti_s3_year", cite: true,
                       prompt: "Year of the near-term Scope 3 commitment (e.g. 2030). Integer year only. null if no Scope 3 commitment is disclosed." },
              label: { extract: "sbti_s3_target", cite: true,
                       prompt: "Two-line label. Line 1: literal '**SCOPE 3**'. Line 2: the absolute target value (e.g. '-25% absolute'). Separate with a real newline character." } },
            { value: { extract: "sbti_re100_year", cite: true,
                       prompt: "Year of the RE100 / 100% renewable-electricity commitment (e.g. 2030). Integer year only. null if no renewable-electricity commitment is disclosed." },
              label: { extract: "sbti_re100_target", cite: true,
                       prompt: "Two-line label. Line 1: literal '**RE100**'. Line 2: the target descriptor (e.g. '100% renewable elec'). Separate with a real newline character." } },
            { value: { extract: "sbti_flag_year", cite: true,
                       prompt: "Year of the near-term FLAG (forest/land/agriculture) commitment (e.g. 2025 or 2030). Integer year only. null if no FLAG commitment is disclosed." },
              label: { extract: "sbti_flag_target", cite: true,
                       prompt: "Two-line label. Line 1: literal '**FLAG · S1+3**'. Line 2: the FLAG target descriptor with deforestation pledge if present (e.g. '-30.3% · no deforestation'). Separate with a real newline character." } },
            { value: { extract: "sbti_long_year", cite: true,
                       prompt: "Year of the long-term Scope 1+2 commitment (typically 2040). Integer year only. null if not committed." },
              label: { extract: "sbti_long_target", cite: true,
                       prompt: "Two-line label. Line 1: literal '**S1+2 LONG-TERM**'. Line 2: absolute target (e.g. '-90% absolute'). Separate with a real newline character." } },
            { value: { extract: "sbti_netzero_year", cite: true,
                       prompt: "Net-zero target year (typically 2050). Integer year only. null if no net-zero commitment is disclosed." },
              label: { extract: "sbti_netzero_target", cite: true,
                       prompt: "Two-line label. Line 1: literal '**NET-ZERO**'. Line 2: scope descriptor (e.g. 'Full value chain' or 'Operations only'). Separate with a real newline character." } }
          ]
        }
      ]}
    ]},

    // Band 2: progress bars — 3 columns × 2 bars. The "Progress vs targets"
    // label is rendered as the first flow child in column 1 (and as an empty
    // section_header in columns 2/3) so all three columns reserve the same
    // top offset and the first bars line up across the band. Each progress
    // chart declares `value` / `display_value` LLM slots plus `cite: true`
    // so the renderer can show an evidence tooltip on the bar's label/value.
    { id: "progress_band", proportion: 0.18, columns: [
      { id: "prog_col_1", proportion: 0.33,
        pins: ["sustainability_overview", "sustainability_details"], blocks: [
          { type: "flow", direction: "vertical", gap: "spread", children: [
            { type: "section_header", text: "Progress vs targets", proportion: 0.18 },
            { type: "chart", kind: "progress", id: "prog_renewable", bar_height: 5, label: "Renewable electricity",
              color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
              value:         { role: "llm_gen", key: "renewable_pct",     prompt: "% renewable electricity currently achieved (0-100). null if not disclosed." },
              display_value: { role: "llm_gen", key: "renewable_display", prompt: "Short label ≤6 words, e.g. '86.7% / 100% target' or '86.7% renewable'. null if not disclosed." }
            },
            { type: "chart", kind: "progress", id: "prog_recycled", bar_height: 5, label: "Recycled / circular content",
              color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
              value:         { role: "llm_gen", key: "recycled_pct",     prompt: "Recycled / circular content % achieved (e.g. 43 for 43% recycled PET). null if not disclosed." },
              display_value: { role: "llm_gen", key: "recycled_display", prompt: "Short label ≤6 words, e.g. '43% PET / 50% target' or '43% recycled PET'. null if not disclosed." }
            }
          ]}
      ]},
      { id: "prog_col_2", proportion: 0.33,
        pins: ["sustainability_overview", "sustainability_details"], blocks: [
          { type: "flow", direction: "vertical", gap: "spread", children: [
            { type: "section_header", text: " ", proportion: 0.18 },
            { type: "chart", kind: "progress", id: "prog_scope12", bar_height: 5, label: "Scope 1+2 reduction",
              color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
              value:         { role: "llm_gen", key: "s12_reduction_pct", prompt: "% Scope 1+2 reduction ACHIEVED to-date against base year (e.g. 5.4, 11). null if only a target is disclosed." },
              display_value: { role: "llm_gen", key: "s12_display",       prompt: "Short label ≤6 words, e.g. '~11% / 50% by 2030' or '-5.4% S1+2 YoY'. null if not disclosed." }
            },
            { type: "chart", kind: "progress", id: "prog_water", bar_height: 5, label: "Water efficiency",
              color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
              value:         { role: "llm_gen", key: "water_efficiency_pct", prompt: "% water-efficiency improvement (stressed-basin or total) achieved. null if not disclosed." },
              display_value: { role: "llm_gen", key: "water_display",       prompt: "Short label ≤6 words, e.g. '+40% stressed basins' or '+20.6% efficiency'. null if not disclosed." }
            }
          ]}
      ]},
      { id: "prog_col_3", proportion: 0.33,
        pins: ["sustainability_overview", "sustainability_details"], blocks: [
          { type: "flow", direction: "vertical", gap: "spread", children: [
            { type: "section_header", text: " ", proportion: 0.18 },
            { type: "chart", kind: "progress", id: "prog_scope3", bar_height: 5, label: "Scope 3 target progress",
              color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
              value:         { role: "llm_gen", key: "s3_progress_pct", prompt: "% Scope 3 reduction achieved against base year, or null if only a target is disclosed (no current progress)." },
              display_value: { role: "llm_gen", key: "s3_display",      prompt: "Short label ≤6 words. If only a target exists: 'Set · -25% by 2030'. null if no target." }
            },
            { type: "chart", kind: "progress", id: "prog_waste", bar_height: 5, label: "Waste to landfill reduction",
              color_rule: { on_track: "#2D8659", behind: "#BA7517", na: "#888780" }, token_fill_pct: 12, cite: true,
              value:         { role: "llm_gen", key: "waste_reduction_pct", prompt: "% waste-to-landfill reduction achieved (e.g. 35.5). null if not disclosed." },
              display_value: { role: "llm_gen", key: "waste_display",      prompt: "Short label ≤6 words, e.g. '-35.5% waste to landfill'. null if not disclosed." }
            }
          ]}
      ]}
    ]},

    // Band 3: three themed bullet lists — Climate / Nature / Water
    { id: "details_band", proportion: 0.48, columns: [
      { id: "climate_col", proportion: 0.33, header: "Climate", blocks: [
        { type: "bullet_list", id: "climate_bullets", item_limit: 4,
          pins: ["sustainability_details"], evidence_in_tooltip: true,
          card_background: "#F5F1E8",
          dot_color_map: { green: "#2D8659", amber: "#BA7517", red: "#C4463A" },
          priority_hint: "Return up to 4 distinct climate / decarbonisation initiatives the source explicitly mentions. Candidate topics (not a checklist — only include each if the source covers it): renewable-electricity progress / RE100 milestones, carbon-neutral or electrified sites, biomass / biofuel substitution, supplier engagement on Scope 3, low-carbon packaging, science-based supply-chain targets. Most material first. Return fewer than 4 items if the source doesn't support 4 — do NOT pad. Skip an initiative entirely if it duplicates a progress-bar metric already shown above (renewable %, Scope 1+2 %, recycled %, water efficiency, waste).",
          item_template: {
            status:   { role: "llm_gen", key: "status",
                        prompt: "RAG dot color. 'green' = measurable improvement (decreased emissions/use, target met/exceeded) OR a substantive forward commitment with concrete scale, named partner, AND a hard deadline (e.g. a £5M peatland programme, a partner-backed net-zero-by-2030 collaboration). 'amber' = vague aspiration without scale or deadline, OR pace clearly trailing the stated target. 'red' = regression: rising emissions/water/waste use, a missed deadline, or a deterioration vs prior year. When in doubt between green and amber, prefer green if there's a real metric, partner, or date attached." },
            headline: { role: "llm_gen", key: "headline", max_words: 26,
                        prompt: "Format: '**<2-5 word bold lead label>** <one-sentence detail with specific metrics, partners, and date/region anchors where stated>.' The bold lead is a label for the initiative, not the column theme — do NOT lead with 'Climate'. Don't repeat the lead label in the description sentence." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "Verbatim sentence(s) from sustainability_details supporting the headline — quote exactly, do not paraphrase. Shown only on hover as the citation." }
          }
        }
      ]},
      { id: "nature_col", proportion: 0.33, header: "Nature & biodiversity", blocks: [
        { type: "bullet_list", id: "nature_bullets", item_limit: 4,
          pins: ["sustainability_details"], evidence_in_tooltip: true,
          card_background: "#F5F1E8",
          dot_color_map: { green: "#2D8659", amber: "#BA7517", red: "#C4463A" },
          priority_hint: "Return up to 4 distinct nature / biodiversity initiatives the source mentions. Candidate topics: peatland or wetland restoration, tree-planting pledges (acres / count / species), regenerative agriculture pilots, no-deforestation commitments with target dates, coalition / NGO memberships (e.g. OP2B, IUCN, Nature Conservancy). Most material first. Return fewer than 4 if the source doesn't support 4 — do NOT pad.",
          item_template: {
            status:   { role: "llm_gen", key: "status",
                        prompt: "RAG dot color. 'green' = active restoration / habitat gain with metrics OR a substantive pledge with concrete scale, named partner AND a hard deadline (e.g. '2.5 million trees with American Forests', 'no deforestation by Dec 2025'). 'amber' = vague pledge with no scale or deadline, OR a pilot without disclosed outcomes after a year+. 'red' = habitat loss, deforestation, or a missed deforestation deadline. Prefer green when a real metric, partner, or date is attached." },
            headline: { role: "llm_gen", key: "headline", max_words: 26,
                        prompt: "Format: '**<2-5 word bold lead label>** <one-sentence detail with specific metrics, partners, and date/region anchors where stated>.' Lead label names the initiative (e.g. 'Peatland restoration', 'Tree planting'), not the column theme. Don't repeat the lead label in the description." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "Verbatim sentence(s) from sustainability_details supporting the headline. Hover-only citation." }
          }
        }
      ]},
      { id: "water_col", proportion: 0.33, header: "Water", blocks: [
        { type: "bullet_list", id: "water_bullets", item_limit: 4,
          pins: ["sustainability_overview", "sustainability_details"], evidence_in_tooltip: true,
          card_background: "#F5F1E8",
          dot_color_map: { green: "#2D8659", amber: "#BA7517", red: "#C4463A" },
          priority_hint: "Return up to 4 distinct water-stewardship initiatives the source mentions. Candidate topics: CDP Water disclosure score / A-List streak, water-efficiency gains in stressed basins, WASH projects (count, countries, people reached), replenishment volumes vs targets, coalition memberships (Water Resilience Coalition, Alliance for Water Stewardship). Most material first. Return fewer than 4 if the source doesn't support 4.",
          item_template: {
            status:   { role: "llm_gen", key: "status",
                        prompt: "RAG dot color. 'green' = improved water efficiency, reduced withdrawal, strong CDP rating (A or A-List, multi-year streak), replenishment surplus OR a substantive water-stewardship programme with concrete scale, named partners and a hard deadline (e.g. '23 WASH projects across 9 countries reaching 128k people'). 'amber' = stable performance, vague water pledge with no scale/deadline. 'red' = increased water use / withdrawal, missed targets, or deteriorating water-stressed-basin coverage without mitigation. Prefer green when a real metric, partner, or date is attached." },
            headline: { role: "llm_gen", key: "headline", max_words: 26,
                        prompt: "Format: '**<2-5 word bold lead label>** <one-sentence detail with specific metrics and a date/region anchor where stated>.' Lead label names the initiative (e.g. 'CDP Water rating', 'WASH projects'), not the column theme. Don't repeat the lead label in the description." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "Verbatim sentence(s) from the source supporting the headline. Hover-only citation." }
          }
        }
      ]}
    ]},

    // Band 4: shared RAG legend underneath all three themed columns. The
    // colors match dot_color_map on the three bullet_lists above; labels
    // mirror the climate/nature/water status prompt criteria.
    { id: "legend_band", proportion: 0.04, columns: [
      { id: "legend_col", proportion: 1, blocks: [
        { type: "legend", items: [
          { color: "#2D8659", label: "Measurable progress or substantive commitment" },
          { color: "#BA7517", label: "Pledged / pace lagging" },
          { color: "#C4463A", label: "Regression / target missed" }
        ]}
      ]}
    ]}
  ]
};

// ---------------------------------------------------------------------------
// Example 14 — Diageo Slide 04 · Operations, footprint & regional focus.
// Top band: footprint posture callout · world map shaded by facility country.
// Bottom band: manufacturing-sites list (region/location/focus/sustainability)
// · regional sustainability-investment bullets (tagged by region code).
// Adds a new `facilities` pin alongside the existing supplier and
// sustainability pins.
// ---------------------------------------------------------------------------

const EXAMPLE_14_DSL = {
  version: 1, kind: "slide",
  pins: [
    { name: "supplier",                type: "entity", label: "Supplier",                          required: true  },
    { name: "sustainability_overview", type: "any",    label: "Sustainability report overview",    required: false },
    { name: "sustainability_details",  type: "any",    label: "Sustainability deep-dive details",  required: false },
    { name: "facilities",              type: "any",    label: "Manufacturing facilities table",    required: false }
  ],
  chrome: {
    header: {
      style: "dark_green_band",
      label: "04 · OPERATIONS, FOOTPRINT & REGIONAL FOCUS",
      supplier_name: { role: "llm_gen", key: "supplier_name", pins: ["supplier"], prompt: "Company name as it appears in the source (e.g. 'Diageo'). Strip suffixes like 'Plc' unless inseparable from the brand." },
      chips: [
        { role: "llm_gen", key: "header_sector",      label: "Sector",       pins: ["supplier", "sustainability_details"], prompt: "2-3 word industry category (e.g. 'Food & beverage')." },
        { role: "llm_gen", key: "header_hq_listing",  label: "HQ · listing", pins: ["supplier", "sustainability_details"], prompt: "Format: '<HQ city>, <country> · <exchange>:<ticker>'. Use 'Private' as the listing portion if no public ticker. null if no HQ at all." },
        { role: "llm_gen", key: "header_ownership",   label: "Ownership",    pins: ["supplier"], prompt: "One word: Public, Private, PE-backed, State-owned, Subsidiary, Recapitalized." }
      ]
    },
    footer: {
      style: "dark_green_band",
      left: "Sense Platform · AI-assisted research · Supplement to, not sole basis for, decision-making",
      right: "Supplier Intelligence · May 2026"
    }
  },
  bands: [
    // Band 1: footprint posture callout (left) · world map (right)
    { id: "footprint_band", proportion: 0.36, columns: [
      { id: "posture_col", proportion: 0.3, header: "Footprint posture", blocks: [
        { type: "text_block", id: "footprint_posture",
          slot_layout: "cards",
          accent_bar: { color: "#1F3A2E", width: 3 },
          card_background: "#F5F1E8",
          slots: [
            { role: "llm_gen", key: "posture_headline", max_words: 8, cite: true, pins: ["supplier", "sustainability_details", "facilities"], style: "italic_serif",
              prompt: "5-8 word italic-serif headline characterising the manufacturing footprint. Examples: 'Global; selective expansion in core markets.', 'Concentrated North-American base.', 'Asia-led growth, Europe-anchored heritage.' End with a period." },
            { role: "llm_gen", key: "posture_body", max_words: 55, cite: true, pins: ["supplier", "sustainability_details", "facilities"],
              prompt: "2-3 sentences. Cover: (i) total scale (sites and countries — use '+' suffix when an aggregate row like '31 distilleries' makes the exact count fuzzy), (ii) where the footprint is anchored (which country/region has the most sites), (iii) any recent openings/closures explicitly stated, (iv) where regional sustainability investment is concentrated. Skip an element if the source doesn't support it." }
          ]
        }
      ]},
      { id: "map_col", proportion: 0.7, header: "Global manufacturing footprint", blocks: [
        { type: "map", id: "world_map",
          pins: ["facilities"],
          shading: { active: "#1F3A2E", inactive: "#EDE8DD" }
        }
      ]}
    ]},

    // Band 2: manufacturing-sites list (left) · regional sustainability
    // investment bullets (right)
    { id: "ops_band", proportion: 0.64, columns: [
      { id: "sites_col", proportion: 0.55, header: "Manufacturing sites by region", blocks: [
        { type: "bullet_list", id: "manufacturing_sites", item_limit: 10,
          pins: ["facilities"], evidence_in_tooltip: true,
          card_background: "#F5F1E8",
          tag_styles: {
            "N. America":  { bg: "#D8E2EF", text: "#2C4A6E" },
            "Latin America": { bg: "#EFE0D8", text: "#8A4A2A" },
            "Europe":      { bg: "#DCE7DF", text: "#1F3A2E" },
            "Africa":      { bg: "#E8E2D0", text: "#5A6F62" },
            "Asia-Pacific": { bg: "#F2D7CE", text: "#B85450" }
          },
          priority_hint: "Return up to 10 distinct site/region entries from the facilities table. Group rows for the same region under the same tag. Aggregate rows (e.g. '31 Scotch whisky distilleries') count as ONE entry — preserve the count in the headline. Order entries by region (Europe, N. America, Latin America, Africa, Asia-Pacific) then by prominence within each region.",
          item_template: {
            tag:      { role: "llm_gen", key: "tag",
                        prompt: "Region tag, one of: 'Europe', 'N. America', 'Latin America', 'Africa', 'Asia-Pacific'. Pick by the country column: UK/Ireland/Türkiye → Europe; US/Canada/Mexico → N. America (Mexico tequila sites count as N. America when the table lists them under Mexico; otherwise Latin America); Brazil/other-LatAm → Latin America; Kenya/Uganda/Nigeria/SA → Africa; India/China/Indonesia/Thailand/Australia/NZ → Asia-Pacific." },
            headline: { role: "llm_gen", key: "headline", max_words: 18,
                        prompt: "Format: '**<location · country>** · <focus>'. Location stays first to anchor the row. Example: '**Scotland (31 sites)** · Scotch whisky'. Use markdown bold for the location portion only." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "Sustainability column for this site/row from the facilities table — quote verbatim. If the source's Sustainability column is '—' (no notes), return null. Shown only on hover." }
          }
        }
      ]},
      { id: "regional_col", proportion: 0.45, header: "Regional focus · sustainability investments", blocks: [
        { type: "bullet_list", id: "regional_investments", item_limit: 5,
          pins: ["sustainability_details"], evidence_in_tooltip: true,
          card_background: "#F5F1E8",
          tag_styles: {
            "N.A.":  { bg: "#D8E2EF", text: "#2C4A6E" },
            "EUR":   { bg: "#DCE7DF", text: "#1F3A2E" },
            "LAT":   { bg: "#EFE0D8", text: "#8A4A2A" },
            "AFR":   { bg: "#E8E2D0", text: "#5A6F62" },
            "APAC":  { bg: "#F2D7CE", text: "#B85450" }
          },
          priority_hint: "Return up to 5 distinct regional sustainability initiatives from the 'Regional / Country Focus' section of sustainability_details. One per region where possible. Drop a region if the source has no investment to attribute.",
          item_template: {
            tag:      { role: "llm_gen", key: "tag",
                        prompt: "Region tag, one of: 'N.A.', 'EUR', 'LAT', 'AFR', 'APAC'. Use 'N.A.' for US/Canada/Mexico north-of-LatAm projects; 'EUR' for UK/Ireland/EU; 'LAT' for Mexico-tequila/Brazil/other LatAm; 'AFR' for Africa; 'APAC' for India/China/SE Asia/Oceania." },
            headline: { role: "llm_gen", key: "headline", max_words: 26,
                        prompt: "Format: '**<2-5 word bold lead label naming the initiative>** <one-sentence detail with the specific country/region, partner if named, and headline metric or date>.' Don't restate the region tag in the lead label." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "Verbatim sentence(s) from sustainability_details supporting the initiative. Hover-only citation." }
          }
        }
      ]}
    ]}
  ]
};

// ---------------------------------------------------------------------------
// Example 15 — JTBD / ODI interview-analysis dashboard (blood center).
// A different domain + visual treatment: dark-red chrome, stacked header,
// KPI tiles, and three columns (job map · outcome statements · contextual &
// chain jobs). Four source pins map to the analysis pipeline phases:
//   phase_1 — functional-job overview (executor/beneficiary/tools/evidence/Qs)
//   phase_2 — job-map signals (one record per step/signal)
//   phase_3 — outcome statements (one record per outcome)
//   phase_4 — contextual & chain jobs (emotional/social/etc.)
// ---------------------------------------------------------------------------

const BLOOD_CENTER_PHASE_1 = [
`# Overview
Supply researchers with specific blood component collections for their studies
# Executor
Blood center's director of special collections
# Beneficiary
Researchers conducting blood component-based studies
# Rationale
Supplying researchers with specific blood component collections for their studies encapsulates the enduring goal of matching demand for specialized donor materials with execution. It transcends a single task or workflow by defining a continuous service model that integrates outreach, procurement, customization, and delivery.
# Stability Test
Providing tailored blood component collections to researchers remains a consistent need for studies requiring human-derived materials. The core goal is tool-agnostic, focusing on supply-demand alignment. References to specific machines, cones, or cold email outreach reflect current practices, but the job persists independent of collection methods, platforms, or organizational scale.
# Tools And Solutions
| Tool / Solution | Context Used | What It Is Used For | Where It Falls Short |
|----|----|----|----|
| Director of special collections | Managing researcher inquiries and collections coordination | Streamlines communication, donor recruitment, and logistics | Single-person capacity limits scalability |
| Cones from apheresis | Selling to researchers who need small volumes | Provides affordable, targeted samples | Does not meet needs for full-volume packs |
| Leukapheresis machine | Collecting full-volume leukapheresis packs | Enables advanced therapeutic collections | No consistent customer demand for full packs |
| Cold email outreach and word-of-mouth | Identifying potential research partners | Generates leads for specialized collections | Low awareness and inconsistent engagement |
# Open Questions
- What specific blood component types and volumes do researchers most frequently require?
- What is the current and potential scalable capacity for specialized collections beyond a single coordinator?
- Are partnerships with other blood centers viable for expanding geographic reach and delivery speed?
- What is the timeline and resource impact for obtaining necessary accreditation to support advanced therapeutic collections?`
];

// phase_2 — job-map signals. Steps appearing here: Define(3), Locate(2),
// Prepare(2), Confirm(1), Execute(1), Monitor(1), Modify(1). Conclude absent.
const BLOOD_CENTER_PHASE_2 = [
`# Step\nDefine\n# Signal Type\nConstraint\n# Job-Map Signal\nScope initiatives based on limited resources to ensure projects succeed\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nDefine\n# Signal Type\nConcern\n# Job-Map Signal\nAvoid investing heavily before confirming market demand to prevent unused capacity\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nDefine\n# Signal Type\nUncertainty\n# Job-Map Signal\nDetermine whether market success in existing services will replicate in advanced collections\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nMedium\n# Validation Flags\npossible inference`,
`# Step\nLocate\n# Signal Type\nChallenge\n# Job-Map Signal\nIdentify and reach unknown researchers who are unaware of available collection services\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nMedium\n# Validation Flags`,
`# Step\nLocate\n# Signal Type\nInformation Need\n# Job-Map Signal\nDetermine which specific researchers are conducting studies requiring blood components\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nPrepare\n# Signal Type\nObjective\n# Job-Map Signal\nObtain accreditation and develop SOPs to qualify for advanced research collections\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nPrepare\n# Signal Type\nObjective\n# Job-Map Signal\nCentralize researcher engagement and ordering through a dedicated coordinator\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nConfirm\n# Signal Type\nUncertainty\n# Job-Map Signal\nVerify if there is sufficient demand to justify lucopac collections\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nExecute\n# Signal Type\nChallenge\n# Job-Map Signal\nFulfill high-volume orders with existing single-apheresis equipment\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nMonitor\n# Signal Type\nSuccess Criterion\n# Job-Map Signal\nVerify that streamlined ordering leads to increased research orders\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nHigh\n# Validation Flags`,
`# Step\nModify\n# Signal Type\nChallenge\n# Job-Map Signal\nAdjust pricing to stay competitive and attract research customers\n# Speaker\nBenjamin Prijatel\n# Evidence Strength\nLow\n# Validation Flags\nweak grounding`
];

// phase_3 — outcome statements (15). Outcome Type Pain-Driven or Aspiration-Driven.
const BLOOD_CENTER_PHASE_3 = [
`# Job Step\nDefine\n# Outcome Statement\nMinimize the likelihood of selecting initiatives that exceed resource constraints\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nDefine\n# Outcome Statement\nMinimize the likelihood of unutilized collection capacity\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nDefine\n# Outcome Statement\nMinimize the difference between anticipated and actual demand for advanced collections\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nLocate\n# Outcome Statement\nIncrease the ability to identify researchers in need of blood collection services\n# Outcome Type\nAspiration-Driven\n# Validation Flags`,
`# Job Step\nLocate\n# Outcome Statement\nMinimize the time it takes to locate potential research customers\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nLocate\n# Outcome Statement\nIncrease the ability to identify active research studies needing specialized blood collections\n# Outcome Type\nAspiration-Driven\n# Validation Flags`,
`# Job Step\nPrepare\n# Outcome Statement\nMinimize the time it takes to meet qualification requirements for advanced research collections\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nPrepare\n# Outcome Statement\nMinimize the effort required to meet qualification requirements for advanced research collections\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nPrepare\n# Outcome Statement\nMinimize the effort required to manage communications with research customers\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nConfirm\n# Outcome Statement\nMinimize the likelihood of producing unrequested lucopac collections\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nExecute\n# Outcome Statement\nMinimize the time it takes to fulfill high-volume research orders\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nExecute\n# Outcome Statement\nMinimize the likelihood of capacity bottlenecks during order fulfillment\n# Outcome Type\nPain-Driven\n# Validation Flags`,
`# Job Step\nMonitor\n# Outcome Statement\nIncrease the ability to measure the impact of ordering processes on research order volume\n# Outcome Type\nAspiration-Driven\n# Validation Flags`,
`# Job Step\nModify\n# Outcome Statement\nMinimize the effort required to update pricing to match market benchmarks\n# Outcome Type\nPain-Driven\n# Validation Flags\nWeak grounding`,
`# Job Step\nModify\n# Outcome Statement\nMinimize the likelihood of losing research customers\n# Outcome Type\nPain-Driven\n# Validation Flags\nWeak grounding`
];

// phase_4 — contextual & chain jobs (5).
const BLOOD_CENTER_PHASE_4 = [
`# Category\nEmotional Job\n# Job Statement\nFeel confident that investments in advanced therapeutic capabilities neither lag behind industry nor risk wasted resources.\n# Why This Matters\nExecutive decisions on new services require confidence that limited resources won't be wasted on unprofitable capabilities.\n# Evidence\n"they spent tons of money on something that they didn't have a single customer for and weren't ready for"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nSocial Job\n# Job Statement\nBe perceived by hospital partners as an indispensable, comprehensive service provider across testing, collections, and research support.\n# Why This Matters\nCreating multiple service touchpoints secures long-term partnerships and discourages contract switching.\n# Evidence\n"I'm a huge believer in having as many hooks as possible into your customers"\n# Evidence Strength\nMedium\n# Validation Flags\nNo significant issues`,
`# Category\nPurchasing / Choosing\n# Job Statement\nEvaluate and decide when to pursue FACT accreditation for advanced therapeutic collections.\n# Why This Matters\nAccreditation is necessary to qualify for and participate in cell and gene therapy research supply.\n# Evidence\n"we know that we're going to eventually...we're going to have to get FACT accredited"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nLearning\n# Job Statement\nMonitor and learn from industry investment trends and peer initiatives to time service development.\n# Why This Matters\nStaying informed ensures timely entry into sustainable advanced therapy services.\n# Evidence\n"we're really watching the industry...keeping a close eye on what's panning out"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nInterfacing\n# Job Statement\nConsolidate researcher communications through a single dedicated liaison to streamline ordering and billing.\n# Why This Matters\nSimplified interactions improve customer experience and drive increased sales.\n# Evidence\n"having one person that is the contact for any of this has really helped...it's streamlined things"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`
];

// Pins are passed as ARRAYS (one element per record) so the crystallizer sees
// explicit item boundaries — exact counts, no reliance on a "---" text
// separator. The crystallizer also still accepts "---"-joined strings (which
// is what the live flow currently produces).
const BLOOD_CENTER_SOURCE_PINS = {
  title: "Supply researchers with specific blood component collections for their studies",
  phase_1: BLOOD_CENTER_PHASE_1,
  phase_2: BLOOD_CENTER_PHASE_2,
  phase_3: BLOOD_CENTER_PHASE_3,
  phase_4: BLOOD_CENTER_PHASE_4
};

const EXAMPLE_15_DSL = {
  version: 1, kind: "slide",
  pins: [
    { name: "title",   type: "string", label: "Slide title",                  required: true  },
    { name: "phase_1", type: "any",    label: "Functional-job overview",      required: true  },
    { name: "phase_2", type: "any",    label: "Job-map signals",              required: true  },
    { name: "phase_3", type: "any",    label: "Outcome statements",           required: true  },
    { name: "phase_4", type: "any",    label: "Contextual & chain jobs",      required: false }
  ],
  chrome: {
    header: {
      band_color: "#6E1E2B",
      layout: "stacked",
      label: "FUNCTIONAL JOB · JTBD / ODI INTERVIEW ANALYSIS",
      supplier_name: { role: "llm_gen", key: "job_title", pins: ["title"], prompt: "Return the supplied title verbatim — do not paraphrase, summarise, or add quotation marks. The pin contains the exact string to use as the main header title." },
      chips: [
        { role: "llm_gen", key: "chip_executor",    label: "Executor",    pins: ["phase_1", "phase_2"], prompt: "The executing organisation/role (e.g. 'Shepherd Community Blood Center'). Prefer the named organisation over the generic role if both appear." },
        { role: "llm_gen", key: "chip_beneficiary", label: "Beneficiary", pins: ["phase_1"], prompt: "The beneficiary, ≤6 words (e.g. 'Researchers running blood-component studies')." },
        { role: "llm_gen", key: "chip_source",      label: "Source",      pins: ["phase_2", "phase_4"], prompt: "The interview speaker/source name (e.g. 'Benjamin Prijatel')." }
      ]
    },
    footer: {
      band_color: "#6E1E2B",
      left: "Sense Platform · JTBD / ODI interview analysis · Supplement to, not sole basis for, decision-making",
      right: "Stability test: PASS (tool-agnostic)"
    }
  },
  bands: [
    // Band 1: functional-job summary (left) + KPI tiles (right).
    { id: "top_band", proportion: 0.2, columns: [
      { id: "summary_col", proportion: 0.30, header: "What this job is", blocks: [
        { type: "text_block", id: "job_summary",
          background: "#F7F2F2",
          slots: [
            { role: "llm_gen", key: "summary", max_words: 45, cite: true, pins: ["phase_1"],
              prompt: "2-3 sentence plain-language summary of the functional job: what it is, who executes it, who benefits, and why it endures (tool-agnostic). Draw on the Overview, Rationale and Stability Test sections." }
          ]
        }
      ]},
      { id: "kpi_col", proportion: 0.70, header: " ", blocks: [
        // KPI counts are DERIVED IN CODE (derive: true) from the source
        // records, not generated — so they stay coherent with the lists they
        // summarize and add no LLM cost. Each tile's value + label come from
        // small aggregation ops over the parsed pin records.
        { type: "tile_row", id: "kpi_tiles", cols: 5, derive: true,
          pins: ["phase_1", "phase_2", "phase_3", "phase_4"],
          top_border: { color: "#6E1E2B", width: 4 },
          null_fallback: { display: "—", color: "#999" },
          tiles: [
            { value: { extract: "kpi_steps",
                       derive: { op: "coverage", pin: "phase_2", field: "Step",
                                 categories: ["Define", "Locate", "Prepare", "Confirm", "Execute", "Monitor", "Modify", "Conclude"] } },
              label: { extract: "kpi_steps_sub", title: "Job steps mapped", template: "{gap} gap · {missing}" } },
            { value: { extract: "kpi_outcomes", derive: { op: "count", pin: "phase_3" } },
              label: { extract: "kpi_outcomes_sub", title: "Outcome statements",
                       derive: { op: "distinct", pin: "phase_3", field: "Job Step" }, template: "across {value} steps" } },
            { value: { extract: "kpi_pain_aspiration",
                       derive: { op: "ratio", pin: "phase_3", field: "Outcome Type", a: "Pain", b: "Aspiration" } },
              label: { extract: "kpi_pain_sub", title: "Pain : Aspiration", template: "split" } },
            { value: { extract: "kpi_contextual", derive: { op: "count", pin: "phase_4" } },
              label: { extract: "kpi_contextual_sub", title: "Contextual jobs",
                       derive: { op: "first_last", pin: "phase_4", field: "Category", word: true, lower: true }, template: "{first} → {last}" } },
            { value: { extract: "kpi_flags",
                       derive: { op: "count_where", pins: ["phase_2", "phase_3", "phase_4"], field: "Validation Flags" } },
              label: { extract: "kpi_flags_sub", title: "Flags to review",
                       derive: { op: "tally", pins: ["phase_2", "phase_3", "phase_4"], field: "Validation Flags",
                                 buckets: { inference: "inference", weak: "weak" } }, template: "{inference} inference · {weak} weak" } }
          ]
        }
      ]}
    ]},

    // Band 2: outcomes (expanded, 2-column list) + contextual & chain jobs.
    { id: "main_band", proportion: 0.74, columns: [
      { id: "outcomes_col", proportion: 0.64, header: "Outcome statements · 15", blocks: [
        // Signal-distribution heatmap sits above the outcomes list. Computed
        // in code (counts '# Step' values across phase_2), not crystallized.
        { type: "chart", kind: "heatmap", id: "step_heatmap", proportion: 0.12,
          pins: ["phase_2"],
          label: "Signals by step",
          heat_color: "#6E1E2B",
          count_field: "Step",
          categories: ["Define", "Locate", "Prepare", "Confirm", "Execute", "Monitor", "Modify", "Conclude"],
          category_labels: ["Def", "Loc", "Prep", "Conf", "Exec", "Mon", "Mod", "Conc"]
        },
        { type: "bullet_list", id: "outcomes", item_limit: 16, proportion: 0.88, columns: 2,
          pins: ["phase_3"], evidence_in_tooltip: true,
          card_background: "#FAF7F7",
          dot_color_map: { pain: "#9B2D30", aspiration: "#2C4A6E" },
          tag_styles: {
            DEFINE:  { bg: "#E7DADC", text: "#6E1E2B" },
            LOCATE:  { bg: "#E7DADC", text: "#6E1E2B" },
            PREPARE: { bg: "#E7DADC", text: "#6E1E2B" },
            CONFIRM: { bg: "#E7DADC", text: "#6E1E2B" },
            EXECUTE: { bg: "#E7DADC", text: "#6E1E2B" },
            MONITOR: { bg: "#E7DADC", text: "#6E1E2B" },
            MODIFY:  { bg: "#E7DADC", text: "#6E1E2B" }
          },
          priority_hint: "Return every outcome statement (up to 16). Order by job step in canonical map order (Define, Locate, Prepare, Confirm, Execute, Monitor, Modify), then within a step keep source order. The colored dot encodes outcome type; the tag chip is the step.",
          item_template: {
            status:   { role: "llm_gen", key: "status", prompt: "'pain' for Pain-Driven outcomes, 'aspiration' for Aspiration-Driven." },
            tag:      { role: "llm_gen", key: "tag", prompt: "Uppercase job step: DEFINE, LOCATE, PREPARE, CONFIRM, EXECUTE, MONITOR, or MODIFY." },
            headline: { role: "llm_gen", key: "headline", max_words: 20,
                        prompt: "The outcome statement verbatim (e.g. 'Minimize the likelihood of unutilized collection capacity')." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 24,
                        prompt: "Supporting evidence text if present, else null. Hover-only." }
          }
        }
      ]},
      { id: "contextual_col", proportion: 0.36, header: "Contextual & chain jobs · 5", blocks: [
        { type: "bullet_list", id: "contextual_jobs", item_limit: 5,
          pins: ["phase_4"], evidence_in_tooltip: true,
          card_background: "#F7F2F2",
          tag_styles: {
            EMOTIONAL:  { bg: "#E6DCF0", text: "#5B3E8E" },
            SOCIAL:     { bg: "#D6E4EC", text: "#2C5A72" },
            PURCHASING: { bg: "#EFE3CC", text: "#8A6516" },
            LEARNING:   { bg: "#D7E8DD", text: "#2E6B4C" },
            INTERFACING:{ bg: "#EAD6D8", text: "#8A2A30" }
          },
          // Evidence strength rendered as a right-aligned pill badge.
          badge_styles: {
            high:   { label: "High", bg: "#D7E8DD", text: "#2E6B4C" },
            medium: { label: "Med",  bg: "#EFE3CC", text: "#8A6516" },
            low:    { label: "Low",  bg: "#EAD6D8", text: "#8A2A30" }
          },
          priority_hint: "Return all contextual & chain jobs (up to 5), in source order.",
          item_template: {
            tag:      { role: "llm_gen", key: "tag", prompt: "Category as one uppercase word: EMOTIONAL, SOCIAL, PURCHASING, LEARNING, or INTERFACING (map 'Purchasing / Choosing' → PURCHASING)." },
            badge:    { role: "llm_gen", key: "badge", prompt: "Evidence strength as 'high', 'medium', or 'low'." },
            headline: { role: "llm_gen", key: "headline", max_words: 22,
                        prompt: "The job statement, lightly condensed to ≤22 words while keeping the core claim." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "The supporting evidence quote verbatim. Hover-only." }
          }
        }
      ]}
    ]},

    // Band 3: shared swatch legend — outcome-type + evidence keys + flag glyph.
    { id: "legend_band", proportion: 0.06, columns: [
      { id: "legend_col", proportion: 1, blocks: [
        { type: "legend", align: "left", items: [
          { group: "OUTCOME TYPE" },
          { color: "#9B2D30", label: "Pain-driven" },
          { color: "#2C4A6E", label: "Aspiration" },
          { group: "EVIDENCE" },
          { color: "#2D8659", label: "High" },
          { color: "#BA7517", label: "Medium" },
          { color: "#C4463A", label: "Low" },
          { flag: true, color: "#B0892F", label: "weak grounding" }
        ]}
      ]}
    ]}
  ]
};

// ---------------------------------------------------------------------------
// Example 16: Diageo — Written Profile (pre-canned document)
// Renders a multi-page document layout using DocDslRenderer.
// ---------------------------------------------------------------------------

const EXAMPLE_16_DOC_DSL = {
  version: 1,
  kind: "document",
  title: "Company Profile — Diageo",
  intent: "Written profile of Diageo's business, financials, and ESG standing.",
  page: {
    width: 816,
    height: 1056,
    margins: { top: 40, bottom: 40, left: 32, right: 32 }
  },
  chrome: {
    header: {
      left: "Diageo Profile",
      right: "Page {{page}} of {{total}}"
    },
    footer: {
      left: "Confidential — Prepared on {{date}}",
      right: "Source: Company filings"
    }
  },
  pins: [
    { name: "company", type: "entity", label: "Company", required: true },
    { name: "financials", type: "financial", label: "Financial data", required: false }
  ],
  flow: [
    {
      type: "section",
      heading: { level: 1, text: "Executive Summary", id: "exec-summary" },
      children: [
        {
          type: "paragraph",
          text: "**Diageo** is a leading spirits company with revenue of *$4.2B* in FY25, representing 12% growth year-over-year. The company has successfully recapitalized, reducing net debt from 3.2x to 1.8x EBITDA following a $500M equity raise in Q3 2024."
        },
        {
          type: "list",
          style: "bullet",
          items: [
            { text: "Net profit increased to **$380M**, up 31% YoY" },
            { text: "Gross margin expanded to **42.3%** driven by premiumisation" },
            { text: "Free cash flow of **$215M** supports ongoing investment" },
            { text: "Private company — stock data not applicable" }
          ]
        }
      ]
    },
    {
      type: "section",
      heading: { level: 2, text: "Financial Performance", id: "financial-performance" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          text: "Revenue grew 12% year-over-year to $4.2B in FY25, driven by premium spirit categories. Gross margins compressed 180bps as input costs rose, but net profit margin expanded to 9.0% from 8.5% in FY24."
        },
        {
          type: "table",
          cols: 3,
          widths: [0.30, 0.35, 0.35],
          header_row: true,
          rows: [
            ["Metric", "FY24", "FY25"],
            ["Revenue", "$3.4B", "$4.2B"],
            ["Net Profit", "$290M", "$380M"],
            ["Net Margin", "8.5%", "9.0%"],
            ["Free Cash Flow", "$190M", "$215M"]
          ]
        },
        {
          type: "list",
          style: "numbered",
          items: [
            { text: "Revenue growth driven by tequila and scotch portfolios" },
            { text: "Operating margin held steady at 33.2% despite inflation" },
            { text: "Debt reduction accelerated with $500M equity raise" }
          ]
        }
      ]
    },
    {
      type: "section",
      heading: { level: 2, text: "ESG & Sustainability", id: "esg-sustainability" },
      children: [
        {
          type: "paragraph",
          text: "Diageo has made significant progress on its **ESG commitments**, with SBTi-validated targets driving reductions in carbon intensity across Scope 1, 2, and 3 emissions."
        },
        {
          type: "table",
          cols: 4,
          widths: [0.25, 0.25, 0.25, 0.25],
          header_row: true,
          rows: [
            ["Metric", "2023", "2024", "2025"],
            ["Carbon Intensity", "−15%", "−22%", "−28%"],
            ["Water Efficiency", "−8%", "−12%", "−18%"],
            ["Waste Reduction", "−20%", "−35%", "−42%"]
          ]
        },
        {
          type: "list",
          style: "bullet",
          items: [
            { text: "**SBTi** targets validated for 1.5°C alignment" },
            { text: "**100%** renewable electricity achieved in operations" },
            { text: "**Zero waste** to landfill certified at 12 sites" }
          ]
        }
      ]
    }
  ]
};

const EXAMPLE_16_DOC_DATA = {
  // Pre-crystallized data matching the blocks above
};

// ---------------------------------------------------------------------------
// Example 17: Diageo — Written Profile (LLM crystallize)
// Same DocDSL but runs through the crystallizer against DIAGEO_SOURCE_PINS.
// ---------------------------------------------------------------------------

const EXAMPLE_17_DOC_DSL = {
  version: 1,
  kind: "document",
  title: "Company Profile — Diageo",
  intent: "Written profile of Diageo's business, financials, and ESG standing, generated from source data.",
  page: {
    width: 816,
    height: 1056,
    margins: { top: 40, bottom: 40, left: 32, right: 32 }
  },
  chrome: {
    header: {
      left: "Diageo Profile",
      right: "Page {{page}} of {{total}}"
    },
    footer: {
      left: "Confidential — Prepared on {{date}}",
      right: "Source: Company filings"
    }
  },
  pins: [
    { name: "supplier", type: "entity", label: "Supplier", required: true },
    { name: "financials", type: "financial", label: "Financial data", required: false }
  ],
  flow: [
    {
      type: "section",
      heading: { level: 1, text: "Executive Summary", id: "exec-summary" },
      children: [
        {
          type: "paragraph",
          id: "exec_para",
          pins: ["supplier"],
          prompt: "Write a 2-3 sentence executive summary of Diageo's current financial and strategic position.",
          max_words: 60
        },
        {
          type: "list",
          id: "exec_bullets",
          style: "bullet",
          pins: ["supplier"],
          item_limit: 5,
          prompt: "List key achievements and strategic moves from the company overview.",
          items: [
            { text: "", prompt: "Key financial achievement" },
            { text: "", prompt: "Strategic move or partnership" },
            { text: "", prompt: "Operational highlight" },
            { text: "", prompt: "Capital structure change" }
          ]
        }
      ]
    },
    {
      type: "section",
      heading: { level: 2, text: "Financial Performance", id: "financial-performance" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          id: "fin_para",
          pins: ["supplier", "financials"],
          prompt: "Write a paragraph summarizing Diageo's financial performance including revenue growth, profit margins, and cash flow.",
          max_words: 80
        },
        {
          type: "chart",
          id: "revenue_chart",
          kind: "bar",
          data_binding: { pin: "financials", limit: 5, label_field: "fiscal_year" },
          series: [
            { key: "revenue", label: "Revenue", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" },
            { key: "net_profit", label: "Net Profit", color: "#7FA690", label_position: "above_bar", label_format: "currency" }
          ],
          y_axis: { gridlines: { dashed: true } }
        },
        {
          type: "table",
          id: "fin_table",
          cols: 3,
          widths: [0.30, 0.35, 0.35],
          header_row: true,
          pins: ["financials"],
          rows: [
            ["Metric", "FY24", "FY25"],
            ["Revenue", "", ""],
            ["Net Profit", "", ""],
            ["Net Margin", "", ""],
            ["Free Cash Flow", "", ""]
          ]
        }
      ]
    },
    {
      type: "section",
      heading: { level: 2, text: "ESG & Sustainability", id: "esg-sustainability" },
      children: [
        {
          type: "paragraph",
          id: "esg_para",
          pins: ["supplier"],
          prompt: "Write a paragraph summarizing Diageo's ESG commitments and sustainability progress.",
          max_words: 70
        },
        {
          type: "list",
          id: "esg_bullets",
          style: "bullet",
          pins: ["supplier"],
          item_limit: 5,
          prompt: "List key ESG achievements and targets.",
          items: [
            { text: "", prompt: "Carbon reduction achievement" },
            { text: "", prompt: "Renewable energy milestone" },
            { text: "", prompt: "Waste or water target" },
            { text: "", prompt: "Certification or validation" }
          ]
        }
      ]
    },
    {
      type: "section",
      heading: { level: 1, text: "Business Overview & Strategy", id: "business-overview" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          id: "biz_overview_para",
          pins: ["supplier"],
          prompt: "Write a detailed overview of Diageo's business and recent activity, covering the Accelerate programme, portfolio realignment, and the key partnerships and acquisitions described in the source.",
          max_words: 200
        },
        {
          type: "paragraph",
          id: "biz_strategy_para",
          pins: ["supplier"],
          prompt: "Analyse Diageo's strategic priorities — margin improvement, cost efficiency, and sharpening the brand portfolio — and explain how the recent moves support disciplined growth and operational resilience.",
          max_words: 170
        }
      ]
    },
    {
      type: "section",
      heading: { level: 2, text: "Leadership & Governance", id: "leadership-governance" },
      children: [
        {
          type: "paragraph",
          id: "governance_para",
          pins: ["supplier"],
          prompt: "Summarise the leadership transition (the CEO stepping down) and the governance posture it signals, and what it means for the company's strategic direction.",
          max_words: 140
        }
      ]
    },
    {
      type: "section",
      heading: { level: 1, text: "Financial Analysis", id: "financial-analysis" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          id: "rev_trend_para",
          pins: ["supplier", "financials"],
          prompt: "Analyse Diageo's revenue trajectory from 2021 to 2025 using the financial data — note the multi-year growth and the more recent decline, and reference the latest half-year net sales figure.",
          max_words: 180
        },
        {
          type: "paragraph",
          id: "profitability_para",
          pins: ["financials"],
          prompt: "Discuss profitability and margins, referencing the net profit and operating income trends across the reported years.",
          max_words: 150
        },
        {
          type: "paragraph",
          id: "balance_sheet_para",
          pins: ["supplier", "financials"],
          prompt: "Describe the balance-sheet evolution — assets, liabilities and net debt — and comment on liquidity, free cash flow and capital allocation, including management's full-year guidance.",
          max_words: 170
        }
      ]
    },
    {
      type: "section",
      heading: { level: 2, text: "Market Position & Portfolio", id: "market-position" },
      children: [
        {
          type: "paragraph",
          id: "portfolio_para",
          pins: ["supplier"],
          prompt: "Describe Diageo's premium-spirits portfolio moves (the Cîroc / Lobos 1707 exchange and the DeLeon Tequila buy-out) and what they imply for its market positioning.",
          max_words: 150
        }
      ]
    },
    {
      type: "section",
      heading: { level: 1, text: "Risk Factors & Considerations", id: "risk-factors" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          id: "risk_para",
          pins: ["supplier", "financials"],
          prompt: "Summarise the key risks and headwinds facing Diageo based on the source — declining net sales, the net-debt position, and the leadership transition.",
          max_words: 150
        },
        {
          type: "list",
          id: "risk_bullets",
          style: "bullet",
          pins: ["supplier", "financials"],
          item_limit: 5,
          prompt: "List specific risk factors and watch items for the company.",
          items: [
            { text: "", prompt: "Top-line / demand risk" },
            { text: "", prompt: "Balance-sheet or leverage risk" },
            { text: "", prompt: "Execution risk on the Accelerate programme" },
            { text: "", prompt: "Governance or leadership-transition risk" }
          ]
        }
      ]
    },
    {
      type: "section",
      heading: { level: 1, text: "Outlook & Conclusion", id: "outlook-conclusion" },
      children: [
        {
          type: "paragraph",
          id: "outlook_para",
          pins: ["supplier", "financials"],
          prompt: "Write a forward-looking outlook and concluding assessment of Diageo's position, referencing reiterated full-year free-cash-flow guidance and the disciplined capital-allocation stance.",
          max_words: 200
        }
      ]
    }
  ]
};

// ---------------------------------------------------------------------------
// Example 18 — JTBD / ODI Analysis Report (written document, single interviewee).
//
// Doc-DSL counterpart to the Example-15 slide. Produces a multi-page written
// report from the same four pipeline phases, PLUS a new `dossier` pin that
// supplies the interviewee-context section (the one piece the four phases
// can't carry — see the dossier-extraction prompt).
//
// Scoped deliberately to a SINGLE interviewee:
//   • no small-vs-large segment comparison (was gap D — needs ≥2 interviewees)
//   • no "Separated Outcomes / second distinct job" section (was gap C)
//   • no Methodology Notes section
// What remains: Executive Summary · Job Definition · Interviewee Context ·
// Universal Job Map (8 steps) · Contextual & Chain Jobs · Gap Analysis.
//
// Source data below is the asset-management example the four-phase pipeline
// produced. The `dossier` pin is a placeholder — paste the output of the
// dossier-extraction prompt into ASSET_MGMT_DOSSIER to populate the
// Interviewee Context section. Until then that section renders thinly.
// ---------------------------------------------------------------------------

const ASSET_MGMT_PHASE_1 = `# Overview
Streamline trade processing and fund administration infrastructure for an asset management firm.
# Executor
Operations leadership team (COO and operations managers)
# Beneficiary
The asset management firm's management and product teams
# Rationale
Streamlining the operational infrastructure that supports trade processing, fund accounting, and reporting across diverse product lines enables an asset management firm to handle increasing volumes and complexity reliably. By unifying heterogeneous workflows and managing third-party dependencies, the operations team can free staff from manual, repetitive tasks and focus on strategic optimization. This job encompasses multiple routine steps—trade capture, data reconciliation, NAV calculation, and compliance checks—rather than any single workflow component or tool. It serves as the foundation for scalable growth, cost management, and governance, adaptable to new fund launches and market demands without prescribing specific technologies or vendor solutions.
# Stability Test
Streamlining operational infrastructure remains essential as firms expand products and volumes. Dependencies on third-party platforms and reporting services persist, though partners may change. Asset class shifts and technology adoption could evolve, but the core need to unify workflows, maintain data integrity, and scale reliably transcends specific systems or vendor references.
# Tools And Solutions
| Tool / Solution | Context Used | What It Is Used For | Where It Falls Short |
|----|----|----|----|
| Trading platform | Processing trade capture and portfolio data | Capturing and consolidating trades for all products | Highly customized and reliant on third-party support |
| Task management system | Scheduling daily operational tasks | Ensuring timely completion of daily and periodic processes | Limited automation and manual follow-up required |
| Fund administration service | Maintaining official books and records | Providing net asset values and financial statements | Dependent on third-party data quality and timeliness |
| Third-party operations provider | Outsourcing middle office functions | Handling trade settlements and operational workflows | Variability in data accuracy and service reliability |
| Business intelligence tool | Generating management information reports | Automating reporting and performance metrics | Lacks real-time integration and comprehensive data coverage |
# Evidence
| Source Quote In Context | What It Supports |
|----|----|
| "we're trying to find, you know, new bright ways to construct our op model and evolve it on a continuous basis" | Desire to improve operational model |
| "we have a tool called Task Manager, which very clearly identifies everything that needs to be done on a daily basis" | Use of a task management system |
| "we outsourced all of our ops middle office, which is to serve Yale's business, to US Bank" | Use of a third-party operations provider |
| "we retired Wall Street Office, which is a very popular bank debt system that you may have heard about" | Elimination of a trading platform due to cost |
| "separately about four years ago we equally retired another third-party accounting system called Advent Geneva" | Substitution of fund accounting systems |
# Open Questions
- What boundaries should define the scope of operational infrastructure improvement?
- Which metrics will best measure successful automation and workflow streamlining?
- How will changes impact compliance with regulatory and investor requirements?
- What level of in-house versus third-party execution balances control and efficiency?
- Are there additional hidden dependencies across product teams that need mapping?`;

const ASSET_MGMT_PHASE_2 = [
`# Step\nDefine\n# Signal Type\nConcern\n# Job-Map Signal\nConcern that the growing number of funds increases operational complexity and requires separate workflows\n# Quote\nthe sheer amount of funds on platform and evolution\n# Speaker\nRob Dunleavy\n# Interpretation\nThis concern defines a planning challenge of scope by highlighting the difficulty of supporting numerous funds each requiring distinct processes.\n# Evidence Strength\nHigh\n# Validation Flags\nNo flags\n# Constraint\nMultiple service providers and fund-specific workflows increase complexity`,
`# Step\nDefine\n# Signal Type\nConcern\n# Job-Map Signal\nConcern about the high manual effort and labor intensity of bank debt settlement processes\n# Quote\nexcess of 90% of everything we trade is bank debt. So that means it's like high touch\n# Speaker\nRob Dunleavy\n# Interpretation\nThis concern highlights a key planning consideration about manual workload in trade processing.\n# Evidence Strength\nHigh\n# Validation Flags\nNo flags\n# Constraint\nHigh-touch settlement requirements for bank debt trades`,
`# Step\nLocate\n# Signal Type\nInformation Need\n# Job-Map Signal\nIdentify manual and low-automation tasks suitable for review and potential automation\n# Quote\nthis work product was a function of addressing that like first going through what has to be done, what's the frequency, status of automation\n# Speaker\nRob Dunleavy\n# Interpretation\nLocating the scope of tasks and their automation levels to target improvement opportunities.\n# Evidence Strength\nHigh\n# Validation Flags\nNo flags\n# Constraint\nReliance on existing task inventory accuracy`,
`# Step\nPrepare\n# Signal Type\nObjective\n# Job-Map Signal\nOptimize staffing and cost structure by leveraging third-party service providers to scale operations without increasing internal headcount\n# Quote\nwe outsourced all of our ops middle office... to US Bank so that was phenomenal because just imagine a world where I had another 17 persons sitting on our floor\n# Speaker\nRob Dunleavy\n# Interpretation\nPreparing the organizational model by arranging for external providers to handle middle office tasks.\n# Evidence Strength\nHigh\n# Validation Flags\nNo flags\n# Constraint\nDependence on third-party service provider performance`,
`# Step\nConfirm\n# Signal Type\nObjective\n# Job-Map Signal\nVerify that new fund launches comply with investor-specific guidelines and meet required timelines\n# Quote\neach one of those [fund launches] comes with the nuance of who the investor is ... what are the investment guidelines specific to that set opportunity, what timeline are we needing to satisfying\n# Speaker\nRob Dunleavy\n# Interpretation\nConfirming readiness of fund launches by checking alignment with investor requirements.\n# Evidence Strength\nHigh\n# Validation Flags\nNo flags\n# Constraint\nUnique investor criteria and varied launch timelines`,
`# Step\nMonitor\n# Signal Type\nObjective\n# Job-Map Signal\nMonitor service provider performance through governance, SLAs, and periodic health checkups\n# Quote\nit creates dependencies ... more robust service level agreements with the third party and then more quarterly health checkups to see how things are going\n# Speaker\nRob Dunleavy\n# Interpretation\nMonitoring is needed to ensure third-party dependencies perform to standards and maintain reliability.\n# Evidence Strength\nHigh\n# Validation Flags\nNo flags\n# Constraint\nReliance on external providers for critical operational tasks`,
`# Step\nModify\n# Signal Type\nChallenge\n# Job-Map Signal\nCorrect and reprocess data errors from trustees by receiving updated versions and re-ingesting the data\n# Quote\nthat data can be wrong ... version two ... reingest\n# Speaker\nRob Dunleavy\n# Interpretation\nModifying processes is required to address external data inaccuracies and maintain accurate records.\n# Evidence Strength\nHigh\n# Validation Flags\nNo flags\n# Constraint\nDependence on timely and accurate corrected data from trustees`
];

const ASSET_MGMT_PHASE_3 = [
`# Job Step\nDefine\n# Outcome Statement\nMinimize the degree of operational complexity.\n# Outcome Type\nPain-Driven\n# Object Of Control\noperational complexity\n# Supporting Evidence\nConcern that the growing number of funds increases operational complexity and requires separate workflows. "the sheer amount of funds on platform and evolution."\n# Validation Flags`,
`# Job Step\nDefine\n# Outcome Statement\nMinimize the effort required to complete bank debt settlements.\n# Outcome Type\nPain-Driven\n# Object Of Control\nbank debt settlements\n# Supporting Evidence\nConcern about the high manual effort and labor intensity of bank debt settlement processes. "excess of 90% of everything we trade is bank debt. So that means it's like high touch."\n# Validation Flags`,
`# Job Step\nLocate\n# Outcome Statement\nMinimize the time it takes to identify manual tasks requiring review.\n# Outcome Type\nAspiration-Driven\n# Object Of Control\nmanual tasks requiring review\n# Supporting Evidence\nIdentify manual and low-automation tasks suitable for review and potential automation. "first going through what has to be done, what's the frequency, status of automation."\n# Validation Flags`,
`# Job Step\nPrepare\n# Outcome Statement\nMinimize the number of new internal hires required to scale operations.\n# Outcome Type\nAspiration-Driven\n# Object Of Control\nnew internal hires\n# Supporting Evidence\nOptimize staffing and cost structure by leveraging third-party service providers to scale operations without increasing internal headcount. "imagine a world where I had another 17 persons sitting on our floor."\n# Validation Flags`,
`# Job Step\nConfirm\n# Outcome Statement\nMinimize the likelihood of new fund launches failing to comply with investor guidelines.\n# Outcome Type\nAspiration-Driven\n# Object Of Control\nnew fund non-compliance with investor guidelines\n# Supporting Evidence\nVerify that new fund launches comply with investor-specific guidelines and meet required timelines. "what are the investment guidelines specific to that set opportunity."\n# Validation Flags`,
`# Job Step\nConfirm\n# Outcome Statement\nMinimize the likelihood of new fund launches missing required timelines.\n# Outcome Type\nAspiration-Driven\n# Object Of Control\nnew fund launches missing required timelines\n# Supporting Evidence\nVerify that new fund launches comply with investor-specific guidelines and meet required timelines. "what timeline are we needing to satisfying."\n# Validation Flags`,
`# Job Step\nMonitor\n# Outcome Statement\nMinimize the frequency of third-party service provider failures.\n# Outcome Type\nAspiration-Driven\n# Object Of Control\nthird-party service provider failures\n# Supporting Evidence\nMonitor service provider performance through governance, SLAs, and periodic health checkups. "more robust service level agreements with the third party and then more quarterly health checkups."\n# Validation Flags`,
`# Job Step\nModify\n# Outcome Statement\nMinimize the frequency of data errors from trustees.\n# Outcome Type\nPain-Driven\n# Object Of Control\ndata errors from trustees\n# Supporting Evidence\nCorrect and reprocess data errors from trustees by receiving updated versions and re-ingesting the data. "that data can be wrong ... version two ... reingest."\n# Validation Flags`,
`# Job Step\nModify\n# Outcome Statement\nMinimize the time it takes to re-ingest corrected trustee data.\n# Outcome Type\nPain-Driven\n# Object Of Control\nre-ingestion of corrected trustee data\n# Supporting Evidence\nCorrect and reprocess data errors from trustees by receiving updated versions and re-ingesting the data. "that data can be wrong ... version two ... reingest."\n# Validation Flags`
];

const ASSET_MGMT_PHASE_4 = [
`# Category\nInterfacing\n# Job Statement\nCollaborate with multiple trustees and fund administrators to reconcile and validate trade and position data.\n# Why This Matters\nEnsures data integrity and timely processing across external partners, reducing settlement failures.\n# Evidence\n"Often that data can be wrong"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nDisposing / Switching\n# Job Statement\nRetire legacy third-party systems and migrate to new fund administration and middle office platforms.\n# Why This Matters\nReduces maintenance costs and manual reconciliation effort while leveraging more advanced third-party solutions.\n# Evidence\n"we retired Wall Street office"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nPurchasing / Choosing\n# Job Statement\nEvaluate and select third-party service providers for operations, such as fund administrators and trustees.\n# Why This Matters\nChoosing reliable partners is critical to support scale and meet operational SLAs.\n# Evidence\n"we moved from State Street IFS to Northern Trust seven years ago"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nLearning\n# Job Statement\nGain visibility into AI capabilities from industry peers and service providers to inform infrastructure decisions.\n# Why This Matters\nUnderstanding evolving AI solutions helps identify new automation and efficiency opportunities.\n# Evidence\n"They serve, you know, 65% of the world's largest hedge funds"\n# Evidence Strength\nMedium\n# Validation Flags\nNone`,
`# Category\nEmotional Job\n# Job Statement\nReduce stress caused by system outages and data errors.\n# Why This Matters\nMinimizing disruptions improves confidence and maintains operational continuity.\n# Evidence\n"Not fun when it happens and it can happen"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nSocial Job\n# Job Statement\nDemonstrate responsive and credible leadership when managing multiple vendors and escalating issues.\n# Why This Matters\nMaintaining trust with stakeholders and investors supports the firm's reputation.\n# Evidence\n"I read US Bank the Riot Act"\n# Evidence Strength\nMedium\n# Validation Flags\nNone`,
`# Category\nMaintaining\n# Job Statement\nContinuously monitor and govern third-party vendor performance through SLAs and health checkups.\n# Why This Matters\nSustained oversight ensures service quality and reduces operational risk.\n# Evidence\n"more robust service level agreements with the third party and then more quarterly health checkups to see how things are going"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`,
`# Category\nInstalling / Setting Up\n# Job Statement\nOnboard and integrate rebadged technologists from partner firms into co-located teams.\n# Why This Matters\nAligns resources quickly to maintain development capacity and team cohesion.\n# Evidence\n"all of our developers are all rebadged employees working at third party companies, but sitting co-located here"\n# Evidence Strength\nHigh\n# Validation Flags\nNone`
];

// Interviewee dossier — output of the dossier-extraction prompt run against
// the original Anchorage transcript, flattened to the same `# Heading` +
// markdown shape the other phases use.
const ASSET_MGMT_DOSSIER = `# Name And Role
- Rob Dunleavy
- Title: No relevant data
- Anchorage
# Organization Profile
- Approximately 90 discrete products/funds
- 18 funds in one division and sub-60 CLOs/CDOs in another
- ~13,500 block trades per year resulting in ~135,000 allocations
- 90% of trading volume in bank debt
- Year-on-year growth: 11% (2023–24), 77% (2024–25), projected 50% (2025–26)
- Ecosystem of ~80 service vendors, ~40 tier-one providers
- Four external trustees: US Bank, BNY, Wilmington Trust, Citi Virtus
# Background And Experience
- Began in operations at Salomon Brothers on the Liars Poker desk
- Joined JPMorgan as a derivative controller working on early swaps (late 1980s)
- Served as global head of derivatives at SEB in Stockholm
- Ran Citigroup treasury operations, leading a 150-person project
- Global head of middle office operations for Deutsche's hedge fund business (~5 years)
- COO at H2 Capital in Stamford, Connecticut
- Joined Anchorage 17 years ago
# Role In The Job
- Oversees end-to-end operations infrastructure: operations, middle office, risk control, legal, compliance, IT
- Manages trade capture, settlement, and clearing across Front Arena, prime brokers, trustees, and custodians
- Chairs the weekly operating committee meeting to coordinate deliverables and strategy
- Governs relationships with service providers (US Bank, Northern Trust, trustees, prime brokers)
- Directs outsourcing and system transformation to optimize cost and scalability
# Current Posture
- High appetite for continuous operating model optimization and innovation
- Proactively explores automation, third-party services, and system replacements
- Embraces frequent system changes as routine to drive efficiency
- Balances innovation with regulatory compliance under SEC and FCA oversight
- Operates within constraints of external dependencies and legacy workflows
# Notable Initiatives And Results
- Outsourced operations and middle office for structured credit to US Bank (17-person team), pass-through to funds
- Retired Wall Street Office bank-debt system (seven-figure annual spend)
- Retired Advent Geneva accounting system (seven-figure annual spend)
- Switched fund administration from State Street FIS to Northern Trust seven years ago
- Engaged third-party valuation modelers (e.g., Hult, Crow) for hard-to-value assets
- Rebadged core technologists through third-party firms to align cost and pass-through models
# Perspective Note
Speaks solely from his position as operations leader responsible for Anchorage's core functional job (operations, middle office, and infrastructure).
# Evidence
| Source Quote In Context | What It Supports |
|----|----|
| "Rob Dunleavy" | Identifies the participant's name |
| "And then I left there to join Anchorage 17 years ago. And I've been here for the last 17." | Tenure and organization |
| "we have or you know just under 90 discrete products..." | Scale of products/funds |
| "we trade maybe about thirteen and a half thousand block trades a year... 135,000 transactions a year" | Trading volumes and allocations |
| "I started my career at Solomon Brothers...junior most ops guy right out of school" | Entry into operations career |
| "Then I left Solomon to join JP Morgan...derivative controller...late 80s" | Early career progression |
| "They asked me to be global head of the derivative business for SEB..." | Leadership role at SEB |
| "Tuesday mornings we run our operating committee meeting." | Role in coordinating operations |
| "We outsourced all of our ops middle office...17 persons at US Bank" | Initiative on outsourcing middle office |
| "we retired Wall Street office...seven-figure spend" | System transformation and cost savings |
# Open Questions
- What is Rob Dunleavy's official job title at Anchorage?
- What is the size of his direct operations team?
- How is the operations function structured and to whom does he report?`;

const ASSET_MGMT_SOURCE_PINS = {
  dossier: ASSET_MGMT_DOSSIER,
  phase_1: ASSET_MGMT_PHASE_1,
  phase_2: ASSET_MGMT_PHASE_2,
  phase_3: ASSET_MGMT_PHASE_3,
  phase_4: ASSET_MGMT_PHASE_4
};

// Eight Universal Job Map steps. Each becomes an H3 subsection with a one-line
// intro (LLM, filtered to the step) and a free-form outcome table (LLM, filtered
// to the step). Free-form tables keep every grounded row and self-report gaps.
const EXAMPLE_18_JOB_MAP_STEPS = [
  { key: "Define",   title: "1. Define — Determine scope, goals, and strategic approach",  focus: "deciding what to pursue, the goals, and the overall approach" },
  { key: "Locate",   title: "2. Locate — Find inputs, opportunities, and information",      focus: "finding the people, opportunities or information the job needs" },
  { key: "Prepare",  title: "3. Prepare — Build capabilities, infrastructure, readiness",   focus: "getting set up: capabilities, infrastructure and readiness" },
  { key: "Confirm",  title: "4. Confirm — Validate readiness, commitment, feasibility",     focus: "verifying readiness, commitment and feasibility before committing" },
  { key: "Execute",  title: "5. Execute — Perform the core work and deliver",               focus: "performing the core work and delivering the outcome" },
  { key: "Monitor",  title: "6. Monitor — Track performance, shifts, and health",           focus: "tracking performance, market shifts and operational health" },
  { key: "Modify",   title: "7. Modify — Adjust, pivot, and work around constraints",       focus: "adjusting the approach, pivoting, or working around constraints" },
  { key: "Conclude", title: "8. Conclude — Finalize, document, and plan next steps",        focus: "closing the loop, documenting outcomes and planning next steps" }
];

const EXAMPLE_18_JOB_MAP_SUBSECTIONS = EXAMPLE_18_JOB_MAP_STEPS.map((s) => {
  const k = s.key.toLowerCase();
  const STEP = s.key.toUpperCase();
  return {
    type: "section",
    heading: { level: 3, text: s.title, id: `jobmap-${k}` },
    children: [
      {
        type: "paragraph",
        id: `step_${k}_intro`,
        pins: ["phase_2", "phase_1"],
        prompt: `In one sentence, describe what the executor is trying to accomplish during the ${STEP} step — ${s.focus} — grounded only in the source. If the source has no ${STEP}-step signals, say this step was not covered in this interview and is a gap for future research.`,
        max_words: 45
      },
      {
        type: "table",
        id: `step_${k}_outcomes`,
        cols: 3,
        widths: [0.07, 0.53, 0.40],
        header_row: true,
        pins: ["phase_3", "phase_2"],
        rows: [],
        prompt: `List every outcome statement whose job step is ${STEP} (and ONLY ${STEP}). First return a header row with col_0 "#", col_1 "Outcome Statement", col_2 "Source Signal". Then one row per outcome: col_0 = sequential number; col_1 = the outcome statement verbatim; col_2 = the supporting evidence / source signal, including a short attributed quote where available. If there are no ${STEP}-step outcomes, return the header row plus one row reading "—", "No outcomes captured for this step", "Gap for future research".`
      }
    ]
  };
});

const EXAMPLE_18_DOC_DSL = {
  version: 1,
  kind: "document",
  title: "JTBD / ODI Analysis Report",
  intent: "Single-interviewee Jobs-to-be-Done / Outcome-Driven Innovation analysis: job definition, interviewee context, the Universal Job Map with outcome statements, contextual & chain jobs, and a pre-survey gap analysis.",
  page: {
    width: 816,
    height: 1056,
    margins: { top: 56, bottom: 56, left: 64, right: 64 }
  },
  chrome: {
    header: {
      left: "JTBD / ODI Analysis",
      right: "Page {{page}} of {{total}}"
    },
    footer: {
      left: "Sense Platform · JTBD / ODI interview analysis · Supplement to, not sole basis for, decision-making",
      right: "Prepared {{date}}"
    }
  },
  pins: [
    { name: "dossier", type: "any", label: "Interviewee dossier",     required: true  },
    { name: "phase_1", type: "any", label: "Functional-job overview", required: true  },
    { name: "phase_2", type: "any", label: "Job-map signals",         required: true  },
    { name: "phase_3", type: "any", label: "Outcome statements",      required: true  },
    { name: "phase_4", type: "any", label: "Contextual & chain jobs", required: false }
  ],
  flow: [
    // 1 · Executive Summary
    {
      type: "section",
      heading: { level: 1, text: "Executive Summary", id: "exec-summary" },
      children: [
        {
          type: "paragraph",
          id: "exec_summary_para",
          pins: ["phase_1", "phase_3", "dossier"],
          prompt: "Write a 3-4 sentence executive summary: state the core functional job, name the interviewee and their organisation/role (from the dossier), and name the 2-3 strongest themes or most-cited pains across the outcomes. Make clear this is a pre-survey qualitative analysis, so opportunity scores are not yet available.",
          max_words: 110
        },
        {
          type: "list",
          id: "exec_highlights",
          style: "bullet",
          pins: ["phase_3", "phase_2"],
          item_limit: 4,
          prompt: "List the strongest signal clusters — the themes most consistently raised across the outcome statements and job-map signals. One crisp clause each."
        }
      ]
    },
    // 2 · Job Definition
    {
      type: "section",
      heading: { level: 1, text: "Job Definition", id: "job-definition" },
      page_break_before: true,
      children: [
        {
          type: "table",
          id: "job_def_table",
          cols: 2,
          widths: [0.28, 0.72],
          header_row: false,
          pins: ["phase_1"],
          rows: [],
          prompt: "Produce exactly these rows in order, two columns each (col_0 = label, col_1 = value drawn from the source). Row 1: col_0 \"Core Functional Job\", col_1 = the functional job statement verbatim from Overview. Row 2: col_0 \"Job Executor\", col_1 = the executor. Row 3: col_0 \"Beneficiary\", col_1 = the beneficiary. Row 4: col_0 \"Context\", col_1 = a one-sentence context drawn from the Rationale. Row 5: col_0 \"Stability Test\", col_1 = a one-sentence summary of the Stability Test."
        }
      ]
    },
    // 3 · Interviewee Context (from the new dossier pin)
    {
      type: "section",
      heading: { level: 1, text: "Interviewee Context", id: "interviewee-context" },
      children: [
        {
          type: "paragraph",
          id: "dossier_bio",
          pins: ["dossier"],
          prompt: "Write a profile of the interviewee: name, title and organisation; background and tenure; their role relative to the job; and notable initiatives or results. Concrete specifics (tools, figures, organisations) are welcome here. If the dossier is a stub with no real content, write a single sentence noting that interviewee context has not yet been supplied.",
          max_words: 180
        },
        {
          type: "paragraph",
          id: "dossier_posture",
          pins: ["dossier"],
          prompt: "Summarise the interviewee's current posture and the constraints they operate under (e.g. resource-constrained, early vs. mature, cautious vs. aggressive), and note whether they speak from a single position or more than one. Omit (return null) if the dossier offers nothing beyond the bio above.",
          max_words: 110
        }
      ]
    },
    // 4 · Universal Job Map (8 step subsections)
    {
      type: "section",
      heading: { level: 1, text: "Universal Job Map", id: "job-map" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          id: "job_map_intro",
          pins: ["phase_2", "phase_3"],
          prompt: "One sentence: the outcomes below are organised across the eight Universal Job Map steps; steps with no captured signal are flagged as research gaps.",
          max_words: 45
        },
        ...EXAMPLE_18_JOB_MAP_SUBSECTIONS
      ]
    },
    // 5 · Contextual & Chain Jobs
    {
      type: "section",
      heading: { level: 1, text: "Contextual & Chain Jobs", id: "contextual-jobs" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          id: "contextual_intro",
          pins: ["phase_4"],
          prompt: "One-sentence framing of the contextual and chain jobs that surround the core functional job (emotional, social, purchasing, learning, interfacing, maintaining, etc.).",
          max_words: 40
        },
        {
          type: "table",
          id: "contextual_table",
          cols: 3,
          widths: [0.24, 0.48, 0.28],
          header_row: true,
          pins: ["phase_4"],
          rows: [],
          prompt: "First return a header row: col_0 \"Category\", col_1 \"Job\", col_2 \"Why it matters\". Then one row per contextual/chain job in the source: col_0 = the category (e.g. Emotional, Social, Purchasing/Choosing, Learning, Interfacing, Maintaining, Disposing/Switching, Installing/Setting Up); col_1 = the job statement; col_2 = why it matters."
        }
      ]
    },
    // 6 · Gap Analysis & Recommendations (single-interviewee — no segment table)
    {
      type: "section",
      heading: { level: 1, text: "Gap Analysis & Recommendations", id: "gap-analysis" },
      page_break_before: true,
      children: [
        {
          type: "paragraph",
          id: "coverage_para",
          pins: ["phase_2", "phase_3"],
          prompt: "Assess coverage across the eight job-map steps: which steps are well covered, which are thin, and which have no captured signal at all. Base this only on the source; do not invent outcomes.",
          max_words: 140
        },
        {
          type: "paragraph",
          id: "recommendations_para",
          pins: ["phase_3", "phase_1"],
          prompt: "Cluster the outcomes into 2-3 candidate innovation targets — the themes most likely to be underserved when surveyed. Make clear these are analyst hypotheses, pre-survey and not opportunity-scored.",
          max_words: 150
        },
        {
          type: "list",
          id: "research_gaps",
          style: "bullet",
          pins: ["phase_1", "phase_2"],
          item_limit: 6,
          prompt: "List the open questions and research gaps to address in further interviews, drawn from the Open Questions section and any thinly-covered job-map steps."
        }
      ]
    }
  ]
};

// ---------------------------------------------------------------------------
// Example 20: Data & Reporting — Challenge Analysis
// Discovery research findings slide. Demonstrates heatmap pulling datatable
// metrics deterministically: count_field counts Category field values across
// the evidence_table pin without an LLM call. Tile row also derives counts
// from the same pin (derive: true). Only text blocks and quote lists use LLM.
// ---------------------------------------------------------------------------

// Structured evidence datatable: one record per verbatim quote, tagged by
// problem area. Parsed by parsePinRecords into { category, quote } objects.
// The heatmap's count_field reads "Category" deterministically (no LLM).
const DR_EVIDENCE_TABLE = [
  `# Category\nSystem Integration\n# Quote\n"Systems don't always feel like they talk to one another"`,
  `# Category\nSystem Integration\n# Quote\n"it doesn't incorporate, you know some of the liquidity pictures for example they have to be manually added in"`,
  `# Category\nReporting Inflexibility\n# Quote\n"That report gets produced and that gets sent out to infinity in perpetuity."`,
  `# Category\nReporting Inflexibility\n# Quote\n"That clogs my inbox."`,
  `# Category\nReporting Inflexibility\n# Quote\n"we used something called Tableau, which gives us a static report every day of how much is our cash...that is not flexible"`,
  `# Category\nManual Processes\n# Quote\n"So what would I do I would call someone in Irv's team and they'll say, oh happy to get that for you... Three days later it comes back"`,
  `# Category\nManual Processes\n# Quote\n"requires us to ask for the data, you know, get it back a day later, look at it, you know, request it again"`,
  `# Category\nReporting Inflexibility\n# Quote\n"we'll still get like multiple versions of like all these reports every day...this reporting package is like fifteen emails"`,
  `# Category\nReporting Inflexibility\n# Quote\n"I mean it would be great if V2s were like a one-off exceptional event rather than...we get a V2 80% of days and we get a V3 forty percent of days"`,
  `# Category\nManual Processes\n# Quote\n"you gotta go manually and look at like here's what my portfolio was on each day, here's what my cache number was"`,
  `# Category\nData Quality\n# Quote\n"I don't know what my risk is I don't know if the assets that are listed in this are correct I don't know if the cash balance that's listed as correct"`,
  `# Category\nData Quality\n# Quote\n"they send us a recon tool every day, like show me the problem And they cannot find it It doesn't exist It's not in the spreadsheet anywhere So I'm like...what is it actually doing"`,
  `# Category\nSystem Integration\n# Quote\n"I get eight or nine different emails from automatically generated from Plaxin. How do I get all of that into 10 bullet points of what I actually need to focus on"`,
  `# Category\nSystem Integration\n# Quote\n"No real-time portal positions. That to me is something that would be super valuable. What we have at the moment is Front, and that's real time"`,
  `# Category\nSystem Integration\n# Quote\n"But it's just crap from a user interface and it and it's clunky and slow because it's consuming so much data"`,
  `# Category\nSystem Integration\n# Quote\n"front is almost for some reason the latency between New York and Europe, like front almost doesn't load in Europe sometimes"`,
  `# Category\nManual Processes\n# Quote\n"you're in excel all day you're reconciling things you're doing calculations you're building models I mean that stuff is just that pace is accelerated exponentially"`,
  `# Category\nManual Processes\n# Quote\n"When I first joined, what I hired was a bunch of Excel reports every day."`,
  `# Category\nManual Processes\n# Quote\n"It's a gigantic Excel sheet...it's called CPW Workbook"`,
  `# Category\nManual Processes\n# Quote\n"That was all copy and pasting across multiple tabs of spreadsheets That's none of that anymore. So it's all macros"`,
  `# Category\nManual Processes\n# Quote\n"it's all manual...if we could have the data...if we could just sometimes just ask information to like an AI, it would be so much better"`,
  `# Category\nSystem Integration\n# Quote\n"Everything comes a lot of the email stuff and alerts and and reporting comes right out of front and it's delivered via email"`,
  `# Category\nData Silos\n# Quote\n"there's data lives in a few places There's the portal So there's a lot of data up there There's front arena"`,
  `# Category\nReporting Inflexibility\n# Quote\n"A limitation we've lived with as long as I can remember...it refreshes nightly only"`,
  `# Category\nSystem Integration\n# Quote\n"All day And it's like Fred and I fixed it before eight AM this morning...there's no way to like refresh, like push that to production"`,
  `# Category\nSystem Integration\n# Quote\n"The portals it comes into the same FTP folder, but our portal is no longer doesn't consume that file"`,
  `# Category\nData Silos\n# Quote\n"There's not a good way to look at in aggregate what we own by fund"`,
  `# Category\nData Silos\n# Quote\n"why can't I like query how much packaging exposure we have as a firm"`,
  `# Category\nData Silos\n# Quote\n"If I could type into Claude and say what was my percent fixed and percent floating on a weighted average basis for ACO9 as of 331...that would be amazing"`,
  `# Category\nData Quality\n# Quote\n"I don't know what my risk is I don't know if the assets that are listed in this are correct"`
];
// Category counts: System Integration: 9, Manual Processes: 8, Reporting Inflexibility: 6, Data Silos: 4, Data Quality: 3

const DR_IMPACT_OVERVIEW = {
  content: "Infrastructure bottlenecks and manual processes have driven up costs and constrained scalability, forcing teams into reactionary modes that delay strategic initiatives and automation and undermine trading agility. Frequent check-offs, error-prone handling of complex loan events, and fragmented data slow execution, limiting the ability to trade actively and capture market opportunities. Persistent instability and poor usability of key systems exacerbate operational risk, fuel recurring errors, and throttle timely reporting. Failure to modernize also erodes service quality and shakes investor confidence, complicating fundraising and updates. Meanwhile, stalled workstreams and lack of integration impede decisive action, prolonging decision-making and inflating ongoing overhead costs.",
  quotes: [
    "Infrastructure inefficiencies are causing substantial manual work and enormous hours spent on tasks that should be automatable. This drives higher operating cost and limits scalability.",
    "Non-standard events (restructurings, complex changes, private loan trading) create havoc with multiple layers of potential error.",
    "Failure to modernize risks reduced service quality and weaker investor confidence in operations.",
    "Multiple operational and strategic workstreams (AI, settlement, bank contract and others) get stuck in discussion without progressing to decisions or answers.",
    "Work arrives continuously and unpredictably, so teams spend their days reacting to ad hoc requests rather than planning.",
    "This slows delivery of strategic work and forces reactive execution.",
    "As a result, the same operational problems recur, staff stay overloaded, and the business loses performance through delays and errors.",
    "This slows or blocks automation initiatives like credit intelligence and document retrieval, forcing reliance on new hires and experimentation.",
    "Limits ability to trade actively and capture opportunities.",
    "The lack of integration drives manual work, inconsistent views, and prevents traders from getting key answers quickly, slowing decisions and execution."
  ]
};

const DR_OVERVIEW_TEXT = `# Overview
Data & Reporting

The firm's fragmented data architecture spans multiple legacy platforms—portal, Front Arena, SQL databases and a risk warehouse—none of which integrate seamlessly. Teams workaround static, one-off reports that persist indefinitely, generating stale outputs and operational drag. Manual stitching across disconnected systems slows response times for investor and internal requests, increasing errors. Heavy reliance on Excel for liquidity management, budgeting and carry models further hampers efficiency. Overlapping analytics by quant, portfolio, tech and IR teams duplicates effort. With no self-serve exposures views or universal identifiers, users endure repetitive manual processes, outdated versions and reduced agility in querying portfolio statistics.`;

// Per-subcategory synthesis — kept as a separate pin so the LLM can focus on
// structured summary content when generating the subcategory cards, without
// mixing in the raw evidence table or high-level overview text.
const DR_SUBCATEGORY_SUMMARIES = `# System Integration
Multiple legacy and third-party systems remain disconnected, forcing manual data additions and consolidation. Fragmented risk, ticketing, and portfolio tools prevent firmwide visibility, impede operational efficiency benchmarks, and block integration of expert intelligence. Lack of consolidated dashboards and portfolio-level views complicates priorities and analysis. Inability to extract FinDocs data hampers workflows.

Themes: Persistent disconnection between legacy and third-party systems requires extensive manual consolidation. Lack of a unified operations dashboard prevents stakeholders from prioritizing daily tasks. Fragmented risk, ticketing, and portal systems create data silos. Disconnection of portfolio construction tools from central platforms forces separate manual tracking. Inability to extract or reuse FinDocs dataroom information limits data reuse.

Underpinning: Legacy and third-party systems remain fragmented, impeding seamless data flow and system integration across platforms. Absence of unified operations dashboards forces manual consolidation and prioritization. Siloed risk, ticketing, and portfolio tools obstruct firmwide visibility.

# Manual Processes
Teams rely on Excel and manual workflows for portfolio construction, data collection, and reporting. Excessive time is spent cleansing and gathering data rather than analysis. Reporting, trustee sign-offs, and valuation true-ups are manual and time-consuming. Pipeline lead additions and calendar visibility lack automation, while resource constraints strain prioritization and backlog.

Themes: Excessive manual data handling and cleansing. Spreadsheets dominate workflows, causing input errors. Reporting and sign-off processes manual. Resource limitations hinder improvements. One-off investor requests and non-standard valuation checks generate significant repetitive manual processes.

Underpinning: Reliance on Excel for critical workflows emerges from a lack of robust tooling. Automation gaps force extensive data gathering and cleansing rather than analysis. Resource constraints and prioritization pressures cause persistent backlogs.

# Reporting Inflexibility
Rigid data architecture blocks flexible reporting, forcing static, nightly inbox deliveries. Multiple versions confuse users and hinder self-service ad hoc analysis. Reports produce stale, point-in-time results while unstructured commentary and raw documents remain unusable. Investment teams often ignore outputs, causing slow iterations and unread reports.

Themes: Inflexible data structures prevent dynamic report customizations. Static scheduling and nightly-refresh cycles produce outdated data. Proliferation of redundant report versions overwhelms stakeholders. Limited self-service capabilities force reliance on analysts. Unprocessable analyst commentary hinders automated insights extraction.

Underpinning: Poor data architecture design prevents flexible reporting. Static scheduling and manual report distribution create inflexible outputs. Inadequate self-service and ad hoc analysis tools restrict user autonomy.

# Data Silos
Teams duplicate analytics and valuation work due to fragmented data across multiple internal and external systems, requiring manual aggregation and repeated conversations. Marketing and performance attribution suffer from disconnected sources and lack of a centralized, queryable fund document repository.

Themes: Duplicate analytics and valuation tasks performed independently by siloed teams. Data resides in multiple isolated internal and external systems. Manual aggregation of dispersed datasets causes significant time loss. Absence of a centralized, queryable documentation repository. Critical knowledge dependence on specific individuals.

Underpinning: Organizational silos preventing collaboration, scattered data sources, absence of a centralized document repository, and overreliance on individual knowledge carriers.

# Data Quality
Multiple reports highlight unreliable data sources, frequent omissions, and obvious errors disrupting daily workflows. Teams distrust US Bank and trustee feeds, legacy workbooks, and third-party integrations. Inaccurate metrics require constant manual verification, while exception lists become noisy and overlooked.

Themes: Overreliance on unvetted external data sources causes recurring errors. Manual verification becomes necessary for every report entry. Inconsistent metric calculations lead to unreliable performance indicators. No clear single source of truth for investor-facing and risk data. Exception lists become overloaded with ignored items.

Underpinning: Insufficient vetting of legacy workbooks, overreliance on untrusted third-party data feeds, manual verification driven by assumed inaccuracies, absence of a single source of truth.`;

const EXAMPLE_20_DSL = {
  version: 1, kind: "slide",
  pins: [
    { name: "overview",               type: "any",  label: "High-level problem overview", required: true  },
    { name: "subcategory_summaries",  type: "any",  label: "Per-subcategory synthesis — one section per problem area", required: true  },
    { name: "evidence_table",         type: "any",  label: "Evidence datatable — one quote per record tagged by Category", required: true  },
    { name: "evidence_view",          type: "view", label: "Evidence heatmap view — items tagged by Category in heatmap mode", required: true  },
    { name: "impact_overview",        type: "any",  label: "Impact overview — summary and verbatim impact statements", required: false },
    { name: "themes",                 type: "any",  label: "Theme clusters — pre-synthesised groups of evidence by topic", required: false },
    { name: "theme_spread",           type: "view", label: "Theme spread heatmap — counts of evidence items by theme", required: false }
  ],
  chrome: {
    header: {
      band_color: "#1C2D4A",
      label: "DATA & REPORTING · CHALLENGE ANALYSIS",
      supplier_name: { role: "llm_gen", key: "supplier_name", pins: ["overview"],
                       prompt: "The title of this challenge area exactly as it appears in the source (the '# Title' value). Short phrase, no punctuation." },
    },
    footer: {
      band_color: "#1C2D4A",
      left: "Sense Platform · Discovery research · AI-assisted synthesis",
      right: "Data & Reporting · Challenge analysis"
    }
  },
  bands: [
    // Band 1 (28%): overview text (wider) + operational impact stats
    { id: "header_band", proportion: 0.28, columns: [
      { id: "overview_col", proportion: 0.42, header: "Problem overview", blocks: [
        { type: "text_block", id: "overview_block",
          accent_bar: { color: "#2C4A6E", width: 3 },
          background: "#EEF2F7",
          slots: [
            { role: "llm_gen", key: "headline", max_words: 10, style: "italic_serif", cite: true,
              pins: ["overview"],
              prompt: "8-10 word headline capturing the core drag or pain from the source. Plain text only — no asterisks, no markdown, no quotes." },
            { role: "llm_gen", key: "body", max_words: 50, cite: true,
              pins: ["overview"],
              prompt: "2-3 sentences: describe the problem space, the manual workarounds teams rely on, and the downstream cost. Concrete and grounded in the source." }
          ]
        }
      ]},
      { id: "impact_col", proportion: 0.58, header: "Operational impact", blocks: [
        { type: "tile_row", id: "impact_tiles", cols: 4,
          pins: ["impact_overview"],
          top_border: { color: "#2C4A6E", width: 3 },
          tile_background: "#EEF2F7",
          null_fallback: { display: "—", color: "#999" },
          tiles: [
            { style: "callout", text: { extract: "callout_1", cite: true,
                prompt: "Most significant business impact as a single declarative sentence, 12-16 words. Ground it in the source content and quotes. No asterisks, no markdown." } },
            { style: "callout", text: { extract: "callout_2", cite: true,
                prompt: "Second distinct business impact — 12-16 word declarative sentence. Different theme from callout_1. No asterisks, no markdown." } },
            { style: "callout", text: { extract: "callout_3", cite: true,
                prompt: "Third distinct business impact — 12-16 word declarative sentence. Different theme from callout_1 and callout_2. No asterisks, no markdown." } },
            { style: "callout", text: { extract: "callout_4", cite: true,
                prompt: "Fourth distinct business impact — 12-16 word declarative sentence. Different theme from callout_1, 2, and 3. No asterisks, no markdown." } }
          ]
        }
      ]}
    ]},

    // Hidden data band (0.1%): holds evidence_heatmap solely for Phase 1b
    // crystallisation. Rendered at ~1px so it's invisible. The data it produces
    // is consumed by subcategory_cards via heat_tag.source_block at render time.
    { id: "heatmap_data_band", proportion: 0.001, columns: [
      { id: "heatmap_data_col", proportion: 1, blocks: [
        { type: "chart", kind: "heatmap", id: "evidence_heatmap",
          pins: ["evidence_view"],
          heat_color: "#2C4A6E",
          count_field: "Category"
        }
      ]}
    ]},

    // Band 3 (71.9%): problem areas (35%) + verbatim quotes (40%) + themes (25%)
    { id: "detail_band", proportion: 0.719, columns: [
      { id: "categories_col", proportion: 0.35, header: "Problem areas", blocks: [
        { type: "bullet_list", id: "subcategory_cards", item_limit: 5,
          pins: ["subcategory_summaries"], evidence_in_tooltip: true,
          card_background: "#EEF2F7",
          priority_hint: "Return one item per subcategory found in the source, ordered by evidence count (most-evidenced first). item_limit caps the list.",
          heat_tag: {
            source_block: "evidence_heatmap",
            heat_color: "#2C4A6E",
            show_count: false,
            legend_label: "Evidence density"
          },
          item_template: {
            tag:      { role: "llm_gen", key: "tag",
                        prompt: "Exact category title from the source (the '# Title' value). Must match the title exactly — used to join with heatmap counts." },
            headline: { role: "llm_gen", key: "headline", max_words: 25,
                        prompt: "Format: '**<exact category title>.** <15-20 word core problem description>'." },
            evidence: { role: "llm_gen", key: "evidence", max_words: 30,
                        prompt: "Key underpinning or root cause from the source Underpinning section. Shown on hover as the citation." }
          }
        }
      ]},
      { id: "quotes_col", proportion: 0.40, header: "Voice of the business", blocks: [
        { type: "bullet_list", id: "evidence_quotes", item_limit: 6,
          pins: ["evidence_table"], evidence_in_tooltip: false,
          priority_hint: "Return 6 of the most vivid verbatim quotes, spread across categories — at least 2 from the highest-count category, others well-distributed.",
          item_template: {
            tag:      { role: "llm_gen", key: "tag",
                        prompt: "Exact Category field value from the source record." },
            headline: { role: "llm_gen", key: "headline", max_words: 30,
                        prompt: "The verbatim quote exactly as it appears in the Quote field, including quotation marks." }
          },
          tag_styles: {
            SYSINT:  { bg: "#D8E2EF", text: "#2C4A6E" },
            MANUAL:  { bg: "#EFE0D8", text: "#8A4A2A" },
            REPORT:  { bg: "#E8E2D0", text: "#5A6F62" },
            SILOS:   { bg: "#E8D8EF", text: "#5A2A7A" },
            QUALITY: { bg: "#EFE8D8", text: "#7A5020" }
          }
        }
      ]},
      { id: "themes_col", proportion: 0.25, header: "Problems by theme", blocks: [
        { type: "chart", kind: "coverage", id: "theme_pie",
          pins: ["theme_spread"],
          sort: "label",
          palette: { colors: ["#2C4A6E", "#4A6E8E", "#6E8EAE", "#8EAECC", "#AECCE8"] }
        }
      ]}
    ]}
  ]
};

// Source pins for LLM crystallize mode. In production (pod) evidence_view is
// a real view primitive in heatmap mode — the backend transforms it to a
// datatable via getSegemntDefinitions (Phase 1b path). On the test page we
// supply the raw evidence array so the count_field fallback in Phase 1b still
// produces heatmap cells without requiring a real view primitive.
// Pre-built heatmap datatable matching DR_EVIDENCE_TABLE category counts.
// In production evidence_view is a real view primitive; on the test page we
// supply the datatable directly so Phase 1b takes the __view_heatmap path.
const DR_EVIDENCE_VIEW_HEATMAP = {
  __view_heatmap: true,
  columns: ["System Integration", "Manual Processes", "Reporting Inflexibility", "Data Silos", "Data Quality"],
  rows: [],
  cells_2d: [
    { col: "System Integration",    count: 9 },
    { col: "Manual Processes",      count: 8 },
    { col: "Reporting Inflexibility", count: 6 },
    { col: "Data Silos",            count: 4 },
    { col: "Data Quality",          count: 3 }
  ]
};

const DR_THEMES = [
  [
    {
      "heading": "Data and analytics",
      "ids": [
        "6a33b4c27563f71125fa0d08","6a33b4c57563f71125fa0d26","6a33b53a146c27bc4d23fb8d",
        "6a33b53d7563f71125fa0deb","6a33b53f7563f71125fa0e13","6a33b5587563f71125fa0f12",
        "6a33b55c7563f71125fa0f30","6a33b6f17563f71125fa1215","6a33b6f47563f71125fa1238",
        "6a33b6f7146c27bc4d23fe1e","6a33b6fe7563f71125fa12d7","6a33b7007563f71125fa12fb",
        "6a33b71b146c27bc4d240030","6a33b7227563f71125fa14e3","6a33b73a146c27bc4d240197",
        "6a33b7f27563f71125fa1784","6a33b73b7563f71125fa1579","6a33b7ae146c27bc4d24025b",
        "6a33b7db7563f71125fa16cc","6a33b7e17563f71125fa1702","6a33b76b7563f71125fa15aa",
        "6a33b8027563f71125fa1808","6a33bd04146c27bc4d2405cb","6a33bd09146c27bc4d2405f3"
      ],
      "quote": [
        "Multiple teams perform overlapping analytics and data work (investment quant/AI, portfolio analytics, CTO/tech, investor relations analytics).",
        "Performance attribution work is being developed for PM use, but Investor Relations also needs the same standardized story and data for investor conversations.",
        "The front arena system feeds some reporting (including Tableau visuals) but does not incorporate key liquidity views, requiring manual additions.",
        "The firm’s data architecture is described as “quite poor,” leading to inflexible, static reporting.",
        "Attribution and exposure reporting is described as static (e.g., top 10 / bottom 10) and not user-configurable.",
        "Valuation/reporting outputs are described as stagnant point-in-time reports rather than flexible tools that anticipate the next layers of analysis.",
        "Users cannot easily view quarter-to-date movers or filter by sector (e.g., software) without asking someone to run it.",
        "the team cannot self-serve data quickly. They must request data, wait for it to return, review it, and request again to iterate.",
        "Portfolio monitoring relied on daily Excel reports, making it slow and effortful to answer basic questions like exposure by fund and sector.",
        "Key operational signals are scattered across emails, portal pages, and reports. Without a consolidated operations dashboard, leadership must hunt for information and manually prioritize risks each morning.",
        "Previously there was not a true portfolio-level view for monitoring and decision-making.",
        "users struggle because they must remember which columns exist and cannot easily find them.",
        "When producing investor-facing materials, there is uncertainty about whether Tableau outputs are the correct source of truth.",
        "The data analytics team produces weekly outputs/emails for the investment team that appear unused.",
        "Key analysis work is slowed because required data is scattered across systems and history is not readily available.",
        "Portfolio and reporting data is spread across the portal, Front Arena, SQL databases, and a risk data warehouse.",
        "Treasury distributes many reports (e.g., liquidity), but key stakeholders may not review them consistently.",
        "To complete investor reporting and questionnaires, IR must request portfolio data from Portfolio Analytics and Risk teams because she lacks access to underlying data.",
        "The reporting package does not clearly state how much cash is available to trade, which is a primary input for traders.",
        "The portal provides limited, fixed pivot-style views that are only “mildly helpful,” but cannot support ad hoc slicing of exposures.",
        "The internal portal showing positions refreshes only overnight.",
        "This limits self-serve and scalability, keeping the team reliant on ad hoc attribution runs and manual effort.",
        "Teams frequently need to reference fund documentation (LPAs/PPMs) for terms like expense provisions and overhead treatment, but there is no seamless, centralized, queryable library.",
        "The answers exist in statements/portals but are not self-service for requesters."
      ],
      "content": "Teams duplicate analytics work across investment quant/AI, portfolio analytics, CTO/tech, and investor relations, leading to inefficiency. Performance attribution is under development for portfolio managers, but investor relations lack the standardized data narrative they need. The front arena system produces Tableau visuals yet omits vital liquidity perspectives, requiring manual augmentations. Data architecture is inadequate, resulting in rigid, static reporting and non-configurable attribution and exposure outputs. Users depend on nightly portals and Excel-based daily monitors to answer basic fund and sector exposure questions. Self-service is constrained by scattered data across portals, Front Arena, SQL, and emails, forcing repeated requests and manual iterations.",
      "type": "markdown formatted string",
      "id_map": {
        "6a33b4c27563f71125fa0d08":[0],"6a33b4c57563f71125fa0d26":[1],"6a33b53a146c27bc4d23fb8d":[2],
        "6a33b53d7563f71125fa0deb":[3],"6a33b53f7563f71125fa0e13":[4],"6a33b5587563f71125fa0f12":[5],
        "6a33b55c7563f71125fa0f30":[6],"6a33b6f17563f71125fa1215":[7],"6a33b6f47563f71125fa1238":[8],
        "6a33b6f7146c27bc4d23fe1e":[9],"6a33b6fe7563f71125fa12d7":[10],"6a33b7007563f71125fa12fb":[11],
        "6a33b71b146c27bc4d240030":[12],"6a33b7227563f71125fa14e3":[13],"6a33b73a146c27bc4d240197":[14],
        "6a33b7f27563f71125fa1784":[15],"6a33b73b7563f71125fa1579":[16],"6a33b7ae146c27bc4d24025b":[17],
        "6a33b7db7563f71125fa16cc":[18],"6a33b7e17563f71125fa1702":[19],"6a33b76b7563f71125fa15aa":[20],
        "6a33b8027563f71125fa1808":[21],"6a33bd04146c27bc4d2405cb":[22],"6a33bd09146c27bc4d2405f3":[23]
      }
    },
    {
      "heading": "AI and automation",
      "ids": [
        "6a33b6f27563f71125fa121f","6a33b6f8146c27bc4d23fe2a","6a33b6ff146c27bc4d23fea8",
        "6a33b70e7563f71125fa13f9","6a33b7367563f71125fa154f","6a33b72c146c27bc4d240122",
        "6a33b7bb146c27bc4d240300","6a33b8047563f71125fa181c","6a33b8067563f71125fa1826",
        "6a33b8027563f71125fa1808"
      ],
      "quote": [
        "Automating investor letter commentary depends on high-quality portfolio analytics data. For multi-asset credit, AI could generate most commentary only if the underlying data is good; otherwise outputs become “noise.”",
        "CLO management relies heavily on manual workflows and email, with insufficient quality portfolio information for the PM. Analysts spend significant time on manual model assembly and copy-paste tasks, limiting coverage across hundreds of names.",
        "Without better automated screening and data integration, sourcing and research breadth are constrained.",
        "The document set is “too raw” to reliably support question-answering directly from PDFs, because analyst insight requires synthesized context across company and sector.",
        "He automated and standardized the liquidity workflow (portalized and reduced manual typing), reducing the prior debugging burden.",
        "They mitigated scaling pain by shifting processing from monolithic “run all portfolios together” reports to parallel runs, which reduced runtimes and avoided a full re-architecture for a period.",
        "Integrate Claude with the risk data warehouse (with sufficient accuracy controls) so IR can query fund metrics on demand.",
        "Using the Claude Excel add-in can be helpful, but it is slow and requires constant attention.",
        "AI assistance in Excel can produce incorrect results, requiring the user to know the correct answer and implement controls to validate outputs.",
        "Delays automation and scalability improvements, perpetuating manual reporting effort and slowing data delivery."
      ],
      "content": "Efforts to automate investor letter commentary are hindered by unreliable underlying portfolio analytics data, as poor inputs yield AI-generated noise rather than value. CLO management endures manual workflows and email-driven processes, with analysts assembling models and copy-pasting data for hundreds of names. Document sets remain too raw for direct AI question-answering, lacking synthesized context across companies and sectors. Some automated-liquidity workflows have reduced typing and debugging burdens, and parallel report runs have mitigated scalability issues temporarily. However, tools like the Claude Excel add-in are slow, demand constant oversight, and can produce errors, delaying broader automation and perpetuating manual reporting burdens.",
      "type": "markdown formatted string",
      "id_map": {
        "6a33b6f27563f71125fa121f":[0],"6a33b6f8146c27bc4d23fe2a":[1],"6a33b6ff146c27bc4d23fea8":[2],
        "6a33b70e7563f71125fa13f9":[3],"6a33b7367563f71125fa154f":[4],"6a33b72c146c27bc4d240122":[5],
        "6a33b7bb146c27bc4d240300":[6],"6a33b8047563f71125fa181c":[7],"6a33b8067563f71125fa1826":[8],
        "6a33b8027563f71125fa1808":[9]
      }
    },
    {
      "heading": "Vendors",
      "ids": [
        "6a33b6ed7563f71125fa11f7","6a33b6ee7563f71125fa1201","6a33b6ef7563f71125fa120b",
        "6a33b7177563f71125fa1486","6a33b73a146c27bc4d240197","6a33b76b7563f71125fa15aa"
      ],
      "quote": [
        "Prospecting relies on daily emails from market intelligence vendors, but evaluating relevance requires manual click-throughs and review of paywalled materials.",
        "The CRM, placement-agent updates, and market intelligence feeds are fragmented across tools and formats.",
        "While they can later pull document engagement statistics from the data room, they are not automatically notified when investors engage.",
        "Front is a powerful real-time system but is clunky, slow, and sometimes fails to load in Europe, potentially due to latency with US-based IT.",
        "Related. - US Bank Middle Office (3rd party vendor). - Intex (called “Intact” in transcript) (3rd party data provider). - Valitana (3rd party service vendor).",
        "Fixes underlying data early (US Bank side) and ensures corrected risk reports reach traders, but must tolerate incorrect portal display until the next nightly refresh and respond to multiple people flagging the same visible issue."
      ],
      "content": "Daily prospecting depends on market intelligence vendors’ emails, yet manual click-throughs and paywalled content reviews slow evaluation. Placement-agent updates, CRM records, and vendor feeds lack integration, leaving insights scattered across tools and formats. Although document engagement metrics can be extracted from the data room, the team receives no real-time investor notifications. Front, a real-time communication platform, underperforms in Europe due to latency and failures. Core third-party partners include US Bank Middle Office, Intex for data provision, and Valitana. Even when US Bank corrects underlying data and issues improved risk reports, traders face stale portal displays until the next nightly refresh.",
      "type": "markdown formatted string",
      "id_map": {
        "6a33b6ed7563f71125fa11f7":[0],"6a33b6ee7563f71125fa1201":[1],"6a33b6ef7563f71125fa120b":[2],
        "6a33b7177563f71125fa1486":[3],"6a33b73a146c27bc4d240197":[4],"6a33b76b7563f71125fa15aa":[5]
      }
    }
  ]
];

// Theme counts derived from DR_THEMES: Data and analytics (24 ids),
// AI and automation (10 ids), Vendors (6 ids).
const DR_THEME_SPREAD_HEATMAP = {
  __view_heatmap: true,
  columns: ["Data and analytics", "AI and automation", "Vendors", "Process & governance"],
  rows: [],
  cells_2d: [
    { col: "Data and analytics", count: 24 },
    { col: "AI and automation",  count: 10 },
    { col: "Vendors",            count: 6  }
  ],
  col_totals: { "Data and analytics": 24, "AI and automation": 10, "Vendors": 6 },
  grand_total: 30
};

const EXAMPLE_20_SOURCE_PINS = {
  overview:              DR_OVERVIEW_TEXT,
  subcategory_summaries: DR_SUBCATEGORY_SUMMARIES,
  evidence_table:        DR_EVIDENCE_TABLE,
  "evidence-view":       DR_EVIDENCE_VIEW_HEATMAP,
  impact_overview:       DR_IMPACT_OVERVIEW,
  themes:                DR_THEMES,
  "theme-spread":        DR_THEME_SPREAD_HEATMAP
};

// ---------------------------------------------------------------------------
// Example 21: Problem detail — example card (left) + evidence instances (right)
// ---------------------------------------------------------------------------

const EXAMPLE_21_DSL = {
  version: 1, kind: "slide", gender_neutral: true,
  pins: [
    { name: "title",     type: "string", label: "Secondary header line at the top of the chrome (title text + marker)",                                                required: true },
    { name: "example",   type: "any", label: "Structured problem example with Title, Overview, Impact, Suggestion and Accommodations sections", required: true },
    { name: "instances", type: "any", label: "Array of evidence instances, each with Details and Quote sections",                               required: true }
  ],
  chrome: {
    header: {
      band_color: "#1C2D4A",
      supplier_name: { role: "llm_gen", key: "supplier_name", pins: ["example"],
                       prompt: "The title of this problem exactly as stated in the Title section. Short phrase, no punctuation." }
    },
    secondary_header: { role: "pin_text", key: "title", pins: ["title"], suffix: " -#" },
    footer: {
      band_color: "#1C2D4A",
      left: "Sense Platform · Discovery research · AI-assisted synthesis",
      right: "Problem analysis"
    }
  },
  bands: [
    { id: "main_band", proportion: 1.0, columns: [

      // ── Left column: generated content ────────────────────────────────
      { id: "content_col", proportion: 0.50, header: null, blocks: [
        { type: "text_block", id: "problem_card",
          margin_top: 16,
          label: "EXAMPLE PROBLEM",
          background: "#EBF0F8",
          accent_bar: { color: "#1C2D4A", width: 5 },
          pins: ["example"],
          slots: [
            { role: "llm_gen", key: "title",
              style: "bold",
              max_words: 14,
              prompt: "Extract the Title section content verbatim." },
            { role: "llm_gen", key: "overview",
              max_words: 80,
              prompt: "Extract the Overview section content verbatim." },
            { role: "llm_gen", key: "impact",
              max_words: 45,
              prompt: "Extract the Impact section content. Prefix the text with '**Impact:** ' (markdown bold)." },
            { role: "llm_gen", key: "suggestion",
              style: "small",
              max_words: 40,
              prompt: "Extract the Suggestion section content. If 'No relevant data', omit entirely (return empty string). Otherwise prefix with '**Suggestion:** ' (markdown bold)." },
            { role: "llm_gen", key: "accommodations",
              style: "small",
              max_words: 50,
              prompt: "Extract the Accommodations section content. Prefix with '**Accommodations today:** ' (markdown bold)." }
          ]
        },
        { type: "text_block", id: "instances_summary",
          pins: ["instances"],
          margin_top: 16,
          label: "BROADER INSTANCES OF CHALLENGE",
          slots: [
            { role: "llm_gen", key: "synthesis",
              style: "italic",
              max_words: 55,
              prompt: "Write 2–3 sentences describing the different ways this problem manifests across the evidence instances. Focus on the variety of contexts and patterns." }
          ]
        },
        { type: "bullet_list", id: "quotes_list",
          pins: ["instances"],
          item_limit: 6,
          columns: 2,
          dot_color: "#2C4A6E",
          card_background: "#F5F8FC",
          evidence_in_tooltip: true,
          item_template: {
            headline: { role: "llm_gen", key: "quote", max_words: 35,
                        prompt: "A key verbatim or near-verbatim quote from this instance that best illustrates the problem. Use italics (_text_) for direct quotes." }
          }
        }
      ]},

      // ── Right column: printout template ───────────────────────────────
      { id: "template_col", proportion: 0.50, header: null, sizing: "proportional", blocks: [
        { type: "flow", id: "restate_row", direction: "horizontal", proportion: 2, gap: 12,
          children: [
            { type: "write_box", id: "restate_problem",  label: "Problem in your own words" },
            { type: "write_box", id: "restate_impact",   label: "Impact of problem in your own words" }
          ]
        },
        { type: "write_box", id: "list_upstream",    label: "List upstream / downstream", proportion: 3 },
        { type: "flow", id: "theme_workflow_row", direction: "horizontal", proportion: 2, gap: 12,
          children: [
            { type: "write_box", id: "key_themes",        label: "Key Theme(s)" },
            { type: "write_box", id: "related_workflows", label: "Related workflow area(s)" }
          ]
        },
        { type: "criteria_list", id: "scoring_criteria", proportion: 3,
          guidance: "(Rate from 1 to 6, 6 is “most impact”)",
          items: [
            { badge: "C1", label: "Company impact",             badge_color: "#4CAF50" },
            { badge: "C2", label: "Client and investor impact", badge_color: "#4CAF50" },
            { badge: "C3", label: "Resource and time impact",   badge_color: "#4CAF50" },
            { badge: "C4", label: "Impact to risk, control and quality",  badge_color: "#4CAF50" },
            { badge: "C5", label: "Impact to cross-functions",  badge_color: "#4CAF50" },
            { label: "Average", no_badge: true }
          ]
        }
      ]}
    ]}
  ]
};

const EXAMPLE_21_EXAMPLE_PIN = [
  { heading: "Title",
    content: "Manual, ad hoc investor information requests overload IR." },
  { heading: "Overview",
    content: "Investors and prospects frequently ask bespoke questions requiring manual coordination and analysis (for example, distributions expectations or underlying portfolio characteristics). These requests pull senior IR away from proactive fundraising, increase turnaround pressure, and create operational friction across investment, ops, and structured credit teams, slowing responsiveness and reducing scalability during active fundraising." },
  { heading: "Impact",
    content: "Introduces delays and operational burden; limits prospecting capacity and reduces efficiency of investor servicing and fundraising execution." },
  { heading: "Suggestion",
    content: "No relevant data." },
  { heading: "Accommodations",
    content: "She coordinates directly with PMs and internal teams to generate bespoke answers and sometimes reuses other data sets to respond. This is labor-intensive, depends on cross-team availability, and becomes more difficult during travel or active fundraising periods." }
];

const EXAMPLE_21_INSTANCES_PIN = [
  [
    { heading: "Details",
      content: "Senior IR dedicates time manually answering investor DDQs via Blueflame, coordinating with ops teams to compile bespoke data. Each new question requires re-running Excel extracts and reproducing static reports, consuming analysts' bandwidth and delaying proactive fundraising." },
    { heading: "Quote",
      content: "But there's like a whole large of other stuff where it's just like responding to DDQs… next time somebody asks something that's slightly different, you gotta do it again." }
  ],
  [
    { heading: "Details",
      content: "An investor in ACO9 sent a bespoke diligence questionnaire demanding discrete metrics and narrative. The IR team manually extracted data from Tableau views, client account statements, and internal notes, coordinating across portfolio analytics and operations. This ad-hoc process consumes days of senior time." },
    { heading: "Quote",
      content: "This is a questionnaire that we got from one of our LPs in ACO9. We're really looking at our Tableau view, their client account statement as well as commentary from our notes in meetings to answer these questions. Fairly time consuming." }
  ],
  [
    { heading: "Details",
      content: "A senior IR manager devotes hours coordinating efforts to respond to an existing investor's bespoke request for portfolio details across 500 underlying loans per fund, requiring manual data retrieval across investment, ops, and structured credit teams." },
    { heading: "Quote",
      content: "For my three investments in these funds I want to understand the characteristics of the underlying loans — this is like 500 loans per fund. It requires coordination with the investment team, with ops, with the structured credit team to answer those questions." }
  ],
  [
    { heading: "Details",
      content: "Last-minute investor emails about missing distributions force IR to manually trace cash movements across spreadsheets and banking reports, diverting senior teams into urgent coordination with operations and trustees." },
    { heading: "Quote",
      content: "If an investor pings me, says hey, we're missing your money, people will say sorry, miss the cutoff. It's probably already in your account. You'll see it on your statement tomorrow morning versus I don't know." }
  ],
  [
    { heading: "Details",
      content: "IR spends time on unstructured investor discussions and must manually track day-to-day calls and questions. Responding to bespoke due diligence questionnaires in Excel involves manually pulling past answers and cross-checking updates." },
    { heading: "Quote",
      content: "From a CL desk, we also complete a lot of due diligence questionnaires from various investors — instead of a platform catch-up they have these standard questionnaires. So we also have a lot of day-to-day conversation with investors and we'd like to get better organised on that." }
  ],
  [
    { heading: "Details",
      content: "Custom investor data requests often arrive as bespoke Excel templates requiring manual completion. IR must coordinate with the portfolio analytics team to populate look-through fields and reconcile multiple request variations per investor." },
    { heading: "Quote",
      content: "I have two investors in Asia. They both ask for look-through information for the portfolio, but I still need to request data from our portfolio analytics team and put in different parts into both of these templates." }
  ],
  [
    { heading: "Details",
      content: "Investors frequently request tailored distribution projections, compelling IR to manually gather and reconcile cash flow data from portfolio analytics, risk, and accounting teams during fundraising periods." },
    { heading: "Quote",
      content: "A common request we'll get — almost like a routine boilerplate — is: can you provide some guidance on distributions in the coming quarters." }
  ]
];

const EXAMPLE_22_DSL = {
  version: 1, kind: "slide",
  pins: [
    { name: "title",   type: "any", label: "Slide title (plain text)",                              required: true },
    { name: "sources", type: "any", label: "Sources / citations (array of title/type/url records)", required: true }
  ],
  chrome: {
    header: {
      style: "dark_green_band",
      label: "SOURCES & CITATIONS",
      supplier_name: { role: "pin_text", key: "title", pins: ["title"] }
    },
    footer: {
      style: "dark_green_band",
      left: "Sense Platform · Auto-generated bibliography",
      right: "Click any title to open the source"
    }
  },
  bands: [
    { id: "main_band", proportion: 1.0, columns: [
      { id: "cites_col", proportion: 1.0, header: null, blocks: [
        { type: "bullet_list", id: "citations",
          item_style: "event",
          pins: ["sources"],
          item_limit: 60,
          columns: 3,
          density: "compact",
          evidence_in_tooltip: false,
          priority_hint:
            "Return AT MOST 60 items (3 columns × 20 per column). Group items by 'type': emit Annual Report records first, then Sustainability Report, then Article, then Web Resource. Within each group, preserve the source order. If the source has more than 60 records, KEEP the rarer types in full and only truncate the most common type (typically Web Resource) to hit the cap.",
          item_template: {
            tag: {
              role: "llm_gen", key: "tag",
              prompt: "The 'type' field, verbatim. Must be one of the configured tag styles."
            },
            headline: {
              role: "llm_gen", key: "headline", max_words: 18,
              prompt:
                "The document title. If the 'title' field begins with 'Download from <url>', derive a readable title from the final URL filename (strip path, extension, replace %20 with spaces, title-case). Otherwise use the 'title' verbatim."
            },
            evidence: {
              role: "llm_gen", key: "evidence",
              prompt: "The 'url' field verbatim — copy the URL character-for-character with no modification. Do NOT add trailing punctuation (no period, comma, or any other character after the final URL character)."
            }
          },
          tag_styles: {
            "Annual Report":         { bg: "#EFE0D8", text: "#8A4A2A" },
            "Sustainability Report": { bg: "#DCE7DF", text: "#1F3A2E" },
            "Web Resource":          { bg: "#E8E2D0", text: "#5A6F62" },
            "Article":               { bg: "#D8E2EF", text: "#2C4A6E" }
          }
        }
      ]}
    ]}
  ]
};

const EXAMPLE_22_SOURCE_PINS = {
  title: "Şişecam",
  sources: [
    "title: ŞİŞECAM CELEBRATES THE 90 YEARS OF PROGRESS\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/sisecam-celebrates-the-90-years-of-progress",
    "title: SISECAM'S FIRST-HALF NET SALES REACHED 86 BILLION TRY\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/sisecam-s-first-half-net-sales-reached-86-billion-try",
    "title: Sisecam's U.S. Move to Bring Global Leadership\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/sisecams-us-move-to-bring-global-leadership",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Webcast%20Transcripts/transcript-on-merger-decision.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Webcast%20Transcripts/transcript-on-merger-decision.pdf",
    "title: Strategic Collaboration Between Şişecam and BGC\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/strategic-collaboration-between-sisecam-and-bgc",
    "title: Şişecam Completes Its Largest Flat Glass Facility Investment\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/sisecam-completes-its-largest-flat-glass-facility-investment",
    "title: HomePage\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en",
    "title: Şişecam Completes Coated Glass Line Investment at Its Facility in Bulgaria\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/sisecam-completes-coated-glass-line-investment-at-its-facility-in-bulgaria",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Mergers/Merger%20Announcement.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Mergers/Merger%20Announcement.pdf",
    "title: Şişecam participates in Drinktec 2025 - glassonweb.com\n\ntype: Article\n\nurl: http://www.glassonweb.com/news/sisecam-participates-drinktec-2025",
    "title: Strategic Investment from Şişecam to Provide Sustainability in Raw ...\n\ntype: Article\n\nurl: https://www.cevko.org.tr/en/posts/strategic-investment-from-sisecam-that-will-ensure-sustainability-in-raw-material-supply",
    "title: Five Trends to Watch in Food Manufacturing Strategy for 2026\n\ntype: Article\n\nurl: https://www.lce.com/news-updates/five-trends-to-watch-in-food-manufacturing-strategy-for-2026/",
    "title: Şişecam participated in Eurasia Packaging Istanbul 2025\n\ntype: Article\n\nurl: https://www.glassonline.com/sisecam-participated-in-eurasia-packaging-2025/",
    "title: IFC Invests in Şişecam to Support Its Innovation and Sustainability ...\n\ntype: Article\n\nurl: https://www.ifc.org/en/pressroom/2025/ifc-invests-in-i-ecam-to-support-its-innovation-and-sustainability-goals",
    "title: Supply Chain recent news | SupplySide Food & Beverage Journal\n\ntype: Article\n\nurl: https://www.supplysidefbj.com/food-beverage-operations/supply-chain",
    "title: Glass Industry Network's Post - LinkedIn\n\ntype: Article\n\nurl: https://www.linkedin.com/posts/glass-industry-network-533182108_%C5%9Fi%C5%9Fecam-participates-in-eurasia-packaging-activity-7385948335691509760-eus5",
    "title: Şişecam Releases Its Annual Sustainability Report - Glass Magazine\n\ntype: Article\n\nurl: https://www.glassmagazine.com/news/2024/sisecam-releases-its-annual-sustainability-report",
    "title: What today's supply chain leaders expect from tomorrow's cold chain\n\ntype: Article\n\nurl: https://www.onelineage.com/news-stories/cold-chain-insights-report-2026",
    "title: Our Key Takeaways from Drinktec 2025 in Munich - gaasch packaging\n\ntype: Article\n\nurl: https://gaaschpack.eu/news/sustainability-in-food-beverage-packaging-our-key-takeaways-from-drinktec-2025-in-munich",
    "title: [PDF] ANNUAL REPORT 2024 TÜRKIYE ŞIŞE VE CAM FABRIKALARI A.Ş.\n\ntype: Article\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/SCAM_FRAE_2024_MTB_uyg_23.pdf",
    "title: 2026 KnowTheChain food and beverage benchmark\n\ntype: Article\n\nurl: https://www.business-humanrights.org/en/from-us/briefings/2026-knowthechain-food-and-beverage-benchmark/",
    "title: Şişecam is to establish its first European glass-packaging facility in ...\n\ntype: Article\n\nurl: https://www.youtube.com/watch?v=ohHn-qYQK4U",
    "title: Şişecam I Glass Packaging I Excelling in sustainability ... - YouTube\n\ntype: Article\n\nurl: https://www.youtube.com/watch?v=Li0qzShBlRY",
    "title: Food, beverage companies lagging on addressing forced labor risks\n\ntype: Article\n\nurl: https://www.esgdive.com/news/food-beverage-companies-lagging-on-addressing-forced-labor-risks-supply-chain-bhrc/812845/",
    "title: Press Release\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases",
    "title: History\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/history",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/sise-2020-presentation.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/sise-2020-presentation.pdf",
    "title: Quality Documents\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/quality-documents",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Webcast%20Transcripts/2020ye-transcript.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Webcast%20Transcripts/2020ye-transcript.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/SCAM_FRAE_2025_UYG_uyg_11.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/SCAM_FRAE_2025_UYG_uyg_11.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/conference-presentation-q1-20-v7.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/conference-presentation-q1-20-v7.pdf",
    "title: Companies\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/our-companies",
    "title: Sisecam Allocated 63.5% of Its R&amp;D Expenditures to Sustainability Projects\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/sisecam-allocated-63-5-of-its-r-d-expenditures-to-sustainability-projects",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/SCAM_FRAE_2024_MTB_uyg_23.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/SCAM_FRAE_2024_MTB_uyg_23.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/US%20Natural%20Soda%20Investment-Webcast%20Prez.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/US%20Natural%20Soda%20Investment-Webcast%20Prez.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Webcast%20Transcripts/Sisecam%20Webcast%20Transcript%201H2025.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Webcast%20Transcripts/Sisecam%20Webcast%20Transcript%201H2025.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2012-Q4.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2012-Q4.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2013-Q3.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2013-Q3.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-IR-Presentation-May-2016.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-IR-Presentation-May-2016.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Mergers/2018%20Activity%20Report.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Mergers/2018%20Activity%20Report.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/Eurobond%20Investor%20Presentation%20-%202024.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/Eurobond%20Investor%20Presentation%20-%202024.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Bulletins/SISE%202020%20Q1%20Results%20-%20YoY%20Comparison.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Bulletins/SISE%202020%20Q1%20Results%20-%20YoY%20Comparison.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2014-Q2.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2014-Q2.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Trakya%20Cam/Presentations%20and%20Reports/Yatirimci%20Sunumlari/11%20Presentations-2012-ENG.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Trakya%20Cam/Presentations%20and%20Reports/Yatirimci%20Sunumlari/11%20Presentations-2012-ENG.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Trakya%20Cam/Presentations%20and%20Reports/Yatirimci%20Sunumlari/Presentation%202019%20Q1.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Trakya%20Cam/Presentations%20and%20Reports/Yatirimci%20Sunumlari/Presentation%202019%20Q1.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/SODA%202Q%202018%20IR%20PRESENTATION.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/SODA%202Q%202018%20IR%20PRESENTATION.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2015-Q3.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-2015-Q3.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/Annual_report_2020.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/Annual_report_2020.pdf",
    "title: TOGG Production Facilities, Gemlik Campus\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/reference-projects/togg-production-facilities-gemlik-campus",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/sisecam-annual-report-2022.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/sisecam-annual-report-2022.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.%20SISECAM%209M%202025%20-%20INVESTOR%20PRESENTATION.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.%20SISECAM%209M%202025%20-%20INVESTOR%20PRESENTATION.pdf",
    "title: Şişecam in the World\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/sisecam-in-the-world",
    "title: Download from https://www.sisecam.com/en/Documents/Sisecam-by-numbers.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/Documents/Sisecam-by-numbers.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-November-2014.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents//Soda%20Sanayi/Presentations%20and%20Reports/Yat%C4%B1r%C4%B1mc%C4%B1%20Sunumlar%C4%B1/Soda-investor-presentation-EN-November-2014.pdf",
    "title: Flat Glass\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/flat-glass",
    "title: Şişecam - annual report search - 2025\n\ntype: Annual Report\n\nurl: https://companiesmarketcap.com/annual-reports/11925.ar.en.2025.pdf",
    "title: Şişecam - annual report search - 2024\n\ntype: Annual Report\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/SCAM_FRAE_2024_MTB_uyg_23.pdf",
    "title: Şişecam - annual report search\n\ntype: Annual Report\n\nurl: https://www.sisecam.com/en/investor-relations/annual-reports",
    "title: Şişecam - sustainability report search - 2024\n\ntype: Sustainability Report\n\nurl: https://www.sisecam.com/en/s-investor-relations/MaterialDisclosures/03.03.2025%202024%20Sustainability%20Compliance%20Report.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/MaterialDisclosures/04.03.2024%202023%20Sustainability%20Compliance%20Report.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/MaterialDisclosures/04.03.2024%202023%20Sustainability%20Compliance%20Report.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/MaterialDisclosures/03.03.2025%202024%20Sustainability%20Compliance%20Report.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/MaterialDisclosures/03.03.2025%202024%20Sustainability%20Compliance%20Report.pdf",
    "title: What Is Sustainability at Şişecam?\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/sustainability",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/SISECAM%20H1%202024%20WEBCAST%20PRESENTATION.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/SISECAM%20H1%202024%20WEBCAST%20PRESENTATION.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/SISECAM%202024%20FY%20INVESTOR%20PRESENTATION.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/SISECAM%202024%20FY%20INVESTOR%20PRESENTATION.pdf",
    "title: Şişecam to Develop the Roof Glass of Circular Car in the ZEvRA Project\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/press-releases/sisecam-to-develop-the-roof-glass-of-circular-car-in-the-zevra-project",
    "title: Download from https://www.sisecam.com/tr/s-investor-relations/Investor%20Relations/Credit%20Ratings/2026%20Moody's%20Credit%20Rating%20Report.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/tr/s-investor-relations/Investor%20Relations/Credit%20Ratings/2026%20Moody's%20Credit%20Rating%20Report.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.SISECAM_2021_12M_Presentation.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.SISECAM_2021_12M_Presentation.pdf",
    "title: Energy\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/energy",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/2021%20Annual%20Report.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Annual%20Reports/2021%20Annual%20Report.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.%20SISECAM%202022%20YE%20Presentation2.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.%20SISECAM%202022%20YE%20Presentation2.pdf",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.%20SISECAM%20H1%202025%20-%20INVESTOR%20PRESENTATION.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/InvestorPresentations/1.%20SISECAM%20H1%202025%20-%20INVESTOR%20PRESENTATION.pdf",
    "title: Corporate Social Responsibility\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/corporate-social-responsibility",
    "title: Download from https://www.sisecam.com/en/s-investor-relations/Documents/Commitees%20and%20Their%20Working%20Principles/Y%C3%B6netim%20Kurulu%20B%C3%BCnyesinde%20Olu%C5%9Fturulan%20Komitelerin%20Kurulu%C5%9F%20ve%20%C3%87al%C4%B1%C5%9Fma%20Esaslar%C4%B1-ENG.pdf\n\ntype: Web Resource\n\nurl: https://www.sisecam.com/en/s-investor-relations/Documents/Commitees%20and%20Their%20Working%20Principles/Y%C3%B6netim%20Kurulu%20B%C3%BCnyesinde%20Olu%C5%9Fturulan%20Komitelerin%20Kurulu%C5%9F%20ve%20%C3%87al%C4%B1%C5%9Fma%20Esaslar%C4%B1-ENG.pdf"
  ]
};

const EXAMPLE_21_SOURCE_PINS = {
  title:     "Manual, ad hoc investor information requests overload IR.",
  example:   EXAMPLE_21_EXAMPLE_PIN,
  instances: EXAMPLE_21_INSTANCES_PIN
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const EXAMPLES = [
  {
    name: "0. Uncrystallized preview",
    dsl: DUMMY_DSL,
    data: null
  },
  {
    name: "1. Financial posture (full)",
    dsl: DUMMY_DSL,
    data: DUMMY_CRYSTALIZED
  },
  {
    name: "2. Text block only",
    dsl: EXAMPLE_2_DSL,
    data: EXAMPLE_2_DATA
  },
  {
    name: "3. Bar chart (sparkline)",
    dsl: EXAMPLE_3_DSL,
    data: EXAMPLE_3_DATA
  },
  {
    name: "4a. Conditional — listed company",
    dsl: EXAMPLE_4_DSL,
    data: EXAMPLE_4_DATA_LISTED
  },
  {
    name: "4b. Conditional — private company",
    dsl: EXAMPLE_4_DSL,
    data: EXAMPLE_4_DATA_PRIVATE
  },
  {
    name: "5. Tile row with null fallbacks",
    dsl: EXAMPLE_5_DSL,
    data: EXAMPLE_5_DATA
  },
  {
    name: "6. Horizontal flow: sidebar + chart",
    dsl: EXAMPLE_6_DSL,
    data: EXAMPLE_6_DATA
  },
  {
    name: "7. Slide 2 — Financial Health (full spec)",
    dsl: EXAMPLE_7_DSL,
    data: EXAMPLE_7_DATA
  },
  {
    name: "8. Slide 1 — At-a-glance dashboard",
    dsl: EXAMPLE_8_DSL,
    data: EXAMPLE_8_DATA
  },
  {
    name: "9. World map — footprint",
    dsl: EXAMPLE_9_DSL,
    data: EXAMPLE_9_DATA
  },
  {
    name: "10. Text sizing test — min_font_scale",
    dsl: EXAMPLE_10_DSL,
    data: EXAMPLE_10_DATA
  },
  {
    name: "11. Ardagh Group — LLM crystallize",
    dsl: EXAMPLE_11_DSL,
    data: null,
    sourcePins: EXAMPLE_11_SOURCE_PINS
  },
  {
    name: "11b. Anchor Glass — LLM crystallize (same DSL)",
    dsl: EXAMPLE_11_DSL,
    data: null,
    sourcePins: ANCHOR_GLASS_SOURCE_PINS
  },
  {
    name: "12. Diageo — Financial health & business activity (LLM)",
    dsl: EXAMPLE_12_DSL,
    data: null,
    sourcePins: DIAGEO_SOURCE_PINS
  },
  {
    name: "13. Diageo — Sustainability commitments & progress (LLM)",
    dsl: EXAMPLE_13_DSL,
    data: null,
    sourcePins: DIAGEO_SOURCE_PINS
  },
  {
    name: "14. Diageo — Operations, footprint & regional focus (LLM)",
    dsl: EXAMPLE_14_DSL,
    data: null,
    sourcePins: DIAGEO_SOURCE_PINS
  },
  {
    name: "15. Blood center — JTBD / ODI interview analysis (LLM)",
    dsl: EXAMPLE_15_DSL,
    data: null,
    sourcePins: BLOOD_CENTER_SOURCE_PINS
  },
  {
    name: "16. Diageo — Written Profile (pre-canned document)",
    dsl: EXAMPLE_16_DOC_DSL,
    data: EXAMPLE_16_DOC_DATA
  },
  {
    name: "17. Diageo — Written Profile (LLM crystallize document)",
    dsl: EXAMPLE_17_DOC_DSL,
    data: null,
    sourcePins: DIAGEO_SOURCE_PINS
  },
  {
    name: "18. JTBD / ODI Analysis Report — single interviewee (LLM crystallize document)",
    dsl: EXAMPLE_18_DOC_DSL,
    data: null,
    sourcePins: ASSET_MGMT_SOURCE_PINS
  },
  {
    name: "19. Diageo — Blank DSL (test the generator from scratch)",
    dsl: {
      version: 1,
      kind: "slide",
      title: "",
      flow: []
    },
    data: null,
    sourcePins: DIAGEO_SOURCE_PINS
  },
  {
    name: "20. Data & Reporting — Challenge analysis (heatmap from datatable)",
    dsl: EXAMPLE_20_DSL,
    data: null,
    sourcePins: EXAMPLE_20_SOURCE_PINS
  },
  {
    name: "21. Problem detail — example card + evidence instances",
    dsl: EXAMPLE_21_DSL,
    data: null,
    sourcePins: EXAMPLE_21_SOURCE_PINS
  },
  {
    name: "22. Sources & citations — grouped by type with clickable links",
    dsl: EXAMPLE_22_DSL,
    data: null,
    sourcePins: EXAMPLE_22_SOURCE_PINS
  }
];
